// src/pages/ChatThread.jsx
import React from 'react';
import { flushSync } from 'react-dom';
import { createPortal } from 'react-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { AnimatePresence, motion } from 'motion/react';
import s from './Chats.module.css';
import Picker from '@emoji-mart/react';

import { ReactComponent as LeaveChatIcon } from '../icons/leave_chat_icon.svg';
import { ReactComponent as QuitMessageActionsIcon } from '../icons/quit_message_actions.svg';
import { ReactComponent as AnswerMessageIcon } from '../icons/answer_message.svg';
import { ReactComponent as CopyMessageIcon } from '../icons/copy_message.svg';
import { ReactComponent as DeleteMessageIcon } from '../icons/delete_message.svg';
import { ReactComponent as ForwardMessageIcon } from '../icons/forward_message.svg';
import { ReactComponent as EditMessageIcon } from '../icons/edit_message.svg';
import { ReactComponent as ConfirmEditIcon } from '../icons/confirm_edit.svg';
import { ReactComponent as SendMessageIcon } from '../icons/send_message_icon.svg';
import { ReactComponent as ReadTicksIcon } from '../icons/read_message_ticks.svg';
import { ReactComponent as SendingTickIcon } from '../icons/sending_message_tick.svg';
import { ReactComponent as SendAttachmentIcon } from '../icons/send_attachment.svg';
import { ReactComponent as MicrophoneIcon } from '../icons/microphone.svg';
import { ReactComponent as SearchIcon } from '../icons/chat_search.svg';
import { ReactComponent as PinMessageIcon } from '../icons/pin_message.svg';
import { ReactComponent as PinnedJumpToChatRightIcon } from '../icons/pinned_jump_to_chat_right.svg';
import { ReactComponent as PinnedJumpToChatLeftIcon } from '../icons/pinned_jump_to_chat_left.svg';
import AudioWaveform from './AudioWaveform';
import { API_URL } from '../config';
import { apiFetch } from '../utils/auth';
import { ALLOWED_MESSAGE_REACTIONS_LIST, isAllowedReaction, preloadReactionImages } from '../utils/messageReactions';
import { ReactionGlyph } from '../utils/ReactionGlyph';
import { EMOJI_CLUSTER_RE, resolveEmojiSpriteStyle, useEmojiSpriteIndex } from '../utils/emojiSpriteIndex';
import { getChatDisplayTitle, isFavoritesChat } from '../utils/chatFavorites';
import { clipboardHtmlToPlainTextWithLinks, insertTextIntoTextarea } from '../utils/clipboardRichText';
import { ChatCounterpartyAvatar, ChatSenderAvatar } from './ChatSidebar';
import { Avatar, Input, Textarea } from './UI';

const REACTION_MINI_HOVER_OPEN_MS = 300;
const REACTION_SPRING_FAST = { type: 'spring', stiffness: 520, damping: 26 };
const REACTION_ROW_TRANSITION = {
  opacity: { duration: 0.14, ease: 'easeOut' },
  height: { duration: 0.2, ease: [0.22, 1, 0.36, 1] },
  marginTop: { duration: 0.2, ease: [0.22, 1, 0.36, 1] },
  y: { duration: 0.18, ease: [0.22, 1, 0.36, 1] },
  scale: { duration: 0.18, ease: [0.22, 1, 0.36, 1] },
};
const EMOJI_MART_GOOGLE_DATA_URL = 'https://cdn.jsdelivr.net/npm/@emoji-mart/data@latest/sets/15/google.json';
const NO_SPRITE_EMOJI = new Set(['📕', '📝', '📊', '🖥️', '📄', '🗂️', '📎']);

let emojiMartDataCache = null;
let emojiMartDataPromise = null;

const loadEmojiMartData = async () => {
  if (emojiMartDataCache) return emojiMartDataCache;
  if (emojiMartDataPromise) return emojiMartDataPromise;

  emojiMartDataPromise = fetch(EMOJI_MART_GOOGLE_DATA_URL)
    .then((res) => {
      if (!res.ok) throw new Error(`emoji mart data load failed: ${res.status}`);
      return res.json();
    })
    .then((json) => {
      emojiMartDataCache = json;
      return json;
    });

  return emojiMartDataPromise;
};

const CHAT_NEAR_BOTTOM_PX = 240;
/** За сколько px до края ленты начинать догрузку */
const CHAT_PRELOAD_EDGE_PX = 520;
const CHAT_HISTORY_CHAIN_GUARD_MS = 650;
const CHAT_SINGLE_IMAGE_FALLBACK_ASPECT = '4 / 3';
const CHAT_SINGLE_VIDEO_FALLBACK_ASPECT = '16 / 9';
const CHAT_SCROLL_ANCHOR_LOCK_MS = 2800;
/** Отступ целевого сообщения от верха ленты при прыжке к закрепу */
const PINNED_SCROLL_TOP_INSET = 72;
const PINNED_SCROLL_OPTS = { align: 'start', topInset: PINNED_SCROLL_TOP_INSET };
/** После смены чата — окно принудительного прижатия к низу (догрузка ленты может занять >420ms). */
const CHAT_OPEN_BOTTOM_CLAMP_MS = 2400;
/** Порог и потолок для расчёта клавиатуры (iOS + Android). */
const KBD_OPEN_MIN_PX = 48;
const KBD_INSET_MAX_RATIO = 0.52;

/**
 * Android с interactive-widget=resizes-content сжимает innerHeight — shrink layout−vv ≈ 0.
 * Сравниваем с baseline (высота при открытии чата) и visualViewport.
 */
function computeKeyboardState(vv, baselineH, isIOS) {
  const layoutH = window.innerHeight;
  const visibleH = vv.height;
  const offsetTop = vv.offsetTop || 0;
  if (!Number.isFinite(layoutH) || !Number.isFinite(visibleH)) {
    return { inset: 0, open: false };
  }
  const base = Number.isFinite(baselineH) && baselineH > 0 ? baselineH : layoutH;
  const shrinkVV = layoutH - visibleH - offsetTop;
  const shrinkLayout = layoutH - visibleH;
  const shrinkFromBaseline = Math.max(base - layoutH, base - visibleH, 0);
  const shrink = Math.max(shrinkVV, shrinkLayout, shrinkFromBaseline, 0);

  if (shrink < KBD_OPEN_MIN_PX) {
    return { inset: 0, open: false };
  }

  if (isIOS) {
    const cap = Math.round(layoutH * KBD_INSET_MAX_RATIO);
    const inset = Math.min(Math.round(shrinkLayout), cap);
    return { inset, open: true };
  }

  const inset = Math.max(0, Math.round(shrink));
  return { inset, open: true };
}

function buildChatSingleImageFrameStyle(maxWidth, dimW, dimH) {
  const hasMediaDims = dimW > 0 && dimH > 0;
  const rawRatio = hasMediaDims ? dimW / dimH : 4 / 3;
  const displayRatio = Math.max(0.56, Math.min(rawRatio, 1.78));
  const isPortrait = hasMediaDims && rawRatio < 0.9;
  const portraitWidth =
    rawRatio < 0.58
      ? `min(${maxWidth}, 204px)`
      : `min(${maxWidth}, 244px)`;
  const base = {
    width: isPortrait ? portraitWidth : maxWidth,
    maxWidth: '100%',
    borderRadius: 8,
    overflow: 'hidden',
    lineHeight: 0,
    background: '#f3f4f6',
  };
  return hasMediaDims
    ? { ...base, aspectRatio: `${displayRatio}` }
    : { ...base, aspectRatio: CHAT_SINGLE_IMAGE_FALLBACK_ASPECT };
}

function buildChatSingleVideoFrameStyle(maxWidth, dimW, dimH) {
  const hasMediaDims = dimW > 0 && dimH > 0;
  const base = {
    width: '100%',
    maxWidth,
    background: '#1f2937',
    borderRadius: 8,
    overflow: 'hidden',
  };
  return hasMediaDims
    ? { ...base, aspectRatio: `${dimW} / ${dimH}` }
    : {
      ...base,
      aspectRatio: CHAT_SINGLE_VIDEO_FALLBACK_ASPECT,
      minHeight: 180,
    };
}

function getChatAttachmentDims(att, viewUrl, knownImageDimensionsByUrl = {}) {
  const cachedDims = viewUrl ? knownImageDimensionsByUrl[viewUrl] : null;
  const dimW = Number(att?.width) || Number(cachedDims?.w) || 0;
  const dimH = Number(att?.height) || Number(cachedDims?.h) || 0;
  return {
    dimW,
    dimH,
    hasMediaDims: dimW > 0 && dimH > 0,
  };
}

function getMessagesWindowAround(messages, targetId, radius = 22) {
  if (!Array.isArray(messages) || !messages.length || targetId == null) return messages || [];
  const idx = messages.findIndex((m) => String(m?.id) === String(targetId));
  if (idx < 0) return messages;
  const start = Math.max(0, idx - radius);
  const end = Math.min(messages.length, idx + radius + 1);
  return messages.slice(start, end);
}

function getChatMediaPreviewWidth({
  isGroup = false,
  isMine = false,
  attachments = [],
  viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0,
} = {}) {
  const imageAttachments = Array.isArray(attachments)
    ? attachments.filter((att) => att?.kind === 'image')
    : [];
  const hasImageAlbum = imageAttachments.length > 1
    && Array.isArray(attachments)
    && imageAttachments.length === attachments.length;
  const isDenseImageAlbum = hasImageAlbum && imageAttachments.length >= 3;
  const isMobileViewport = viewportWidth > 0 && viewportWidth <= 767;
  const isMidMobileViewport = viewportWidth > 400 && viewportWidth <= 420;
  const isNarrowMobileViewport = viewportWidth > 0 && viewportWidth <= 400;
  const isVeryNarrowMobileViewport = viewportWidth > 0 && viewportWidth <= 360;

  const mediaWidthProfile =
    (isGroup && !isMine)
      ? {
        mobile: 'min(248px, max(136px, calc(100vw - 196px)))',
        mid: 'min(232px, max(132px, calc(100vw - 206px)))',
        narrow: 'min(220px, max(128px, calc(100vw - 210px)))',
        veryNarrow: 'min(196px, max(120px, calc(100vw - 220px)))',
      }
      : (!isGroup)
        ? {
          mobile: 'min(270px, max(150px, calc(100vw - 118px)))',
          mid: 'min(244px, max(140px, calc(100vw - 146px)))',
          narrow: 'min(232px, max(138px, calc(100vw - 136px)))',
          veryNarrow: 'min(210px, max(128px, calc(100vw - 148px)))',
        }
        : (isMine && isDenseImageAlbum)
          ? {
            mobile: 'min(252px, max(142px, calc(100vw - 132px)))',
            mid: 'min(236px, max(136px, calc(100vw - 146px)))',
            narrow: 'min(226px, max(134px, calc(100vw - 148px)))',
            veryNarrow: 'min(204px, max(124px, calc(100vw - 160px)))',
          }
          : null;

  if (!mediaWidthProfile) return 'min(320px, 74vw)';
  if (isVeryNarrowMobileViewport) return mediaWidthProfile.veryNarrow;
  if (isNarrowMobileViewport) return mediaWidthProfile.narrow;
  if (isMidMobileViewport && mediaWidthProfile.mid) return mediaWidthProfile.mid;
  if (isMobileViewport) return mediaWidthProfile.mobile;
  return 'min(340px, 78vw)';
}

function ruPinnedScreenTitle(n) {
  const x = Math.abs(Number(n)) || 0;
  if (x === 0) return 'Закреплённые сообщения';
  const mod100 = x % 100;
  const mod10 = x % 10;
  if (mod10 === 1 && mod100 !== 11) return `${x} закреплённое сообщение`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${x} закреплённых сообщения`;
  return `${x} закреплённых сообщений`;
}

function ruUnpinAllCta(n) {
  const x = Math.abs(Number(n)) || 0;
  const mod100 = x % 100;
  const mod10 = x % 10;
  let w = 'СООБЩЕНИЙ';
  if (mod10 === 1 && mod100 !== 11) w = 'СООБЩЕНИЕ';
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) w = 'СООБЩЕНИЯ';
  return `ОТКРЕПИТЬ ${x} ${w}`;
}

function ruUnpinConfirmTitle(n) {
  const x = Math.abs(Number(n)) || 0;
  if (x === 1) return 'Открепить сообщение?';
  const mod100 = x % 100;
  const mod10 = x % 10;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `Открепить ${x} сообщения?`;
  return `Открепить ${x} сообщений?`;
}

function ruUnpinConfirmBody(n) {
  const x = Math.abs(Number(n)) || 0;
  if (x === 1) {
    return 'Сообщение исчезнет из закреплённых, но останется в истории чата.';
  }
  const mod100 = x % 100;
  const mod10 = x % 10;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
    return `${x} сообщения исчезнут из закреплённых, но останутся в истории чата.`;
  }
  return `${x} сообщений исчезнут из закреплённых, но останутся в истории чата.`;
}

export default function ChatThread({
  isOpen,
  onClose,
  isChatSwitching,
  currentChat,
  currentChatType,
  counterpartyPresence,
  resolveImageUrl,
  formatTime,

  chatMessages,
  messagesEndRef,
  flashMessageId,
  onFlashMessage,
  reactionJumpHint = null,
  onConsumeReactionJumpHint,
  unseenReactionMessageIds = [],
  onAckUnseenReactionMessage,
  messagesContainerRef,
  prependScrollRestoreRef = null,
  messagesAroundRevision = 0,

  onReachTop,
  onReachBottom,

  loadingOlder = false,
  loadingNewer = false,

  hasMoreOlder = false,
  hasMoreNewer = false,
  // можно ли автоскроллить (когда пользователь «внизу»)
  autoscrollEnabled = false,
  scrollAnchorActive = false,
  // сообщать родителю, у низа ли пользователь
  onMessagesScroll,

  myId,
  isMessageReadByCounterparty,

  actionBarOpen,
  multiSelectMode = false,
  selectedMessage,
  selectedMessageIds,
  selectedCount,
  selectionHasContent,
  deletableCount,
  onCloseActionBar,
  onMessageClick,
  onEnableMultiSelect,
  onReply,
  onCopy,
  onDelete,
  onForward,
  onStartEdit,
  onStartEditLastMessage,
  canDeleteMessage,
  canEditMessage,

  isEditingMessageId,
  editingText,
  setEditingText,
  onCancelEdit,
  onSaveEdit,

  onIncFont,
  onDecFont,
  fontSize,

  replyDraft,
  setReplyDraft,

  newMessage,
  setNewMessage,
  onSendMessage,

  isSending = false,
  onRetrySend,
  onSearchModeChange,
  onLoadAroundForSearch,
  onJumpToLatestWindow,
  onRequestLiveBottom,
  searchMode = false,
  firstUnreadId = null,

  chatId = null,
  onPatchMessage,
  onSuppressReactionToast,
  showToast,
  onOpenChatAvatarProfile,
  onOpenUserProfile,
  onToggleChatMute,
  getPeerEmployeeId,
  employeeTaskStatsMap,

  pinnedMessages = [],
  canPinOrUnpin = false,
  onPinMessage,
  onUnpinMessage,
}) {
  const notReady = !isOpen || !currentChat;
  const IsIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const emojiSpriteIndex = useEmojiSpriteIndex();
  const [isImportant, setIsImportant] = React.useState(false);
  const [showScrollDown, setShowScrollDown] = React.useState(false);
  const [hideUnreadDivider, setHideUnreadDivider] = React.useState(false);
  const [pinnedStripIndex, setPinnedStripIndex] = React.useState(0);
  const [pinnedFeedOpen, setPinnedFeedOpen] = React.useState(false);
  const [pinnedFeedItems, setPinnedFeedItems] = React.useState([]);
  const [pinnedFeedBulkBusy, setPinnedFeedBulkBusy] = React.useState(false);
  const [pinnedFeedUnpinConfirmOpen, setPinnedFeedUnpinConfirmOpen] = React.useState(false);
  const [knownImageDimensionsByUrl, setKnownImageDimensionsByUrl] = React.useState({});
  const pendingImageDimensionsRef = React.useRef({});
  const scrollAnchorStateRef = React.useRef(null);

  const rememberImageNaturalSize = React.useCallback((url, naturalW, naturalH) => {
    if (!url) return;
    const w = Number(naturalW) || 0;
    const h = Number(naturalH) || 0;
    if (w <= 0 || h <= 0) return;
    const anchor = scrollAnchorStateRef.current;
    if (anchor && Date.now() < anchor.until) {
      const prevEntry = pendingImageDimensionsRef.current[url];
      if (prevEntry?.w === w && prevEntry?.h === h) return;
      pendingImageDimensionsRef.current[url] = { w, h };
      return;
    }
    setKnownImageDimensionsByUrl((prev) => {
      const prevEntry = prev[url];
      if (prevEntry?.w === w && prevEntry?.h === h) return prev;
      return { ...prev, [url]: { w, h } };
    });
  }, []);

  const messageById = React.useMemo(() => {
    const map = new Map();
    if (!Array.isArray(chatMessages)) return map;
    for (const message of chatMessages) {
      if (message?.id == null) continue;
      map.set(String(message.id), message);
    }
    return map;
  }, [chatMessages]);

  const selectedMessageIdSet = React.useMemo(() => {
    const set = new Set();
    for (const id of selectedMessageIds || []) {
      if (id != null) set.add(String(id));
    }
    return set;
  }, [selectedMessageIds]);

  const unseenReactionMessageIdSet = React.useMemo(() => {
    const set = new Set();
    for (const id of unseenReactionMessageIds || []) {
      if (id != null) set.add(String(id));
    }
    return set;
  }, [unseenReactionMessageIds]);

  const unreadBelowCount = React.useMemo(() => {
    if (!firstUnreadId || !chatMessages) return 0;
    return chatMessages.filter(
      m => Number(m.id) >= Number(firstUnreadId) && Number(m.sender_id) !== Number(myId)
    ).length;
  }, [firstUnreadId, chatMessages, myId]);

  /** Непросмотренные реакции: наши сообщения из списка «ещё не посещали» и с ненулевыми реакциями */
  const reactionJumpTargets = React.useMemo(() => {
    const messageIds = [];
    if (!Array.isArray(chatMessages) || myId == null) return { messageIds };
    if (!unseenReactionMessageIdSet.size) return { messageIds };
    const myIdNum = Number(myId);
    for (const m of chatMessages) {
      if (!unseenReactionMessageIdSet.has(String(m.id))) continue;
      if (Number(m.sender_id) !== myIdNum) continue;
      const rx = Array.isArray(m.reactions) ? m.reactions : [];
      let sum = 0;
      for (const r of rx) sum += Number(r.count) || 0;
      if (sum > 0) messageIds.push(m.id);
    }
    return { messageIds };
  }, [chatMessages, myId, unseenReactionMessageIdSet]);

  React.useEffect(() => {
    const el = messagesContainerRef?.current;
    if (!el) return;
    const handleScroll = () => {
      const diff = Math.max(0, el.scrollHeight - el.scrollTop - el.clientHeight);
      setShowScrollDown(diff > 300);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [messagesContainerRef]);

  // ---------- поиск по сообщениям ----------
  const [headerMenuOpen, setHeaderMenuOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [chatDragActive, setChatDragActive] = React.useState(false);
  const chatDragCounterRef = React.useRef(0);
  // В режиме поиска отключаем любые автоскроллы (кроме явного клика "вверх/вниз")
  const disableAutoScroll = searchOpen || searchMode;
  const lastScrollTopRef = React.useRef(0);
  const lastScrollDirectionRef = React.useRef('none');
  const topLoadRequiresUserScrollRef = React.useRef(false);
  const topReachLockRef = React.useRef(false);
  const topReachCooldownUntilRef = React.useRef(0);
  const bottomReachLockRef = React.useRef(false);
  const bottomReachCooldownUntilRef = React.useRef(0);

  const [activeMatchIndex, setActiveMatchIndex] = React.useState(-1);
  const [searchIds, setSearchIds] = React.useState([]);
  const [searchTotal, setSearchTotal] = React.useState(0);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [aroundLoading, setAroundLoading] = React.useState(false);
  const [jumpingToBottom, setJumpingToBottom] = React.useState(false);
  const [mediaPanelOpen, setMediaPanelOpen] = React.useState(false);
  const handleHeaderAvatarClick = React.useCallback((e) => {
    e.stopPropagation();
    if (!currentChat || isFavoritesChat(currentChat)) return;
    onOpenChatAvatarProfile?.(currentChat);
  }, [currentChat, onOpenChatAvatarProfile]);

  const handleMessageAvatarClick = React.useCallback((e, senderId, senderName) => {
    e.stopPropagation();
    e.preventDefault();
    if ((senderName || '').trim().toLowerCase() === 'система') return;
    const employeeId = Number(senderId);
    if (!Number.isFinite(employeeId) || employeeId <= 0) return;
    onOpenUserProfile?.(employeeId);
  }, [onOpenUserProfile]);

  const handleHeaderTitleClick = React.useCallback((e) => {
    e.stopPropagation();
    setMediaPanelOpen(true);
  }, []);

  const formatMessageTimestamp = React.useCallback((iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return formatTime(iso);

    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    const datePart = d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
    const timePart = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    return `${datePart} в ${timePart}`;
  }, [formatTime]);

  const renderMessageTimeRow = React.useCallback((msg) => (
    <span className={s.msgTimeRow}>
      {Boolean(msg.is_pinned) && (
        <span className={s.msgPinOnBubble} title="Закреплено" aria-label="Закреплено">
          <PinMessageIcon className={s.msgPinOnBubbleSvg} />
        </span>
      )}
      <span className={s.msgTime}>
        {formatMessageTimestamp(msg.timestamp)}
        {msg.edited_at ? <span className={s.msgEdited}> (изменено)</span> : null}
      </span>
    </span>
  ), [formatMessageTimestamp]);
  const scrollToMessage = React.useCallback((messageId, opts = {}) => {
    if (!messageId) return false;
    const el = document.getElementById(`msg-${messageId}`);
    if (!el) return false;
    // Отменяем фазу стабилизации prepend — иначе ResizeObserver (загрузка картинок)
    // будет корректировать scrollTop и сбивать позицию после прыжка.
    prependStabilizeUntilRef.current = 0;
    const container = messagesContainerRef?.current;
    if (container) {
      const cr = container.getBoundingClientRect();
      const er = el.getBoundingClientRect();
      const alignStart = opts?.align === 'start';
      const topInset = Number.isFinite(Number(opts?.topInset))
        ? Number(opts.topInset)
        : PINNED_SCROLL_TOP_INSET;
      const targetTop = alignStart
        ? container.scrollTop + (er.top - cr.top) - topInset
        : container.scrollTop + (er.top - cr.top)
          - (container.clientHeight - el.offsetHeight) / 2;
      container.scrollTop = Math.max(0, targetTop);
    } else {
      el.scrollIntoView({
        behavior: 'auto',
        block: opts?.align === 'start' ? 'start' : 'center',
      });
    }
    return true;
  }, [messagesContainerRef]);

  const jumpToNextReactionAbove = React.useCallback(() => {
    const container = messagesContainerRef?.current;
    if (!container || !reactionJumpTargets.messageIds.length) return;

    const getMsgTop = (id) => {
      const el = document.getElementById(`msg-${id}`);
      if (!el) return null;
      const cr = container.getBoundingClientRect();
      const er = el.getBoundingClientRect();
      return container.scrollTop + (er.top - cr.top);
    };

    const items = reactionJumpTargets.messageIds
      .map((id) => ({ id, top: getMsgTop(id) }))
      .filter((x) => x.top != null);

    if (!items.length) {
      showToast?.('Сообщения с реакциями вне экрана — прокрутите историю');
      return;
    }

    const scrollTop = container.scrollTop;
    const padding = 12;
    const above = items.filter((x) => x.top < scrollTop + padding);
    const target = above.length
      ? above.reduce((a, b) => (a.top > b.top ? a : b))
      : items.reduce((a, b) => (a.top > b.top ? a : b));

    const el = document.getElementById(`msg-${target.id}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      onFlashMessage?.(target.id);
      onAckUnseenReactionMessage?.(target.id);
    }
  }, [
    messagesContainerRef,
    reactionJumpTargets,
    onFlashMessage,
    onAckUnseenReactionMessage,
    showToast,
  ]);

  const onReactionJumpFabClick = React.useCallback(() => {
    if (reactionJumpHint?.messageId) {
      const id = reactionJumpHint.messageId;
      const el = document.getElementById(`msg-${id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        onFlashMessage?.(id);
        onConsumeReactionJumpHint?.();
        onAckUnseenReactionMessage?.(id);
        return;
      }
      showToast?.('Сообщение вне экрана — прокрутите историю');
      onConsumeReactionJumpHint?.();
    }
    jumpToNextReactionAbove();
  }, [
    reactionJumpHint,
    jumpToNextReactionAbove,
    onFlashMessage,
    onConsumeReactionJumpHint,
    onAckUnseenReactionMessage,
    showToast,
  ]);

  const [searchTerm, setSearchTerm] = React.useState('');
  const headerMenuRef = React.useRef(null);
  const searchInputRef = React.useRef(null);
  const searchEnterStartedRef = React.useRef(false);
  const sendFormRef = React.useRef(null);
  const chatModalMainRef = React.useRef(null);
  const replyDockRef = React.useRef(null);
  const editDockRef = React.useRef(null);
  const messagesListRef = React.useRef(null);
  const topLoadSentinelRef = React.useRef(null);
  const bottomLoadSentinelRef = React.useRef(null);
  const tryLoadOlderRef = React.useRef(() => { });
  const chatCardRef = React.useRef(null);
  const chatModalBackdropRef = React.useRef(null);
  const prevKbdOffsetRef = React.useRef(0);
  const viewportBaselineHRef = React.useRef(0);
  const [emojiPickerOpen, setEmojiPickerOpen] = React.useState(false);
  const emojiPickerRef = React.useRef(null);
  const [emojiMartData, setEmojiMartData] = React.useState(() => emojiMartDataCache);
  const [emojiMartDataLoading, setEmojiMartDataLoading] = React.useState(() => !emojiMartDataCache);

  React.useEffect(() => {
    let cancelled = false;
    if (!emojiMartDataCache) setEmojiMartDataLoading(true);
    loadEmojiMartData()
      .then((data) => {
        if (cancelled) return;
        setEmojiMartData(data);
      })
      .catch((err) => {
        console.warn('emoji mart preload failed', err);
      })
      .finally(() => {
        if (!cancelled) setEmojiMartDataLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    preloadReactionImages().catch(() => {
      // Если прогрев не удался, просто продолжаем: реакции отрисуются лениво.
    });
  }, []);

  React.useEffect(() => {
    if (!emojiPickerOpen) return;
    const onDown = (e) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setEmojiPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [emojiPickerOpen]);

  const handleEmojiSelect = React.useCallback((emoji) => {
    const el = textareaRef.current;
    if (!el) {
      setNewMessage(prev => prev + emoji.native);
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const before = el.value.slice(0, start);
    const after = el.value.slice(end);
    const next = before + emoji.native + after;
    setNewMessage(next);
    requestAnimationFrame(() => {
      const pos = start + emoji.native.length;
      try { el.setSelectionRange(pos, pos); } catch { }
      el.focus();
    });
  }, [setNewMessage]);

  // актуальное значение autoscrollEnabled без пересоздания слушателей
  const autoscrollEnabledRef = React.useRef(autoscrollEnabled);
  React.useEffect(() => {
    autoscrollEnabledRef.current = autoscrollEnabled;
  }, [autoscrollEnabled]);

  const openBottomClampUntilRef = React.useRef(0);
  const initialFeedClampPendingRef = React.useRef(false);

  const forceScrollContainerToBottom = React.useCallback((list) => {
    if (!list?.isConnected) return;
    const top = Math.max(0, list.scrollHeight - list.clientHeight);
    try {
      list.scrollTo({ top, behavior: 'auto' });
    } catch {
      list.scrollTop = top;
    }
  }, []);

  React.useEffect(() => {
    if (!currentChat?.id) return;
    openBottomClampUntilRef.current = Date.now() + CHAT_OPEN_BOTTOM_CLAMP_MS;
    initialFeedClampPendingRef.current = true;
    const list = messagesContainerRef?.current;
    if (list?.isConnected) {
      list.scrollTop = 0;
    }
  }, [currentChat?.id, messagesContainerRef]);

  /** mode: "contentResize" — рост ленты (картинки); "composer" — рост поля ввода у уже «прижатых» к низу. */
  const clampChatToBottom = React.useCallback((mode = 'default') => {
    if (!isOpen || !currentChat?.id) return;
    if (disableAutoScroll) {
      console.log('[autoscroll] clampChatToBottom skipped: disableAutoScroll', { mode });
      return;
    }
    if (scrollAnchorActive) {
      console.warn('[autoscroll] clampChatToBottom BLOCKED by scrollAnchorActive', { mode, chatId: currentChat?.id });
      return;
    }
    const anchorLock = scrollAnchorStateRef.current;
    if (anchorLock && Date.now() < anchorLock.until) return;
    const list = messagesContainerRef?.current;
    if (!list?.isConnected) return;

    const withinOpenClamp = () => Date.now() < openBottomClampUntilRef.current;
    const diff = Math.max(0, list.scrollHeight - list.scrollTop - list.clientHeight);
    const nearBottom = diff < CHAT_NEAR_BOTTOM_PX || list.scrollHeight <= list.clientHeight + 1;

    if (mode === 'open') {
      if (!withinOpenClamp() || !autoscrollEnabledRef.current) return;
    } else if (mode === 'contentResize' || mode === 'default') {
      if (!autoscrollEnabledRef.current && !nearBottom) return;
    } else if (mode === 'composer') {
      const composing = sendFormRef.current?.contains(document.activeElement);
      if (!nearBottom && !composing) {
        console.log('[autoscroll] clampChatToBottom skipped (composer): not near bottom', { diff });
        return;
      }
    } else if (mode === 'keyboard') {
      if (!nearBottom && !autoscrollEnabledRef.current) return;
    } else if (!autoscrollEnabledRef.current && !nearBottom) {
      console.log('[autoscroll] clampChatToBottom skipped: user away from bottom', { diff, mode });
      return;
    }

    forceScrollContainerToBottom(list);
  }, [isOpen, currentChat?.id, disableAutoScroll, scrollAnchorActive, messagesContainerRef, forceScrollContainerToBottom]);

  const clampChatToBottomRef = React.useRef(clampChatToBottom);
  clampChatToBottomRef.current = clampChatToBottom;

  // После долгого фона вкладки метрики/scrollTop иногда рассинхронизируются — восстанавливаем «у низа».
  React.useEffect(() => {
    const onVis = () => {
      if (document.hidden || !isOpen || !currentChat?.id) return;
      const list = messagesContainerRef?.current;
      if (!list?.isConnected || disableAutoScroll || scrollAnchorActive) return;
      const diff = Math.max(0, list.scrollHeight - list.scrollTop - list.clientHeight);
      const near = diff < CHAT_NEAR_BOTTOM_PX || list.scrollHeight <= list.clientHeight + 1;
      if (!near) return;
      if (!hasMoreNewer) {
        onMessagesScroll?.(true);
        requestAnimationFrame(() => {
          clampChatToBottom('composer');
        });
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [
    isOpen,
    currentChat?.id,
    disableAutoScroll,
    scrollAnchorActive,
    hasMoreNewer,
    messagesContainerRef,
    onMessagesScroll,
    clampChatToBottom,
  ]);

  const mediaClampRafRef = React.useRef(null);
  const tryLoadNewerRef = React.useRef(() => { });
  const prependStabilizeUntilRef = React.useRef(0);
  const prependLastScrollHeightRef = React.useRef(0);
  const prependStabilizeUserAdjustedRef = React.useRef(false);
  const prependProgrammaticScrollUntilRef = React.useRef(0);
  /** Якорь позиции целевого сообщения при прыжке к закрепу (компенсация без повторного center) */
  const scrollAnchorMaintainTimersRef = React.useRef([]);

  const clearScrollAnchorMaintainTimers = React.useCallback(() => {
    scrollAnchorMaintainTimersRef.current.forEach((timerId) => {
      window.clearTimeout(timerId);
    });
    scrollAnchorMaintainTimersRef.current = [];
  }, []);

  const endScrollAnchorLock = React.useCallback(() => {
    const pending = pendingImageDimensionsRef.current;
    const pendingKeys = Object.keys(pending);
    scrollAnchorStateRef.current = null;
    clearScrollAnchorMaintainTimers();
    if (pendingKeys.length) {
      const patch = { ...pending };
      pendingImageDimensionsRef.current = {};
      setKnownImageDimensionsByUrl((prev) => ({ ...prev, ...patch }));
    }
  }, [clearScrollAnchorMaintainTimers]);

  const applyScrollAnchorLock = React.useCallback(() => {
    const state = scrollAnchorStateRef.current;
    const box = messagesContainerRef?.current;
    if (!state || !box?.isConnected) return;
    if (Date.now() > state.until) {
      scrollAnchorStateRef.current = null;
      return;
    }
    if (state.offsetTop == null) return;
    const node = box.querySelector(`[data-msg-id="${state.messageId}"]`);
    if (!node) return;
    const cr = box.getBoundingClientRect();
    const nr = node.getBoundingClientRect();
    const currentOffset = nr.top - cr.top;
    const delta = currentOffset - state.offsetTop;
    if (Math.abs(delta) < 0.5) return;
    box.scrollTop += delta;
    state.offsetTop = currentOffset;
  }, [messagesContainerRef]);

  const scheduleScrollAnchorMaintenance = React.useCallback((messageId) => {
    clearScrollAnchorMaintainTimers();
    [80, 200, 450, 900, 1600, 2400].forEach((delayMs) => {
      const timerId = window.setTimeout(() => {
        const state = scrollAnchorStateRef.current;
        if (!state || state.messageId !== String(messageId) || Date.now() > state.until) return;
        applyScrollAnchorLock();
      }, delayMs);
      scrollAnchorMaintainTimersRef.current.push(timerId);
    });
  }, [applyScrollAnchorLock, clearScrollAnchorMaintainTimers]);

  const scrollAnchorUserScrollRef = React.useRef(0);

  const beginScrollAnchorLock = React.useCallback((messageId, durationMs = CHAT_SCROLL_ANCHOR_LOCK_MS) => {
    if (messageId == null) return;
    clearScrollAnchorMaintainTimers();
    scrollAnchorStateRef.current = null;
    scrollAnchorStateRef.current = {
      messageId: String(messageId),
      offsetTop: null,
      until: Date.now() + durationMs,
    };
    scrollAnchorUserScrollRef.current = messagesContainerRef?.current?.scrollTop ?? 0;
    prependStabilizeUntilRef.current = 0;
    scrollToMessage(messageId, PINNED_SCROLL_OPTS);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const state = scrollAnchorStateRef.current;
        if (!state || state.messageId !== String(messageId)) return;
        const box = messagesContainerRef?.current;
        const node = box?.querySelector(`[data-msg-id="${messageId}"]`);
        if (!box || !node) return;
        const cr = box.getBoundingClientRect();
        const nr = node.getBoundingClientRect();
        state.offsetTop = nr.top - cr.top;
        applyScrollAnchorLock();
        scheduleScrollAnchorMaintenance(messageId);
      });
    });
  }, [
    messagesContainerRef,
    scrollToMessage,
    applyScrollAnchorLock,
    clearScrollAnchorMaintainTimers,
    scheduleScrollAnchorMaintenance,
  ]);

  React.useLayoutEffect(() => {
    const state = scrollAnchorStateRef.current;
    if (!state || Date.now() > state.until) return;
    applyScrollAnchorLock();
  }, [knownImageDimensionsByUrl, applyScrollAnchorLock]);

  /** Блокирует догрузку «вверх», пока scrollTop кратко 0 после прыжка к сообщению */
  const scrollJumpLockRef = React.useRef(false);
  const pendingJumpAnchorRef = React.useRef(null);

  React.useEffect(() => {
    if (!currentChat?.id) return;
    prependStabilizeUntilRef.current = 0;
    prependLastScrollHeightRef.current = 0;
    prependStabilizeUserAdjustedRef.current = false;
    prependProgrammaticScrollUntilRef.current = 0;
    scrollAnchorStateRef.current = null;
    scrollJumpLockRef.current = false;
    pendingJumpAnchorRef.current = null;
    clearScrollAnchorMaintainTimers();
    pendingImageDimensionsRef.current = {};
  }, [currentChat?.id, clearScrollAnchorMaintainTimers]);

  const scheduleClampAfterMedia = React.useCallback(() => {
    if (disableAutoScroll || scrollAnchorActive) return;
    const anchor = scrollAnchorStateRef.current;
    if (anchor && Date.now() < anchor.until) return;
    if (scrollJumpLockRef.current) return;
    if (mediaClampRafRef.current != null) {
      cancelAnimationFrame(mediaClampRafRef.current);
    }
    mediaClampRafRef.current = requestAnimationFrame(() => {
      mediaClampRafRef.current = null;
      clampChatToBottom('contentResize');
      requestAnimationFrame(() => {
        clampChatToBottom('contentResize');
      });
    });
  }, [disableAutoScroll, scrollAnchorActive, clampChatToBottom]);

  const onChatMediaLoaded = React.useCallback(() => {
    const anchor = scrollAnchorStateRef.current;
    if (anchor && Date.now() < anchor.until) {
      applyScrollAnchorLock();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          applyScrollAnchorLock();
        });
      });
      return;
    }
    if (scrollJumpLockRef.current) return;
    scheduleClampAfterMedia();
  }, [applyScrollAnchorLock, scheduleClampAfterMedia]);

  // Восстановление scrollTop после prepend истории — в layout-фазе того же commit, до paint (иначе кадр мелькает).
  React.useLayoutEffect(() => {
    if (!prependScrollRestoreRef) return;
    const spec = prependScrollRestoreRef.current;
    const box = messagesContainerRef?.current;
    if (!spec || !box?.isConnected) return;

    const {
      prevScrollHeight,
      prevScrollTop,
      anchorMessageId,
      anchorVisibleOffset,
      skipPrependStabilize,
      skipDeltaFallback,
    } = spec;

    let nextTop;
    if (anchorMessageId != null) {
      const node = box.querySelector(`[data-msg-id="${anchorMessageId}"]`);
      if (node) {
        const cr = box.getBoundingClientRect();
        const nr = node.getBoundingClientRect();
        const positionInContent = box.scrollTop + (nr.top - cr.top);
        nextTop = positionInContent - anchorVisibleOffset;
      }
    }
    if (nextTop == null) {
      if (skipDeltaFallback) {
        prependScrollRestoreRef.current = null;
        return;
      }
      const delta = box.scrollHeight - prevScrollHeight;
      nextTop = prevScrollTop + delta;
    }
    prependProgrammaticScrollUntilRef.current = Date.now() + (IsIOS ? 420 : 180);
    box.scrollTop = Math.max(0, nextTop);
    lastScrollTopRef.current = box.scrollTop;
    topReachCooldownUntilRef.current = Math.max(
      topReachCooldownUntilRef.current,
      Date.now() + CHAT_HISTORY_CHAIN_GUARD_MS,
    );
    topLoadRequiresUserScrollRef.current = true;
    if (!skipPrependStabilize) {
      // Короткая фаза стабилизации после prepend:
      // компенсируем изменения высоты контента (например, когда догружаются картинки).
      prependStabilizeUntilRef.current = Date.now() + (IsIOS ? 1200 : 1400);
      prependLastScrollHeightRef.current = box.scrollHeight;
      prependStabilizeUserAdjustedRef.current = false;
    }
    prependScrollRestoreRef.current = null;
  }, [chatMessages.length, messagesAroundRevision, messagesContainerRef, prependScrollRestoreRef, IsIOS]);

  // Первая порция сообщений после смены чата: сброс устаревшего scrollTop и прижатие к низу (если нет якоря).
  React.useLayoutEffect(() => {
    if (!isOpen || !currentChat?.id) return;
    if (!initialFeedClampPendingRef.current) return;
    if (!Array.isArray(chatMessages) || chatMessages.length === 0) return;
    if (scrollAnchorActive || disableAutoScroll) {
      initialFeedClampPendingRef.current = false;
      return;
    }

    initialFeedClampPendingRef.current = false;
    openBottomClampUntilRef.current = Date.now() + CHAT_OPEN_BOTTOM_CLAMP_MS;
    const list = messagesContainerRef?.current;
    if (!list?.isConnected) return;

    forceScrollContainerToBottom(list);
    clampChatToBottom('open');
    const t1 = window.setTimeout(() => clampChatToBottom('open'), 0);
    const t2 = window.setTimeout(() => clampChatToBottom('open'), 64);
    const t3 = window.setTimeout(() => clampChatToBottom('contentResize'), 180);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [
    isOpen,
    currentChat?.id,
    chatMessages.length,
    scrollAnchorActive,
    disableAutoScroll,
    messagesContainerRef,
    forceScrollContainerToBottom,
    clampChatToBottom,
  ]);

  React.useLayoutEffect(() => {
    const anchorId = pendingJumpAnchorRef.current;
    if (anchorId == null) return;
    pendingJumpAnchorRef.current = null;
    prependStabilizeUntilRef.current = 0;
    scrollToMessage(anchorId);
  }, [chatMessages.length, messagesAroundRevision, scrollToMessage]);

  React.useEffect(() => {
    const box = messagesContainerRef?.current;
    const list = messagesListRef.current;
    if (!box || !list || typeof ResizeObserver === 'undefined') return;

    let rafId = null;
    const ro = new ResizeObserver(() => {
      if (!box.isConnected) return;
      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const anchor = scrollAnchorStateRef.current;
        if (anchor && Date.now() < anchor.until) {
          applyScrollAnchorLock();
          return;
        }
        if (scrollJumpLockRef.current) return;
        if (Date.now() >= prependStabilizeUntilRef.current) return;
        if (prependStabilizeUserAdjustedRef.current) {
          prependLastScrollHeightRef.current = box.scrollHeight;
          return;
        }
        const prevH = prependLastScrollHeightRef.current || box.scrollHeight;
        const nextH = box.scrollHeight;
        const delta = nextH - prevH;
        if (Math.abs(delta) < 0.5) {
          prependLastScrollHeightRef.current = nextH;
          return;
        }
        prependProgrammaticScrollUntilRef.current = Date.now() + (IsIOS ? 240 : 120);
        box.scrollTop += delta;
        lastScrollTopRef.current = box.scrollTop;
        prependLastScrollHeightRef.current = nextH;
      });
    });

    ro.observe(list);
    return () => {
      try { ro.disconnect(); } catch { /* noop */ }
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [messagesContainerRef, chatMessages.length, applyScrollAnchorLock, IsIOS]);

  const tryLoadOlder = React.useCallback(() => {
    const now = Date.now();
    if (!hasMoreOlder || loadingOlder) return;
    if (scrollJumpLockRef.current || aroundLoading) return;
    if (topReachLockRef.current) return;
    if (topLoadRequiresUserScrollRef.current) return;
    if (now < topReachCooldownUntilRef.current) return;
    onReachTop?.();
  }, [hasMoreOlder, loadingOlder, aroundLoading, onReachTop]);

  const tryLoadNewer = React.useCallback(() => {
    const now = Date.now();
    if (!hasMoreNewer || loadingNewer) return;
    if (scrollJumpLockRef.current || aroundLoading) return;
    if (bottomReachLockRef.current) return;
    if (now < bottomReachCooldownUntilRef.current) return;
    onReachBottom?.();
  }, [hasMoreNewer, loadingNewer, aroundLoading, onReachBottom]);

  React.useEffect(() => {
    tryLoadNewerRef.current = tryLoadNewer;
  }, [tryLoadNewer]);

  React.useEffect(() => {
    tryLoadOlderRef.current = tryLoadOlder;
  }, [tryLoadOlder]);

  React.useEffect(() => {
    const root = messagesContainerRef?.current;
    const topSentinel = topLoadSentinelRef.current;
    const bottomSentinel = bottomLoadSentinelRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;

    const edge = `${CHAT_PRELOAD_EDGE_PX}px`;

    const topObserver = topSentinel ? new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (lastScrollDirectionRef.current !== 'up') continue;
          tryLoadOlderRef.current?.();
        }
      },
      {
        root,
        rootMargin: `${edge} 0px 0px 0px`,
        threshold: 0.01,
      },
    ) : null;

    const bottomObserver = bottomSentinel ? new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          tryLoadNewerRef.current?.();
        }
      },
      {
        root,
        rootMargin: `0px 0px ${edge} 0px`,
        threshold: 0.01,
      },
    ) : null;

    if (topObserver && topSentinel) topObserver.observe(topSentinel);
    if (bottomObserver && bottomSentinel) bottomObserver.observe(bottomSentinel);

    return () => {
      try { topObserver?.disconnect(); } catch { /* ignore */ }
      try { bottomObserver?.disconnect(); } catch { /* ignore */ }
    };
  }, [messagesContainerRef, currentChat?.id, isOpen, chatMessages.length]);

  React.useEffect(() => {
    if (loadingOlder) {
      topReachLockRef.current = true;
      return;
    }
    topReachCooldownUntilRef.current = Math.max(
      topReachCooldownUntilRef.current,
      Date.now() + 220,
    );
    const t = window.setTimeout(() => {
      topReachLockRef.current = false;
    }, 220);
    return () => window.clearTimeout(t);
  }, [loadingOlder]);

  React.useEffect(() => {
    if (loadingNewer) {
      bottomReachLockRef.current = true;
      return;
    }
    bottomReachCooldownUntilRef.current = Date.now() + 180;
    const t = window.setTimeout(() => {
      bottomReachLockRef.current = false;
    }, 180);
    return () => window.clearTimeout(t);
  }, [loadingNewer]);

  React.useEffect(() => {
    const el = messagesContainerRef?.current;
    lastScrollTopRef.current = el?.scrollTop ?? 0;
    topReachLockRef.current = false;
    topReachCooldownUntilRef.current = 0;
    topLoadRequiresUserScrollRef.current = false;
    lastScrollDirectionRef.current = 'none';
    bottomReachLockRef.current = false;
    bottomReachCooldownUntilRef.current = 0;
  }, [currentChat?.id, isOpen, messagesContainerRef]);

  // состояние “клавиатура открыта” (по offset)
  const kbdOpenRef = React.useRef(false);


  const closeSearch = React.useCallback(() => {
    setSearchOpen(false);
    setSearchTerm('');
    setActiveMatchIndex(-1);
    setSearchIds([]);
    setSearchTotal(0);
    searchEnterStartedRef.current = false;
  }, []);

  React.useEffect(() => {
    // при смене чата очищаем поиск, чтобы не наследовать результаты от прошлого чата
    setSearchOpen(false);
    setSearchTerm('');
    setSearchIds([]);
    setSearchTotal(0);
    setActiveMatchIndex(-1);
    searchEnterStartedRef.current = false;
  }, [currentChat?.id]);


  // закрытие меню по клику вне
  React.useEffect(() => {
    if (!headerMenuOpen) return;
    const onDown = (e) => {
      const root = headerMenuRef.current;
      if (root && !root.contains(e.target)) setHeaderMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [headerMenuOpen]);

  // фокус в инпут при открытии поиска
  React.useEffect(() => {
    if (!searchOpen) return;
    requestAnimationFrame(() => {
      try { searchInputRef.current?.focus(); } catch { }
    });
  }, [searchOpen]);
  React.useEffect(() => {
    onSearchModeChange?.(searchOpen);
  }, [searchOpen, onSearchModeChange]);

  // const scrollToEnd = React.useCallback(() => {
  //   if (disableAutoScroll) return;
  //   if (!autoscrollEnabled) return;
  //   const container = messagesContainerRef?.current;
  //   if (!container) return;
  //   container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  // }, [messagesContainerRef, autoscrollEnabled, disableAutoScroll]);

  // const scrollToEndInstant = React.useCallback(() => {
  //   if (disableAutoScroll) return;
  //   if (!autoscrollEnabled) return;

  //   const container = messagesContainerRef?.current;
  //   if (!container) return;
  //   container.scrollTop = container.scrollHeight;
  // }, [messagesContainerRef, disableAutoScroll, autoscrollEnabled]);

  const resetViewportInlineStyles = React.useCallback(() => {
    const backdrop = chatModalBackdropRef.current;
    const card = chatCardRef.current;
    if (backdrop) {
      backdrop.style.top = '';
      backdrop.style.left = '';
      backdrop.style.right = '';
      backdrop.style.bottom = '';
      backdrop.style.width = '';
      backdrop.style.height = '';
      delete backdrop.dataset.kbdOpen;
    }
    if (card) {
      card.style.height = '';
      card.style.marginTop = '';
      card.style.flexShrink = '';
      card.style.setProperty('--kbd-offset', '0px');
      card.style.setProperty('--kbd-inset', '0px');
      delete card.dataset.kbdOpen;
    }
  }, []);

  /** Только при закрытой клавиатуре — иначе baseline «прилипает» к низкой высоте и ломает следующие фокусы. */
  const captureViewportBaseline = React.useCallback(() => {
    if (sendFormRef.current?.contains(document.activeElement)) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const layoutH = window.innerHeight;
    const visibleH = vv.height;
    const shrink = Math.max(layoutH - visibleH - (vv.offsetTop || 0), layoutH - visibleH, 0);
    if (shrink >= KBD_OPEN_MIN_PX) return;
    viewportBaselineHRef.current = Math.max(layoutH, visibleH);
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      viewportBaselineHRef.current = 0;
      return;
    }
    const t = window.setTimeout(captureViewportBaseline, 0);
    return () => window.clearTimeout(t);
  }, [isOpen, currentChat?.id, captureViewportBaseline]);

  React.useEffect(() => {
    if (!isOpen) return;

    const vv = window.visualViewport;
    if (!vv) return;

    let raf = 0;
    let scrollRaf = 0;
    let clampRaf = 0;
    const isMobileLayout = () => window.matchMedia('(max-width: 767px)').matches;

    const resetPageScroll = () => {
      if (!IsIOS) return;
      if (window.scrollY === 0 && vv.offsetTop === 0) return;
      window.scrollTo(0, 0);
    };

    const applyLayout = () => {
      const card = chatCardRef.current;
      const backdrop = chatModalBackdropRef.current;
      if (!card) return;

      const isFixedBackdrop = backdrop
        && getComputedStyle(backdrop).position === 'fixed';

      if (!isFixedBackdrop) {
        resetViewportInlineStyles();
        prevKbdOffsetRef.current = 0;
        kbdOpenRef.current = false;
        return;
      }

      const composerFocused = sendFormRef.current?.contains(document.activeElement);
      let { inset, open: kbdOpen } = computeKeyboardState(
        vv,
        viewportBaselineHRef.current,
        IsIOS,
      );

      if (isMobileLayout() && composerFocused) {
        kbdOpen = true;
      }

      backdrop.style.top = '';
      backdrop.style.left = '';
      backdrop.style.right = '';
      backdrop.style.bottom = '';
      backdrop.style.width = '';
      backdrop.style.height = '';
      if (kbdOpen) {
        const cardH = Math.max(1, Math.round(vv.height));
        const cardTop = Math.max(0, Math.round(vv.offsetTop || 0));
        card.style.height = `${cardH}px`;
        card.style.marginTop = `${cardTop}px`;
        card.style.flexShrink = '0';
        card.style.setProperty('--kbd-offset', '0px');
        card.style.setProperty('--kbd-inset', '0px');
      } else {
        card.style.height = '100%';
        card.style.marginTop = '';
        card.style.flexShrink = '';
        card.style.setProperty('--kbd-offset', '0px');
        card.style.setProperty('--kbd-inset', '0px');
      }
      card.dataset.kbdOpen = kbdOpen ? 'true' : 'false';
      backdrop.dataset.kbdOpen = kbdOpen ? 'true' : 'false';

      prevKbdOffsetRef.current = inset;
      kbdOpenRef.current = kbdOpen;

      if (kbdOpen) {
        cancelAnimationFrame(clampRaf);
        clampRaf = requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            clampChatToBottomRef.current?.('keyboard');
          });
        });
      }
    };

    const scheduleLayout = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(applyLayout);
    };

    const onVvScroll = () => {
      if (!IsIOS) {
        scheduleLayout();
        return;
      }
      cancelAnimationFrame(scrollRaf);
      scrollRaf = requestAnimationFrame(() => {
        resetPageScroll();
        scheduleLayout();
      });
    };

    const onOrient = () => {
      captureViewportBaseline();
      scheduleLayout();
    };
    const onWinResize = () => scheduleLayout();

    const mobileLayoutMq = window.matchMedia('(max-width: 767px)');
    const onBreakpointChange = () => {
      if (!mobileLayoutMq.matches) {
        resetViewportInlineStyles();
        viewportBaselineHRef.current = 0;
        prevKbdOffsetRef.current = 0;
        kbdOpenRef.current = false;
        window.setTimeout(captureViewportBaseline, 0);
      } else {
        captureViewportBaseline();
      }
      scheduleLayout();
    };

    const refreshBaselineAfterBlur = () => {
      if (sendFormRef.current?.contains(document.activeElement)) return;
      captureViewportBaseline();
      scheduleLayout();
    };

    const onFocusIn = (e) => {
      if (!isMobileLayout()) return;
      if (!sendFormRef.current?.contains(e.target)) return;
      scheduleLayout();
      window.setTimeout(scheduleLayout, 80);
      window.setTimeout(scheduleLayout, 200);
      window.setTimeout(() => {
        clampChatToBottomRef.current?.('keyboard');
      }, 120);
    };

    const onFocusOut = (e) => {
      if (!isMobileLayout()) return;
      if (!sendFormRef.current?.contains(e.target)) return;
      scheduleLayout();
      const related = e.relatedTarget;
      if (related && sendFormRef.current?.contains(related)) return;
      window.setTimeout(refreshBaselineAfterBlur, 120);
      window.setTimeout(refreshBaselineAfterBlur, 360);
    };

    const cardEl = chatCardRef.current;

    scheduleLayout();
    setTimeout(() => {
      captureViewportBaseline();
      scheduleLayout();
    }, 0);
    setTimeout(scheduleLayout, 50);

    vv.addEventListener('resize', scheduleLayout);
    vv.addEventListener('scroll', onVvScroll);
    window.addEventListener('resize', onWinResize);
    window.addEventListener('orientationchange', onOrient);
    mobileLayoutMq.addEventListener('change', onBreakpointChange);
    cardEl?.addEventListener('focusin', onFocusIn);
    cardEl?.addEventListener('focusout', onFocusOut, true);

    return () => {
      vv.removeEventListener('resize', scheduleLayout);
      vv.removeEventListener('scroll', onVvScroll);
      window.removeEventListener('resize', onWinResize);
      window.removeEventListener('orientationchange', onOrient);
      mobileLayoutMq.removeEventListener('change', onBreakpointChange);
      cardEl?.removeEventListener('focusin', onFocusIn);
      cardEl?.removeEventListener('focusout', onFocusOut, true);
      cancelAnimationFrame(raf);
      cancelAnimationFrame(scrollRaf);
      cancelAnimationFrame(clampRaf);
      resetViewportInlineStyles();
    };
  }, [isOpen, currentChat?.id, IsIOS, resetViewportInlineStyles, captureViewportBaseline]);

  const normalizedSearch = (searchTerm || '').trim().toLowerCase();
  const searchableContentById = React.useMemo(() => {
    if (!normalizedSearch || !Array.isArray(chatMessages)) return null;
    const map = new Map();
    for (const message of chatMessages) {
      if (message?.id == null) continue;
      map.set(String(message.id), String(message?.content || '').toLowerCase());
    }
    return map;
  }, [chatMessages, normalizedSearch]);

  // NEW: активное совпадение теперь берём из searchIds (пришли с бэка)
  const activeMatchId = React.useMemo(() => {
    if (activeMatchIndex < 0) return null;
    return searchIds[activeMatchIndex] ?? null;
  }, [searchIds, activeMatchIndex]);
  const [activeMatchPosition, setActiveMatchPosition] = React.useState('unknown'); // above | below | visible | unknown

  React.useEffect(() => {
    const container = messagesContainerRef?.current;
    if (!container || !searchOpen || activeMatchId == null) {
      setActiveMatchPosition('unknown');
      return;
    }

    const updateVisibility = () => {
      const msg = document.getElementById(`msg-${activeMatchId}`);
      if (!msg) {
        // Если сообщение еще не в DOM (вне текущего окна around),
        // пытаемся определить направление по id границ загруженного окна.
        const firstLoadedId = chatMessages?.[0]?.id;
        const lastLoadedId = chatMessages?.[chatMessages.length - 1]?.id;
        const targetNum = Number(activeMatchId);
        const firstNum = Number(firstLoadedId);
        const lastNum = Number(lastLoadedId);
        if (
          Number.isFinite(targetNum)
          && Number.isFinite(firstNum)
          && Number.isFinite(lastNum)
        ) {
          if (targetNum < firstNum) {
            setActiveMatchPosition('above');
            return;
          }
          if (targetNum > lastNum) {
            setActiveMatchPosition('below');
            return;
          }
        }
        setActiveMatchPosition('unknown');
        return;
      }
      const cr = container.getBoundingClientRect();
      const mr = msg.getBoundingClientRect();
      if (mr.bottom <= cr.top) {
        setActiveMatchPosition('above');
        return;
      }
      if (mr.top >= cr.bottom) {
        setActiveMatchPosition('below');
        return;
      }
      setActiveMatchPosition('visible');
    };

    updateVisibility();
    const rafId = requestAnimationFrame(updateVisibility);
    container.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);

    return () => {
      cancelAnimationFrame(rafId);
      container.removeEventListener('scroll', updateVisibility);
      window.removeEventListener('resize', updateVisibility);
    };
  }, [messagesContainerRef, searchOpen, activeMatchId, chatMessages]);

  const jumpToMessage = React.useCallback(
    async (targetId, opts = {}) => {
      if (!targetId) return;
      const forceAround = Boolean(opts?.forceAround);

      if (!forceAround) {
        const el = document.getElementById(`msg-${targetId}`);
        if (el) {
          scrollToMessage(targetId);
          return;
        }
      }

      if (!onLoadAroundForSearch) {
        if (forceAround) {
          scrollToMessage(targetId);
        }
        return;
      }

      try {
        scrollJumpLockRef.current = true;
        pendingJumpAnchorRef.current = targetId;
        setAroundLoading(true);
        await onLoadAroundForSearch(targetId, opts?.aroundOpts ?? {});
        let attempts = 0;
        const maxAttempts = 10;
        const tryScrollAfterLoad = () => {
          const didScroll = scrollToMessage(targetId);
          if (didScroll || attempts >= maxAttempts) return;
          attempts += 1;
          window.setTimeout(() => {
            requestAnimationFrame(tryScrollAfterLoad);
          }, 35);
        };
        requestAnimationFrame(() => {
          requestAnimationFrame(tryScrollAfterLoad);
        });
      } catch (e) {
        console.error(e);
      } finally {
        setAroundLoading(false);
        window.setTimeout(() => {
          scrollJumpLockRef.current = false;
        }, 900);
      }
    },
    [onLoadAroundForSearch, scrollToMessage]
  );

  const pinnedJumpSeqRef = React.useRef(0);

  const isMessageInLoadedList = React.useCallback((messageId) => {
    if (messageId == null) return false;
    return messageById.has(String(messageId));
  }, [messageById]);

  const scrollToMessageWithRetry = React.useCallback((
    messageId,
    maxAttempts = 12,
    scrollOpts = null,
  ) => new Promise((resolve) => {
    let attempts = 0;
    const tryScroll = () => {
      const ok = scrollToMessage(messageId, scrollOpts || undefined);
      if (ok || attempts >= maxAttempts) {
        resolve(!!ok);
        return;
      }
      attempts += 1;
      requestAnimationFrame(tryScroll);
    };
    requestAnimationFrame(tryScroll);
  }), [scrollToMessage]);

  const preloadChunkImageDimensions = React.useCallback(async (
    messages,
    knownMap = {},
    timeoutMs = 3200,
  ) => {
    if (!Array.isArray(messages) || !messages.length) return {};
    const urls = [];
    const seen = new Set();
    for (const msg of messages) {
      for (const att of msg?.attachments || []) {
        if (att?.kind !== 'image' || !att?.s3_key) continue;
        let k = String(att.s3_key).replace(/^\/+/, '');
        if (!k.startsWith('media/')) k = `media/${k}`;
        const url = `${API_URL}/media/${k}`;
        if (seen.has(url)) continue;
        const { hasMediaDims } = getChatAttachmentDims(att, url, knownMap);
        if (hasMediaDims) continue;
        seen.add(url);
        urls.push(url);
      }
    }
    if (!urls.length) return {};

    const probe = (url) => new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ url, w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => resolve(null);
      img.src = url;
    });

    let results = [];
    await Promise.race([
      Promise.all(urls.map(probe)).then((rows) => { results = rows; }),
      new Promise((resolve) => { window.setTimeout(resolve, timeoutMs); }),
    ]);

    const patch = {};
    for (const row of results) {
      if (row?.w > 0 && row?.h > 0) patch[row.url] = { w: row.w, h: row.h };
    }
    return patch;
  }, []);

  const preloadPinnedChunkAndScroll = React.useCallback(async (messages, targetId) => {
    const windowMsgs = getMessagesWindowAround(messages, targetId);
    const knownMap = knownImageDimensionsByUrl;
    const patch = await preloadChunkImageDimensions(windowMsgs, knownMap);
    if (Object.keys(patch).length) {
      flushSync(() => {
        setKnownImageDimensionsByUrl((prev) => ({ ...prev, ...patch }));
      });
    }
    await new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
    return scrollToMessageWithRetry(targetId, 24, PINNED_SCROLL_OPTS);
  }, [
    knownImageDimensionsByUrl,
    preloadChunkImageDimensions,
    scrollToMessageWithRetry,
  ]);

  const jumpToPinnedMessage = React.useCallback(async (targetId) => {
    if (targetId == null) return false;
    endScrollAnchorLock();
    scrollJumpLockRef.current = true;
    const seq = ++pinnedJumpSeqRef.current;

    const finish = (ok) => {
      if (seq !== pinnedJumpSeqRef.current) return false;
      if (ok) {
        onFlashMessage?.(targetId);
        beginScrollAnchorLock(targetId);
      }
      return ok;
    };

    try {
      if (document.getElementById(`msg-${targetId}`)) {
        const ok = await preloadPinnedChunkAndScroll(chatMessages, targetId);
        return finish(ok);
      }

      if (isMessageInLoadedList(targetId)) {
        const ok = await preloadPinnedChunkAndScroll(chatMessages, targetId);
        if (ok) return finish(true);
      }

      if (!onLoadAroundForSearch) {
        return finish(scrollToMessage(targetId, PINNED_SCROLL_OPTS));
      }

      setAroundLoading(true);
      const data = await onLoadAroundForSearch(targetId, {
        before: 18,
        after: 18,
        keepPinnedList: true,
        skipScrollRestore: true,
      });
      if (seq !== pinnedJumpSeqRef.current) return false;
      prependStabilizeUntilRef.current = 0;
      prependLastScrollHeightRef.current = 0;
      await new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });
      const messages = Array.isArray(data?.messages) ? data.messages : [];
      const ok = await preloadPinnedChunkAndScroll(messages, targetId);
      return finish(ok);
    } catch (e) {
      console.error(e);
      return false;
    } finally {
      if (seq === pinnedJumpSeqRef.current) {
        setAroundLoading(false);
        window.setTimeout(() => {
          if (seq === pinnedJumpSeqRef.current) scrollJumpLockRef.current = false;
        }, CHAT_SCROLL_ANCHOR_LOCK_MS);
      }
    }
  }, [
    chatMessages,
    isMessageInLoadedList,
    onFlashMessage,
    onLoadAroundForSearch,
    scrollToMessage,
    preloadPinnedChunkAndScroll,
    beginScrollAnchorLock,
    endScrollAnchorLock,
  ]);

  const goToMatch = React.useCallback(async (nextIndex) => {
    if (!searchOpen) return;
    if (!searchIds.length) return;

    const idx = Math.max(0, Math.min(searchIds.length - 1, nextIndex));
    const anchorId = searchIds[idx];
    if (!anchorId) return;

    setActiveMatchIndex(idx);

    try {
      await jumpToMessage(anchorId);
    } catch (e) {
      console.error(e);
    }
  }, [searchOpen, searchIds, jumpToMessage]);

  const handleSearchInputEnter = React.useCallback(async () => {
    if (!searchOpen || aroundLoading) return;
    if (!searchIds.length) return;

    const newestIndex = searchIds.length - 1;
    const nextIndex = (!searchEnterStartedRef.current || activeMatchIndex < 0)
      ? newestIndex
      : Math.max(0, activeMatchIndex - 1);

    searchEnterStartedRef.current = true;
    await goToMatch(nextIndex);
  }, [searchOpen, aroundLoading, searchIds, activeMatchIndex, goToMatch]);

  React.useEffect(() => {
    searchEnterStartedRef.current = false;
  }, [searchTerm]);

  const hasSingleMatch = searchIds.length === 1;
  const singleMatchLocked = hasSingleMatch && activeMatchPosition === 'visible';
  const disableSearchPrev = searchIds.length === 0
    || aroundLoading
    || (hasSingleMatch
      ? (singleMatchLocked || activeMatchPosition === 'below')
      : activeMatchIndex <= 0);
  const disableSearchNext = searchIds.length === 0
    || aroundLoading
    || (hasSingleMatch
      ? (singleMatchLocked || activeMatchPosition === 'above')
      : activeMatchIndex >= searchIds.length - 1);

  const showScrollDownFab = showScrollDown && !searchIds.length && !isEditingMessageId;
  const stackReactionJumpFab = showScrollDownFab;

  // NEW: загрузка ids совпадений с бэка при вводе
  React.useEffect(() => {
    if (!searchOpen) return;

    const q = (searchTerm || '').trim();
    if (!q) {
      setSearchIds([]);
      setSearchTotal(0);
      setActiveMatchIndex(-1);
      return;
    }

    const t = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const r = await apiFetch(
          `${API_URL}/chats/${currentChat.id}/messages/search?query=${encodeURIComponent(q)}&limit=2000&offset=0`
        );
        if (!r.ok) throw new Error(String(r.status));
        const data = await r.json();

        // Бэк отдаёт ids "новые -> старые". Нам нужно, чтобы самое новое было ПОСЛЕДНИМ:
        const idsRaw = Array.isArray(data?.ids) ? data.ids : [];
        const ids = idsRaw.slice().reverse(); // теперь "старые -> новые"
        setSearchIds(ids);
        setSearchTotal(Number(data?.total || ids.length || 0));

        // Стартуем с самого нового.
        // Кнопка "вверх" (index - 1) будет вести к более старым совпадениям.
        setActiveMatchIndex(ids.length ? (ids.length - 1) : -1);

      } catch (e) {
        console.error(e);
        setSearchIds([]);
        setSearchTotal(0);
        setActiveMatchIndex(-1);
      } finally {
        setSearchLoading(false);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [searchOpen, searchTerm, currentChat?.id]);



  // ---------- вложения ----------
  const [pickedItems, setPickedItems] = React.useState([]); // { id, file, kind, previewUrl, chatId }
  const [uploading, setUploading] = React.useState(false);
  const [uploadOwnerChatId, setUploadOwnerChatId] = React.useState(null);
  const [uploadProgress, setUploadProgress] = React.useState({}); // { [id]: 0..100 }
  const uploadXhrsRef = React.useRef(new Map());
  const uploadCancelledIdsRef = React.useRef(new Set());
  const activeChatId = chatId || currentChat?.id || null;
  const activeChatIdRef = React.useRef(activeChatId);
  React.useEffect(() => {
    activeChatIdRef.current = activeChatId;
  }, [activeChatId]);
  const isUploadingCurrentChat = uploading
    && uploadOwnerChatId != null
    && String(uploadOwnerChatId) === String(activeChatId);
  const currentChatPickedItems = React.useMemo(
    () => pickedItems.filter((it) => String(it?.chatId ?? '') === String(activeChatId ?? '')),
    [pickedItems, activeChatId]
  );

  const makeId = React.useCallback(() => {
    try {
      // eslint-disable-next-line no-undef
      const uuid = (typeof crypto !== 'undefined' && crypto?.randomUUID) ? crypto.randomUUID() : null;
      return uuid || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    } catch {
      return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
  }, []);

  const inferKindFromFile = React.useCallback((f) => {
    const t = (f?.type || '').toLowerCase();
    if (t.startsWith('image/')) return 'image';
    if (t.startsWith('video/')) return 'video';
    if (t.startsWith('audio/')) return 'audio';
    return 'file';
  }, []);

  React.useLayoutEffect(() => {
    const card = chatCardRef.current;
    const form = sendFormRef.current;
    const main = chatModalMainRef.current;
    if (!card || !form) return;

    const setVar = () => {
      const cardRect = card.getBoundingClientRect();
      const formRect = form.getBoundingClientRect();
      const stackFromBottom = Math.max(
        Math.ceil(cardRect.bottom - formRect.top),
        Math.ceil(formRect.height),
      );
      document.documentElement.style.setProperty('--chat-composer-h', `${stackFromBottom}px`);

      let dockH = 0;
      if (replyDockRef.current) dockH += replyDockRef.current.offsetHeight;
      if (editDockRef.current) dockH += editDockRef.current.offsetHeight;
      const dockPx = `${Math.ceil(dockH)}px`;
      main?.style.setProperty('--chat-inline-dock-h', dockPx);
    };

    setVar();
    window.addEventListener('resize', setVar);
    window.addEventListener('orientationchange', setVar);

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => setVar());
      ro.observe(card);
      ro.observe(form);
      if (main) ro.observe(main);
      if (replyDockRef.current) ro.observe(replyDockRef.current);
      if (editDockRef.current) ro.observe(editDockRef.current);
    }

    return () => {
      window.removeEventListener('resize', setVar);
      window.removeEventListener('orientationchange', setVar);
      main?.style.removeProperty('--chat-inline-dock-h');
      ro?.disconnect();
    };
  }, [
    replyDraft,
    currentChatPickedItems.length,
    isEditingMessageId,
    pinnedMessages,
  ]);

  // Дожим низа: композер, рост списка (картинки/видео), таймеры после первого paint.
  React.useLayoutEffect(() => {
    if (!isOpen || !currentChat) return;
    if (disableAutoScroll || scrollAnchorActive) return;
    const list = messagesContainerRef?.current;
    const form = sendFormRef.current;
    const ul = messagesListRef.current;
    if (!list || !form || !Array.isArray(chatMessages) || chatMessages.length === 0) return;

    clampChatToBottom();

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver((entries) => {
        requestAnimationFrame(() => {
          for (const e of entries) {
            const contentResize = ul && e.target === ul;
            clampChatToBottom(contentResize ? 'contentResize' : 'default');
          }
        });
      });
      ro.observe(form);
      if (ul) ro.observe(ul);
    }

    const t1 = window.setTimeout(clampChatToBottom, 0);
    const t2 = window.setTimeout(clampChatToBottom, 48);
    const t3 = window.setTimeout(clampChatToBottom, 120);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
      if (mediaClampRafRef.current != null) {
        cancelAnimationFrame(mediaClampRafRef.current);
        mediaClampRafRef.current = null;
      }
      try {
        ro?.disconnect();
      } catch {
        /* ignore */
      }
    };
    /* Нужна только смена чата/числа сообщений; весь chatMessages тянул бы эффект на каждый апдейт. */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- см. выше
  }, [
    isOpen,
    currentChat?.id,
    chatMessages.length,
    disableAutoScroll,
    scrollAnchorActive,
    messagesContainerRef,
    replyDraft,
    currentChatPickedItems.length,
    clampChatToBottom,
  ]);

  const mediaRecorderRef = React.useRef(null);
  const mediaStreamRef = React.useRef(null);
  const audioChunksRef = React.useRef([]);
  const isRecordingRef = React.useRef(false);
  const isRecordingStartingRef = React.useRef(false);
  const micSessionRef = React.useRef(0);
  const recStartedAtRef = React.useRef(null);
  const [isRecording, setIsRecording] = React.useState(false);
  const [recStartAt, setRecStartAt] = React.useState(null);
  const [recSeconds, setRecSeconds] = React.useState(0);

  const renderTextWithLinks = React.useCallback((text, highlightQuery) => {
    if (!text) return null;

    const parts = [];
    const source = String(text);
    const q = String(highlightQuery || '').trim();

    const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const isValidInn = (digits) => {
      if (!/^\d+$/.test(digits)) return false;
      if (digits.length === 10) {
        const d = digits.split('').map(Number);
        const n10 = (
          2 * d[0] + 4 * d[1] + 10 * d[2] + 3 * d[3] + 5 * d[4]
          + 9 * d[5] + 4 * d[6] + 6 * d[7] + 8 * d[8]
        ) % 11 % 10;
        return n10 === d[9];
      }
      if (digits.length === 12) {
        const d = digits.split('').map(Number);
        const n11 = (
          7 * d[0] + 2 * d[1] + 4 * d[2] + 10 * d[3] + 3 * d[4] + 5 * d[5]
          + 9 * d[6] + 4 * d[7] + 6 * d[8] + 8 * d[9]
        ) % 11 % 10;
        const n12 = (
          3 * d[0] + 7 * d[1] + 2 * d[2] + 4 * d[3] + 10 * d[4] + 3 * d[5]
          + 5 * d[6] + 9 * d[7] + 4 * d[8] + 6 * d[9] + 8 * d[10]
        ) % 11 % 10;
        return n11 === d[10] && n12 === d[11];
      }
      return false;
    };

    const pushEmojiAwareText = (chunk, keyBase) => {
      if (!chunk) return;
      let lastIndex = 0;
      let match;
      let emojiIdx = 0;
      EMOJI_CLUSTER_RE.lastIndex = 0;

      while ((match = EMOJI_CLUSTER_RE.exec(chunk)) !== null) {
        const emojiText = match[0];
        const emojiStart = match.index;
        if (emojiStart > lastIndex) {
          parts.push(chunk.slice(lastIndex, emojiStart));
        }

        const emojiSpriteStyle = resolveEmojiSpriteStyle(emojiSpriteIndex, emojiText);
        if (emojiSpriteStyle && !NO_SPRITE_EMOJI.has(emojiText)) {
          parts.push(
            <span
              key={`${keyBase}-emoji-${emojiIdx}-${emojiStart}`}
              role="img"
              aria-label={emojiText}
              className={s.inlineAppleEmojiSprite}
              style={emojiSpriteStyle}
            />
          );
        } else {
          parts.push(emojiText);
        }

        lastIndex = emojiStart + emojiText.length;
        emojiIdx += 1;
      }

      if (lastIndex < chunk.length) {
        parts.push(chunk.slice(lastIndex));
      }
    };

    const pushHighlightedText = (chunk, keyBase) => {
      if (!chunk) return;

      if (!q) {
        pushEmojiAwareText(chunk, `${keyBase}-plain`);
        return;
      }

      const reHi = new RegExp(escapeRegExp(q), 'ig');
      let last = 0;
      let mi = 0;
      let m;

      while ((m = reHi.exec(chunk)) !== null) {
        const idx = m.index;
        const hit = m[0];

        if (idx > last) pushEmojiAwareText(chunk.slice(last, idx), `${keyBase}-txt-${last}-${idx}`);

        parts.push(
          <span key={`${keyBase}-h-${mi}-${idx}`} className={s.searchTextMark}>
            {hit}
          </span>
        );

        last = idx + hit.length;
        mi += 1;
      }

      if (last < chunk.length) {
        pushEmojiAwareText(chunk.slice(last), `${keyBase}-txt-tail-${last}`);
      }
    };

    const protectedRe =
      /\b(?:[A-Za-zА-Яа-яЁё]{4}|[A-Za-zА-Яа-яЁё]{2}\d{2})-\d{6}\s*\d{2}\.\d{2}\.\d{4}\b/g;

    const linkRe =
      /(https?:\/\/[^\s<>"']+|ftp:\/\/[^\s<>"']+|www\.[^\s<>"']+|[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}|(?<![\p{L}\d-])\+?\d[\d\s\-()]{8,}\d(?![\p{L}\d]))/giu;

    const mdLinkRe = /\[([^\]]+)\]\((https?:\/\/[^)\s]+|mailto:[^)\s]+)\)/gi;

    function pushChunkWithAutoLinks(chunk, globalOffset) {
      let lastIndex = 0;
      let m;

      linkRe.lastIndex = 0;

      while ((m = linkRe.exec(chunk)) !== null) {
        const [hit] = m;
        const i = m.index;

        if (i > lastIndex) {
          pushHighlightedText(
            chunk.slice(lastIndex, i),
            `txt-${globalOffset + lastIndex}-${globalOffset + i}`
          );
        }

        const isEmail = /^[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}$/i.test(hit);
        const isUrlWithScheme = /^(https?:\/\/|ftp:\/\/)/i.test(hit);
        const isWww = /^www\./i.test(hit);
        const digitsOnly = hit.replace(/\D/g, '');
        const hasPhoneSeparators = /[()+-]/.test(hit);
        const hasPlus = hit.includes('+');
        const looksLikeInn = (digitsOnly.length === 10 || digitsOnly.length === 12) && isValidInn(digitsOnly);
        const hasMoneyMarker = /(?:₽|руб\.?|р\.?)$/i.test(source.slice(globalOffset + i + hit.length, globalOffset + i + hit.length + 6))
          || /(?:^|\s)(?:₽|руб\.?|р\.?)\s*$/i.test(source.slice(Math.max(0, globalOffset + i - 8), globalOffset + i));
        const isRfPhoneByDigits = digitsOnly.length === 11 && (digitsOnly.startsWith('7') || digitsOnly.startsWith('8'));

        const isPhone =
          !isEmail &&
          !isUrlWithScheme &&
          !isWww &&
          !looksLikeInn &&
          !hasMoneyMarker &&
          (
            isRfPhoneByDigits
            || (hasPhoneSeparators && isRfPhoneByDigits)
            || (hasPlus && digitsOnly.length === 11 && digitsOnly.startsWith('7'))
          );

        if (looksLikeInn) {
          pushHighlightedText(
            hit,
            `inn-${globalOffset + i}-${globalOffset + i + hit.length}`
          );
          lastIndex = linkRe.lastIndex;
          continue;
        }

        if (!isEmail && !isUrlWithScheme && !isWww && !isPhone) {
          pushHighlightedText(
            hit,
            `txt-num-${globalOffset + i}-${globalOffset + i + hit.length}`
          );
          lastIndex = linkRe.lastIndex;
          continue;
        }

        let href = hit;

        if (isEmail) {
          href = `mailto:${hit}`;
        } else if (isPhone) {
          const normalized = hit.replace(/[^\d+]/g, '');
          href = `tel:${normalized}`;
        } else if (isWww) {
          href = `https://${hit}`;
        }

        parts.push(
          <a
            key={`lnk-${globalOffset + i}-${hit}`}
            href={href}
            target={isEmail || isPhone ? undefined : '_blank'}
            rel={isEmail || isPhone ? undefined : 'noopener noreferrer'}
            className={isPhone ? s.msgPhoneLink : s.msgLink}
            onClick={(e) => e.stopPropagation()}
          >
            {hit}
          </a>
        );

        lastIndex = linkRe.lastIndex;
      }

      if (lastIndex < chunk.length) {
        pushHighlightedText(
          chunk.slice(lastIndex),
          `txt-${globalOffset + lastIndex}-tail`
        );
      }
    }

    function pushChunkWithLinks(chunk, globalOffset) {
      let mdLast = 0;
      let mdMatch;
      mdLinkRe.lastIndex = 0;

      while ((mdMatch = mdLinkRe.exec(chunk)) !== null) {
        const mdStart = mdMatch.index;
        if (mdStart > mdLast) {
          pushChunkWithAutoLinks(chunk.slice(mdLast, mdStart), globalOffset + mdLast);
        }
        const label = mdMatch[1];
        const url = mdMatch[2];
        parts.push(
          <a
            key={`mdlnk-${globalOffset + mdStart}-${url}`}
            href={url}
            target={/^mailto:/i.test(url) ? undefined : '_blank'}
            rel={/^mailto:/i.test(url) ? undefined : 'noopener noreferrer'}
            className={s.msgLink}
            onClick={(e) => e.stopPropagation()}
          >
            {label}
          </a>
        );
        mdLast = mdLinkRe.lastIndex;
      }

      if (mdLast < chunk.length) {
        pushChunkWithAutoLinks(chunk.slice(mdLast), globalOffset + mdLast);
      }
    }

    let cursor = 0;
    let protectedMatch;

    while ((protectedMatch = protectedRe.exec(source)) !== null) {
      const protectedStart = protectedMatch.index;
      const protectedEnd = protectedStart + protectedMatch[0].length;

      if (protectedStart > cursor) {
        const chunk = source.slice(cursor, protectedStart);
        pushChunkWithLinks(chunk, cursor);
      }

      pushHighlightedText(
        source.slice(protectedStart, protectedEnd),
        `protected-${protectedStart}-${protectedEnd}`
      );

      cursor = protectedEnd;
    }

    if (cursor < source.length) {
      pushChunkWithLinks(source.slice(cursor), cursor);
    }

    return parts;
  }, [emojiSpriteIndex]);

  const renderTextWithAppleEmoji = React.useCallback((text) => {
    if (!text) return null;
    const source = String(text);
    const parts = [];
    let lastIndex = 0;
    let match;
    let emojiIdx = 0;
    EMOJI_CLUSTER_RE.lastIndex = 0;

    while ((match = EMOJI_CLUSTER_RE.exec(source)) !== null) {
      const emojiText = match[0];
      const emojiStart = match.index;
      if (emojiStart > lastIndex) parts.push(source.slice(lastIndex, emojiStart));

      const emojiSpriteStyle = resolveEmojiSpriteStyle(emojiSpriteIndex, emojiText);
      if (emojiSpriteStyle && !NO_SPRITE_EMOJI.has(emojiText)) {
        parts.push(
          <span
            key={`composer-emoji-${emojiIdx}-${emojiStart}`}
            role="img"
            aria-label={emojiText}
            className={`${s.inlineAppleEmojiSprite} ${s.composerInlineAppleEmoji}`}
            style={emojiSpriteStyle}
          />
        );
      } else {
        parts.push(emojiText);
      }

      lastIndex = emojiStart + emojiText.length;
      emojiIdx += 1;
    }

    if (lastIndex < source.length) parts.push(source.slice(lastIndex));
    return parts;
  }, [emojiSpriteIndex]);

  const abortPickedUpload = React.useCallback((itemId) => {
    if (itemId == null) return;
    const xhr = uploadXhrsRef.current.get(itemId);
    if (!xhr) return;
    uploadCancelledIdsRef.current.add(itemId);
    try { xhr.abort(); } catch { /* noop */ }
    uploadXhrsRef.current.delete(itemId);
  }, []);

  const removePickedItem = React.useCallback((itemId) => {
    if (itemId == null) return;
    if (uploading) uploadCancelledIdsRef.current.add(itemId);
    abortPickedUpload(itemId);
    setPickedItems((prev) => {
      const it = prev.find((x) => x.id === itemId);
      if (it?.previewUrl) {
        try { URL.revokeObjectURL(it.previewUrl); } catch { /* noop */ }
      }
      return prev.filter((x) => x.id !== itemId);
    });
    setUploadProgress((prev) => {
      if (!prev || prev[itemId] == null) return prev;
      const { [itemId]: _, ...rest } = prev;
      return rest;
    });
  }, [abortPickedUpload, uploading]);

  function handleClearPicked() {
    setPickedItems((prev) => {
      for (const it of prev) {
        if (uploading && it?.id) uploadCancelledIdsRef.current.add(it.id);
        abortPickedUpload(it?.id);
        if (it?.previewUrl) {
          try { URL.revokeObjectURL(it.previewUrl); } catch { /* noop */ }
        }
      }
      return [];
    });
    setUploadProgress({});
    const el = document.getElementById('chatFileInput');
    if (el) el.value = '';
  }

  function pickSupportedAudioMime() {
    // На iOS предпочтительно писать в m4a (audio/mp4).
    const candidates = IsIOS
      ? ['audio/mp4']
      : [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
      ];
    for (const t of candidates) {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported?.(t)) return t;
    }
    // Safari может не рекламировать поддержку — форсим mp4 на iOS
    return IsIOS ? 'audio/mp4' : '';
  }

  // SHORT-CLIP FILTER: точное измерение длительности blob
  async function getAudioDuration(blob) {
    let url = null;
    try {
      url = URL.createObjectURL(blob);
      const a = new Audio();
      a.preload = 'metadata';
      const dur = await new Promise((resolve) => {
        let settled = false;
        let timeoutId = null;
        const done = (v) => {
          if (settled) return;
          settled = true;
          if (timeoutId) clearTimeout(timeoutId);
          try { URL.revokeObjectURL(url); } catch { }
          try {
            a.removeAttribute('src');
            a.load?.();
          } catch { }
          resolve(v);
        };
        timeoutId = setTimeout(() => done(0), 1500);
        a.onloadedmetadata = () => done(isFinite(a.duration) ? a.duration : 0);
        a.onerror = () => done(0);
        a.src = url;
        try { a.load?.(); } catch { }
      });
      return dur || 0;
    } catch {
      if (url) {
        try { URL.revokeObjectURL(url); } catch { }
      }
      return 0;
    }
  }

  function resetRecordingUi() {
    isRecordingRef.current = false;
    isRecordingStartingRef.current = false;
    recStartedAtRef.current = null;
    setIsRecording(false);
    setRecStartAt(null);
    setRecSeconds(0);
  }

  function releaseMicStream() {
    try { mediaStreamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch { }
    mediaStreamRef.current = null;
  }

  function cancelPendingMicStart() {
    micSessionRef.current += 1;
    isRecordingStartingRef.current = false;
    stopRecording();
    releaseMicStream();
    if (!isRecordingRef.current) resetRecordingUi();
  }

  async function startRecording() {
    if (isUploadingCurrentChat || isRecordingRef.current || isRecordingStartingRef.current) return;
    if (!window.MediaRecorder || !navigator.mediaDevices?.getUserMedia) {
      showToast?.('Запись голосовых не поддерживается в этом браузере');
      return;
    }
    const session = micSessionRef.current + 1;
    micSessionRef.current = session;
    isRecordingStartingRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (session !== micSessionRef.current) {
        try { stream.getTracks().forEach((t) => t.stop()); } catch { }
        return;
      }
      mediaStreamRef.current = stream;
      audioChunksRef.current = [];
      const mime = pickSupportedAudioMime();
      let mr;
      try {
        mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      } catch {
        mr = new MediaRecorder(stream);
      }
      if (session !== micSessionRef.current) {
        try { stream.getTracks().forEach((t) => t.stop()); } catch { }
        return;
      }
      mediaRecorderRef.current = mr;

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        releaseMicStream();
        mediaRecorderRef.current = null;
        const chunks = audioChunksRef.current;
        const startedAt = recStartedAtRef.current;
        audioChunksRef.current = [];
        resetRecordingUi();
        if (!chunks.length) return;

        // Важно для iOS: корректный тип m4a, иначе blob может быть невалидным
        const preferType = IsIOS ? 'audio/mp4' : (mr.mimeType || 'audio/webm');
        const blob = new Blob(chunks, { type: preferType });

        // SHORT-CLIP FILTER: отбраковка записей < 1s без предупрежований/файла
        const soft = startedAt
          ? (Date.now() - startedAt) / 1000
          : 0;
        let duration = soft;
        const meta = await getAudioDuration(blob);
        if (meta > 0) duration = meta;
        if (duration < 0.95) {
          return;
        }

        const ext =
          (preferType?.includes('ogg') && 'ogg') ||
          (preferType?.includes('mp4') && 'm4a') ||
          'webm';
        const ts = new Date();
        const fname = `voice-${ts.getFullYear()}${String(ts.getMonth() + 1).padStart(2, '0')}${String(ts.getDate(),).padStart(2, '0')}-${String(ts.getHours()).padStart(2, '0')}${String(ts.getMinutes()).padStart(2, '0')}${String(ts.getSeconds()).padStart(2, '0')}.${ext}`;
        const file = new File([blob], fname, { type: preferType, lastModified: Date.now() });

        const targetChatId = activeChatIdRef.current;
        if (!targetChatId) {
          showToast?.('Не удалось прикрепить запись: чат не выбран');
          return;
        }
        setPickedItems((prev) => {
          const id = makeId();
          const kind = inferKindFromFile(file);
          const previewUrl = kind !== 'file' ? URL.createObjectURL(file) : null;
          return [...prev, { id, file, kind, previewUrl, chatId: targetChatId }];
        });
      };

      mr.start(250);
      isRecordingStartingRef.current = false;
      const startedAt = Date.now();
      recStartedAtRef.current = startedAt;
      isRecordingRef.current = true;
      setIsRecording(true);
      setRecStartAt(startedAt);
      setRecSeconds(0);
    } catch (e) {
      console.error('mic start failed', e);
      releaseMicStream();
      mediaRecorderRef.current = null;
      if (session === micSessionRef.current) resetRecordingUi();
      const denied = e?.name === 'NotAllowedError' || e?.name === 'PermissionDeniedError';
      showToast?.(denied ? 'Разрешите доступ к микрофону в настройках браузера' : 'Не удалось начать запись');
    }
  }

  function stopRecording() {
    const mr = mediaRecorderRef.current;
    if (!mr) return;
    try {
      if (mr.state === 'recording' || mr.state === 'paused') {
        try { mr.requestData(); } catch { /* ignore */ }
        mr.stop();
      }
    } catch { /* ignore */ }
  }

  function fmtRec(sec) {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  // таймер секунд во время записи
  React.useEffect(() => {
    if (!isRecording) return;
    const id = setInterval(() => {
      setRecSeconds(Math.floor((Date.now() - recStartAt) / 1000));
    }, 250);
    return () => clearInterval(id);
  }, [isRecording, recStartAt]);

  // останавливаем запись при размонтировании/закрытии
  React.useEffect(() => {
    if (!isOpen) {
      cancelPendingMicStart();
      handleClearPicked();
    }
  }, [isOpen]);

  // fullscreen image viewer state
  const [imageView, setImageView] = React.useState({ open: false, urls: [], thumbUrls: [], index: 0 });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deleteConfirmBusy, setDeleteConfirmBusy] = React.useState(false);
  const closeImageViewer = React.useCallback(() => {
    setImageView({ open: false, urls: [], thumbUrls: [], index: 0 });
  }, []);

  const openImageViewer = React.useCallback((urls, startIndex = 0, thumbUrls = []) => {
    const list = Array.isArray(urls) ? urls.filter(Boolean) : [];
    if (!list.length) return;
    const thumbs = Array.isArray(thumbUrls) && thumbUrls.length ? thumbUrls.filter(Boolean) : list;
    const safeIndex = Math.max(0, Math.min(startIndex, list.length - 1));
    setImageView({ open: true, urls: list, thumbUrls: thumbs, index: safeIndex });
  }, []);

  const [allChatImageUrls, setAllChatImageUrls] = React.useState([]);
  const [allChatChunkByUrl, setAllChatChunkByUrl] = React.useState({});

  React.useEffect(() => {
    let cancelled = false;
    const chatIdSafe = chatId || currentChat?.id;
    if (!chatIdSafe) {
      setAllChatImageUrls([]);
      setAllChatChunkByUrl({});
      return undefined;
    }

    const loadAllChatImages = async () => {
      const seen = new Set();
      const urls = [];
      const groupsByMessage = new Map();
      let beforeId = null;
      let guard = 0;
      try {
        while (!cancelled && guard < 500) {
          let url = `${API_URL}/chats/${chatIdSafe}/media?kind=image&limit=80`;
          if (beforeId) url += `&before_attachment_id=${beforeId}`;
          const r = await apiFetch(url);
          if (!r.ok) break;
          const data = await r.json();
          const items = Array.isArray(data?.items) ? data.items : [];
          for (const item of items) {
            if (!item?.s3_key) continue;
            const viewUrl = getViewUrl(item.s3_key);
            if (seen.has(viewUrl)) continue;
            seen.add(viewUrl);
            urls.push(viewUrl);

            const msgKey =
              item?.message_id
              ?? item?.messageId
              ?? item?.chat_message_id
              ?? item?.chatMessageId
              ?? `solo:${viewUrl}`;
            if (!groupsByMessage.has(msgKey)) groupsByMessage.set(msgKey, []);
            const attachmentOrderRaw =
              item?.attachment_id
              ?? item?.attachmentId
              ?? item?.id
              ?? item?.created_at
              ?? item?.timestamp
              ?? item?.uploaded_at
              ?? null;
            const attachmentOrderNum = Number(attachmentOrderRaw);
            groupsByMessage.get(msgKey).push({
              url: viewUrl,
              order: Number.isFinite(attachmentOrderNum) ? attachmentOrderNum : null,
            });
          }
          if (!data?.has_more || !data?.next_before_attachment_id) break;
          beforeId = data.next_before_attachment_id;
          guard += 1;
        }
      } catch {
        // ignore: в этом случае останемся на локально загруженных фото
      }
      if (!cancelled) {
        const chunkMap = {};
        groupsByMessage.forEach((chunkItems) => {
          const sortedChunkUrls = chunkItems
            .slice()
            .sort((a, b) => {
              if (a.order != null && b.order != null) return a.order - b.order;
              if (a.order != null) return -1;
              if (b.order != null) return 1;
              return 0;
            })
            .map((x) => x.url);
          for (const url of sortedChunkUrls) chunkMap[url] = sortedChunkUrls;
        });
        setAllChatImageUrls(urls);
        setAllChatChunkByUrl(chunkMap);
      }
    };

    loadAllChatImages();
    return () => { cancelled = true; };
  }, [chatId, currentChat?.id]);

  const globalImageUrls = React.useMemo(() => {
    if (!Array.isArray(chatMessages) || !chatMessages.length) return [];
    const urls = [];
    for (const msg of chatMessages) {
      if (!Array.isArray(msg?.attachments)) continue;
      for (const att of msg.attachments) {
        if (att?.kind !== 'image' || !att?.s3_key) continue;
        urls.push(getViewUrl(att.s3_key));
      }
    }
    return urls;
  }, [chatMessages]);

  const imageChunkByUrl = React.useMemo(() => {
    const map = new Map();
    if (allChatChunkByUrl && typeof allChatChunkByUrl === 'object') {
      Object.entries(allChatChunkByUrl).forEach(([url, chunk]) => {
        if (url && Array.isArray(chunk) && chunk.length) map.set(url, chunk);
      });
    }
    if (!Array.isArray(chatMessages) || !chatMessages.length) return map;
    for (const msg of chatMessages) {
      if (!Array.isArray(msg?.attachments)) continue;
      const chunkUrls = msg.attachments
        .filter((att) => att?.kind === 'image' && att?.s3_key)
        .map((att) => getViewUrl(att.s3_key));
      if (!chunkUrls.length) continue;
      for (const url of chunkUrls) {
        map.set(url, chunkUrls);
      }
    }
    return map;
  }, [chatMessages, allChatChunkByUrl]);

  const openImageFromGallery = React.useCallback((viewUrl, fallbackUrls = []) => {
    const fromAll = Array.isArray(allChatImageUrls) ? allChatImageUrls.filter(Boolean) : [];
    const fromGlobal = Array.isArray(globalImageUrls) ? globalImageUrls.filter(Boolean).slice().reverse() : [];
    const fromFallback = Array.isArray(fallbackUrls) ? fallbackUrls.filter(Boolean).slice().reverse() : [];
    const hasInAll = fromAll.includes(viewUrl);
    const hasInGlobal = fromGlobal.includes(viewUrl);

    const preferred = hasInAll
      ? fromAll
      : (hasInGlobal ? fromGlobal : fromFallback);
    const urls = preferred.length ? preferred : [viewUrl];
    if (!urls.length) return;
    const idx = urls.indexOf(viewUrl);
    openImageViewer(urls, idx >= 0 ? idx : 0, fallbackUrls);
  }, [allChatImageUrls, globalImageUrls, openImageViewer]);

  React.useEffect(() => {
    if (!imageView.open) return;
    if (!Array.isArray(allChatImageUrls) || !allChatImageUrls.length) return;
    const currentUrl = imageView.urls?.[imageView.index];
    if (!currentUrl) return;
    const currentInAll = allChatImageUrls.indexOf(currentUrl);
    if (currentInAll < 0) return;
    const sameList =
      imageView.urls.length === allChatImageUrls.length
      && imageView.urls.every((url, idx) => url === allChatImageUrls[idx]);
    if (sameList) return;
    setImageView((prev) => ({
      ...prev,
      urls: allChatImageUrls,
      index: currentInAll,
    }));
  }, [imageView.open, imageView.urls, imageView.index, allChatImageUrls]);

  React.useEffect(() => {
    if (!imageView.open) return;
    const currentUrl = imageView.urls?.[imageView.index];
    if (!currentUrl) return;
    const nextThumbs = imageChunkByUrl.get(currentUrl);
    if (!Array.isArray(nextThumbs) || !nextThumbs.length) return;
    const prevThumbs = Array.isArray(imageView.thumbUrls) ? imageView.thumbUrls : [];
    const isSame =
      prevThumbs.length === nextThumbs.length
      && prevThumbs.every((url, idx) => url === nextThumbs[idx]);
    if (isSame) return;
    setImageView((prev) => ({ ...prev, thumbUrls: nextThumbs }));
  }, [imageView.open, imageView.urls, imageView.index, imageView.thumbUrls, imageChunkByUrl]);

  React.useEffect(() => {
    if (!isOpen || !currentChat) return;
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CLEAR_BADGE_TAG',
        tag: `chat-${currentChat.id}`,
      });
    }
  }, [isOpen, currentChat?.id]);

  React.useEffect(() => {
    if (!deleteConfirmOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setDeleteConfirmOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [deleteConfirmOpen]);

  const handleConfirmDelete = React.useCallback(async () => {
    if (!onDelete || deleteConfirmBusy) return;
    setDeleteConfirmBusy(true);
    try {
      const res = onDelete();
      if (res && typeof res.then === 'function') await res;
    } finally {
      setDeleteConfirmBusy(false);
      setDeleteConfirmOpen(false);
    }
  }, [onDelete, deleteConfirmBusy]);

  // закрываем открытую картинку при закрытии модалки или смене чата
  React.useEffect(() => {
    closeImageViewer();
  }, [isOpen, currentChat?.id, closeImageViewer]);

  // ====== Стабильные media URL (без presigned GET) ======
  function getViewUrl(key, filename) {
    let k = (key || '').replace(/^\/+/, '');
    if (!k.startsWith('media/')) k = `media/${k}`;
    const base = `${API_URL}/media/${k}`;
    if (!filename) return base;
    return `${base}?download_name=${encodeURIComponent(filename)}`;
  }

  async function presignUploads(items) {
    const res = await apiFetch(`${API_URL}/s3/presign-uploads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(items),
    });
    if (!res.ok) throw new Error('presign failed');
    return res.json(); // [{s3_key,url,headers,method,ttl}]
  }

  // ---- Белые списки для клиентской фильтрации ----
  const EXT_WHITELIST = React.useMemo(() => new Set([
    '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.xlsm', '.txt',
    '.msg',
    '.pptx', '.ppsx',
    '.zip', '.7z', '.rar', '.tar', '.gz', '.bz2', '.xz',
    '.tar.gz', '.tar.bz2', '.tar.xz', '.tgz', '.tbz2', '.txz',
  ]), []);

  function isAllowedFile(f) {
    const t = (f.type || '').toLowerCase();
    if (/^image\//.test(t) || /^video\//.test(t) || /^audio\//.test(t)) return true;
    if (t === 'application/pdf') return true;
    if (t === 'application/vnd.ms-outlook') return true;
    if (t === 'application/msword') return true;
    if (t === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return true;
    if (t === 'application/vnd.ms-excel') return true;
    if (t === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return true;
    if (t === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') return true; // pptx
    if (t === 'application/vnd.openxmlformats-officedocument.presentationml.slideshow') return true;   // ppsx
    if (t === 'text/plain') return true;
    if ([
      'application/zip', 'application/x-zip-compressed',
      'application/x-7z-compressed',
      'application/vnd.rar', 'application/x-rar-compressed',
      'application/x-tar',
      'application/gzip', 'application/x-gzip',
      'application/x-bzip2', 'application/x-xz'
    ].includes(t)) return true;

    const name = (f.name || '').toLowerCase();
    const dot = name.lastIndexOf('.');
    if (dot !== -1) {
      const ext = name.slice(dot);
      if (EXT_WHITELIST.has(ext)) return true;
      for (const cx of EXT_WHITELIST) {
        if (cx.includes('.') && name.endsWith(cx)) return true;
      }
    }
    return false;
  }

  // ====== Пережим изображений перед upload (без изменения остальной логики) ======
  async function compressImageFile(file, opts = {}) {
    const {
      maxSide = 1920,
      quality = 0.82,
    } = opts;

    try {
      const type = (file?.type || '').toLowerCase();
      if (!type.startsWith('image/')) return file;

      // Декодируем изображение
      let bitmap = null;

      if (window.createImageBitmap) {
        try {
          // imageOrientation поддерживается не везде, поэтому try/catch
          bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
        } catch {
          bitmap = await createImageBitmap(file);
        }
      }

      let srcW = bitmap ? bitmap.width : 0;
      let srcH = bitmap ? bitmap.height : 0;

      // Fallback: HTMLImageElement (на случай, если createImageBitmap недоступен)
      if (!bitmap || !srcW || !srcH) {
        const url = URL.createObjectURL(file);
        const img = await new Promise((resolve, reject) => {
          const el = new Image();
          el.onload = () => resolve(el);
          el.onerror = (e) => reject(e);
          el.src = url;
        });
        try { URL.revokeObjectURL(url); } catch { }
        srcW = img.naturalWidth || img.width;
        srcH = img.naturalHeight || img.height;

        // Рисуем из <img> дальше
        const scale = Math.min(1, maxSide / Math.max(srcW, srcH));
        const dstW = Math.max(1, Math.round(srcW * scale));
        const dstH = Math.max(1, Math.round(srcH * scale));

        const canvas = document.createElement('canvas');
        canvas.width = dstW;
        canvas.height = dstH;
        const ctx = canvas.getContext('2d', { alpha: false });
        ctx.drawImage(img, 0, 0, dstW, dstH);

        const blob = await new Promise((resolve) => {
          canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
        });
        if (!blob) return file;

        const baseName = (file.name || 'image').replace(/\.[^.]+$/, '');
        const newName = `${baseName}.jpg`;
        return new File([blob], newName, { type: 'image/jpeg', lastModified: file.lastModified || Date.now() });
      }

      // createImageBitmap путь
      const scale = Math.min(1, maxSide / Math.max(srcW, srcH));
      const dstW = Math.max(1, Math.round(srcW * scale));
      const dstH = Math.max(1, Math.round(srcH * scale));

      const canvas = document.createElement('canvas');
      canvas.width = dstW;
      canvas.height = dstH;
      const ctx = canvas.getContext('2d', { alpha: false });
      ctx.drawImage(bitmap, 0, 0, dstW, dstH);

      const blob = await new Promise((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
      });

      try { bitmap.close?.(); } catch { }

      if (!blob) return file;

      const baseName = (file.name || 'image').replace(/\.[^.]+$/, '');
      const newName = `${baseName}.jpg`;
      return new File([blob], newName, { type: 'image/jpeg', lastModified: file.lastModified || Date.now() });
    } catch {
      // если что-то пошло не так — возвращаем оригинал, чтобы не ломать отправку
      return file;
    }
  }

  async function uploadToS3(files, chatId) {
    const queue = (files || []).filter((it) => {
      const id = it?.id;
      return !id || !uploadCancelledIdsRef.current.has(id);
    });
    if (!queue.length) return [];

    // пережимаем только изображения
    // ВНИМАНИЕ: чтобы показывать прогресс в %, используем XHR (fetch не даёт upload progress)
    const processed = [];
    for (const it of queue) {
      const id = it?.id;
      if (id && uploadCancelledIdsRef.current.has(id)) continue;
      const f = it?.file || it;
      const t = (f?.type || '').toLowerCase();
      const processedFile = t.startsWith('image/') ? await compressImageFile(f) : f;
      if (id && uploadCancelledIdsRef.current.has(id)) continue;
      processed.push({
        ...(typeof it === 'object' && it?.file ? it : { id: null, file: f, kind: null, previewUrl: null }),
        processedFile,
      });
    }
    if (!processed.length) return [];

    const items = processed.map((it) => {
      const f = it.processedFile;
      const type = (f?.type || '').toLowerCase();
      let kind = 'file';
      if (type.startsWith('image/')) kind = 'image';
      else if (type.startsWith('video/')) kind = 'video';
      else if (type.startsWith('audio/')) kind = 'audio';
      else if (
        type === 'application/pdf' ||
        type === 'application/msword' ||
        type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        type === 'application/vnd.ms-excel' ||
        type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        type === 'application/vnd.ms-excel.sheet.macroEnabled.12' ||
        type === 'text/plain'
      ) kind = 'file';

      return {
        filename: f.name,
        mime: f.type || 'application/octet-stream',
        kind,
        chat_id: chatId,
      };
    });

    const presigns = await presignUploads(items);

    function xhrUploadWithProgress(presign, file, onProgress, itemId) {
      return new Promise((resolve, reject) => {
        try {
          if (itemId && uploadCancelledIdsRef.current.has(itemId)) {
            reject(new Error('Upload aborted'));
            return;
          }
          const xhr = new XMLHttpRequest();
          if (itemId) uploadXhrsRef.current.set(itemId, xhr);
          xhr.open(presign.method || 'PUT', presign.url, true);

          const headers = presign.headers || { 'Content-Type': file.type || 'application/octet-stream' };
          for (const [k, v] of Object.entries(headers)) {
            try { xhr.setRequestHeader(k, v); } catch { }
          }

          xhr.upload.onprogress = (evt) => {
            if (itemId && uploadCancelledIdsRef.current.has(itemId)) return;
            if (!evt.lengthComputable) return;
            const pct = Math.max(0, Math.min(100, Math.round((evt.loaded / evt.total) * 100)));
            onProgress?.(pct);
          };

          const cleanup = () => {
            if (itemId) uploadXhrsRef.current.delete(itemId);
          };

          xhr.onload = () => {
            cleanup();
            if (itemId && uploadCancelledIdsRef.current.has(itemId)) {
              reject(new Error('Upload aborted'));
              return;
            }
            if (xhr.status >= 200 && xhr.status < 300) {
              onProgress?.(100);
              resolve();
            } else {
              reject(new Error(`Upload failed: ${xhr.status}`));
            }
          };
          xhr.onerror = () => {
            cleanup();
            reject(new Error('Upload failed: network error'));
          };
          xhr.onabort = () => {
            cleanup();
            reject(new Error('Upload aborted'));
          };

          xhr.send(file);
        } catch (e) {
          if (itemId) uploadXhrsRef.current.delete(itemId);
          reject(e);
        }
      });
    }

    const uploadOutcomes = await Promise.all(presigns.map(async (p, i) => {
      const it = processed[i];
      const f = it.processedFile;
      const id = it?.id;
      if (id && uploadCancelledIdsRef.current.has(id)) {
        return { ok: false, index: i, cancelled: true };
      }
      if (id) {
        setUploadProgress((prev) => ({ ...prev, [id]: prev[id] ?? 0 }));
      }
      try {
        await xhrUploadWithProgress(p, f, (pct) => {
          if (!id) return;
          setUploadProgress((prev) => (prev[id] === pct ? prev : { ...prev, [id]: pct }));
        }, id);
        if (id && uploadCancelledIdsRef.current.has(id)) {
          return { ok: false, index: i, cancelled: true };
        }
        return { ok: true, index: i };
      } catch (err) {
        if (id && uploadCancelledIdsRef.current.has(id)) {
          return { ok: false, index: i, cancelled: true };
        }
        throw err;
      }
    }));

    const failed = uploadOutcomes.filter((o) => !o.ok && !o.cancelled);
    if (failed.length) {
      throw new Error('Upload failed');
    }

    return uploadOutcomes
      .filter((o) => o.ok)
      .map((o) => {
      const p = presigns[o.index];
      const f = processed[o.index].processedFile;
      const type = (f?.type || '').toLowerCase();
      let kind = 'file';
      if (type.startsWith('image/')) kind = 'image';
      else if (type.startsWith('video/')) kind = 'video';
      else if (type.startsWith('audio/')) kind = 'audio';
      return {
        s3_key: p.s3_key,
        mime: f?.type || 'application/octet-stream',
        kind,
        size: f?.size,
        // для изображений после пережима имя/тип могли измениться (например, стало .jpg)
        original_filename: f?.name,
      };
    });
  }

  function addPickedFiles(files) {
    const targetChatId = activeChatId;
    if (!targetChatId) return;
    const allowed = (files || []).filter(isAllowedFile);
    if (!allowed.length) return;
    setPickedItems((prev) => {
      const next = [...prev];
      for (const f of allowed) {
        const id = makeId();
        const kind = inferKindFromFile(f);
        const previewUrl = kind !== 'file' ? URL.createObjectURL(f) : null;
        next.push({ id, file: f, kind, previewUrl, chatId: targetChatId });
      }
      return next;
    });
  }

  function handlePickFiles(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    addPickedFiles(files); // addPickedFiles фильтрует через isAllowedFile
    e.target.value = '';
  }

  function handlePaste(e) {
    if (isUploadingCurrentChat) return;
    const dt = e.clipboardData;
    if (!dt) return;

    const files = [];
    if (dt.items) {
      for (const item of dt.items) {
        if (item.kind === 'file') {
          const f = item.getAsFile?.();
          if (f) files.push(f);
        }
      }
    }

    // Вставили картинку/файл — добавляем как вложение и не вставляем "пустоту" в textarea
    const hasAllowed = files.some(isAllowedFile);
    if (hasAllowed) {
      e.preventDefault();
      addPickedFiles(files);
      return;
    }

    const html = dt.getData?.('text/html') || '';
    const plain = dt.getData?.('text/plain') || '';
    if (!html && !plain) return;

    const plainNormalized = (plain || '').replace(/\r\n/g, '\n');
    const fromHtml = html ? clipboardHtmlToPlainTextWithLinks(html) : '';
    let textToInsert = fromHtml || plainNormalized;
    if (!textToInsert) return;

    if (fromHtml && plainNormalized) {
      if (textToInsert.startsWith('\n') && !plainNormalized.startsWith('\n')) {
        textToInsert = textToInsert.replace(/^\n+/, '');
      }
      if (textToInsert.endsWith('\n') && !plainNormalized.endsWith('\n')) {
        textToInsert = textToInsert.replace(/\n+$/, '');
      }
    } else if (textToInsert.startsWith('\n') && (!plainNormalized || !plainNormalized.startsWith('\n'))) {
      textToInsert = textToInsert.replace(/^\n+/, '');
    }

    textToInsert = textToInsert.replace(/\r\n/g, '\n');
    if (!textToInsert) return;

    // Если есть HTML — извлекаем ссылки из разметки; иначе оставляем нативную вставку plain text
    if (!html) return;

    e.preventDefault();
    const ta = textareaRef.current;
    if (!ta) return;

    const currentValue = isEditingMessageId ? (editingText || '') : (newMessage || '');
    const onValueChange = isEditingMessageId ? setEditingText : setNewMessage;
    insertTextIntoTextarea(ta, textToInsert, currentValue, onValueChange);
  }

  function dragHasFiles(dt) {
    if (!dt) return false;
    const types = Array.from(dt.types || []);
    return types.includes('Files');
  }

  function handleChatDragEnter(e) {
    if (isUploadingCurrentChat || isEditingMessageId) return;
    if (!dragHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    chatDragCounterRef.current += 1;
    setChatDragActive(true);
  }

  function handleChatDragOver(e) {
    if (isUploadingCurrentChat || isEditingMessageId) return;
    if (!dragHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    try {
      e.dataTransfer.dropEffect = 'copy';
    } catch { }
    if (!chatDragActive) setChatDragActive(true);
  }

  function handleChatDragLeave(e) {
    if (!dragHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    chatDragCounterRef.current = Math.max(0, chatDragCounterRef.current - 1);
    if (chatDragCounterRef.current === 0) {
      setChatDragActive(false);
    }
  }

  function handleChatDrop(e) {
    if (!dragHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    chatDragCounterRef.current = 0;
    setChatDragActive(false);
    if (isUploadingCurrentChat || isEditingMessageId) return;
    const files = Array.from(e.dataTransfer?.files || []);
    if (!files.length) return;
    addPickedFiles(files);
  }

  async function handleSubmit(e) {
    e.preventDefault?.();

    if (isEditingMessageId) {
      const msg = messageById.get(String(isEditingMessageId));
      if (msg) {
        await onSaveEdit(msg);
      }
      return;
    }

    // если пришёл флаг из события — берём его, иначе используем стейт
    const forcedImportant = !!e?.forceImportant;
    const important = forcedImportant || isImportant;

    const submitChatId = activeChatId;
    if (!submitChatId) return;
    const submitItems = currentChatPickedItems;
    const submitItemIds = submitItems.map((it) => it?.id).filter(Boolean);

    try {
      let attachments = [];
      if (submitItems.length) {
        setUploadOwnerChatId(submitChatId);
        setUploading(true);
        setUploadProgress((prev) => {
          const base = { ...prev };
          for (const it of submitItems) base[it.id] = base[it.id] ?? 0;
          return base;
        });
        attachments = await uploadToS3(submitItems, submitChatId);
      }

      const text = (newMessage || '').trim();

      if (typeof onSendMessage === 'function') {
        if (attachments.length || text) {
          const payload = { content: text, attachments, is_important: important, chat_id: submitChatId };
          onSendMessage(payload, e);
        } else {
          return;
        }
      }

      if (attachments.length || text) {
        setPickedItems((prev) => {
          const next = [];
          for (const it of prev) {
            if (!submitItemIds.includes(it?.id)) {
              next.push(it);
              continue;
            }
            if (it?.previewUrl) {
              try { URL.revokeObjectURL(it.previewUrl); } catch { }
            }
          }
          return next;
        });
        const stillInSubmitChat = String(activeChatIdRef.current) === String(submitChatId);
        if (stillInSubmitChat) {
          setNewMessage('');
          setIsImportant(false);
        }
        setUploadProgress((prev) => {
          const next = { ...prev };
          for (const id of submitItemIds) delete next[id];
          return next;
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      for (const id of submitItemIds) {
        uploadCancelledIdsRef.current.delete(id);
        uploadXhrsRef.current.delete(id);
      }
      setUploading(false);
      setUploadOwnerChatId(null);
    }
  }

  const textareaRef = React.useRef(null);
  const composerPreviewRef = React.useRef(null);

  React.useEffect(() => {
    let raf = 0;
    raf = requestAnimationFrame(() => {
      clampChatToBottom('composer');
      const list = messagesContainerRef?.current;
      const composing = sendFormRef.current?.contains(document.activeElement);
      if (list?.isConnected) {
        const d = Math.max(0, list.scrollHeight - list.scrollTop - list.clientHeight);
        if ((d < CHAT_NEAR_BOTTOM_PX || composing) && !hasMoreNewer) onMessagesScroll?.(true);
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [newMessage, editingText, isEditingMessageId, clampChatToBottom, messagesContainerRef, onMessagesScroll, hasMoreNewer]);

  React.useEffect(() => {
    const el = messagesContainerRef?.current;
    if (!el || !isOpen || !currentChat) return;
    if (loadingOlder) return;
    const diff = Math.max(0, el.scrollHeight - el.scrollTop - el.clientHeight);
    const isNearBottom = diff < CHAT_NEAR_BOTTOM_PX || el.scrollHeight <= el.clientHeight + 1;

    if (isNearBottom && !hasMoreNewer) {
      onMessagesScroll?.(true);
    }
    // Плашка непрочитанного скрывается только при наборе текста или уходе из чата — не по скроллу
  }, [
    isOpen,
    currentChat?.id,
    chatMessages.length,
    loadingOlder,
    hasMoreNewer,
    messagesContainerRef,
    onMessagesScroll,
    currentChat
  ]);

  React.useEffect(() => {
    setHideUnreadDivider(false);
  }, [currentChat?.id, firstUnreadId]);

  // Скрываем плашку «Непрочитанные» как только пользователь начал печатать
  React.useEffect(() => {
    if (newMessage != null && String(newMessage).trim().length > 0 && firstUnreadId != null) {
      setHideUnreadDivider(true);
    }
  }, [newMessage, firstUnreadId]);

  const pinnedList = React.useMemo(() => {
    const arr = Array.isArray(pinnedMessages) ? [...pinnedMessages] : [];
    arr.sort((a, b) => {
      const ta = new Date(a?.timestamp || a?.created_at || 0).getTime();
      const tb = new Date(b?.timestamp || b?.created_at || 0).getTime();
      if (ta !== tb) return ta - tb;
      return Number(a?.id || 0) - Number(b?.id || 0);
    });
    return arr;
  }, [pinnedMessages]);

  const pinnedOrderKey = pinnedList.map((p) => String(p.id)).join('|');

  const pinnedStripSyncRef = React.useRef({ chatId: null, orderKey: null });
  const pinnedStripScrollSyncRafRef = React.useRef(0);

  const syncPinnedStripToScroll = React.useCallback(() => {
    const n = pinnedList.length;
    const box = messagesContainerRef?.current;
    if (!n || !box?.isConnected) return;
    if (scrollJumpLockRef.current) return;

    const boxRect = box.getBoundingClientRect();
    let lastVisibleMessageId = null;
    for (const item of box.querySelectorAll('[data-msg-id]')) {
      const r = item.getBoundingClientRect();
      if (r.bottom <= boxRect.top + 1 || r.top >= boxRect.bottom - 1) continue;
      lastVisibleMessageId = item.dataset.msgId ?? null;
    }
    if (lastVisibleMessageId == null) return;

    const visibleNum = Number(lastVisibleMessageId);
    if (!Number.isFinite(visibleNum)) return;

    let nextIndex = 0;
    for (let i = 0; i < n; i += 1) {
      const pinNum = Number(pinnedList[i]?.id);
      if (!Number.isFinite(pinNum)) continue;
      if (pinNum <= visibleNum) nextIndex = i;
      else break;
    }

    setPinnedStripIndex((prev) => (prev === nextIndex ? prev : nextIndex));
  }, [messagesContainerRef, pinnedList]);

  const schedulePinnedStripScrollSync = React.useCallback(() => {
    if (pinnedStripScrollSyncRafRef.current) return;
    pinnedStripScrollSyncRafRef.current = requestAnimationFrame(() => {
      pinnedStripScrollSyncRafRef.current = 0;
      syncPinnedStripToScroll();
    });
  }, [syncPinnedStripToScroll]);

  React.useEffect(() => () => {
    if (pinnedStripScrollSyncRafRef.current) {
      cancelAnimationFrame(pinnedStripScrollSyncRafRef.current);
      pinnedStripScrollSyncRafRef.current = 0;
    }
  }, []);

  React.useEffect(() => {
    const n = pinnedList.length;
    setPinnedStripIndex((i) => (n === 0 ? 0 : Math.min(Math.max(0, i), n - 1)));
  }, [pinnedList.length]);

  React.useEffect(() => {
    schedulePinnedStripScrollSync();
  }, [
    chatMessages.length,
    messagesAroundRevision,
    pinnedOrderKey,
    schedulePinnedStripScrollSync,
  ]);

  React.useEffect(() => {
    pinnedJumpSeqRef.current += 1;
    endScrollAnchorLock();
  }, [currentChat?.id, endScrollAnchorLock]);

  React.useEffect(() => {
    const cid = currentChat?.id ?? null;
    const key = pinnedOrderKey;
    const n = pinnedList.length;
    const r = pinnedStripSyncRef.current;
    if (n === 0) {
      setPinnedStripIndex(0);
      r.chatId = cid;
      r.orderKey = key;
      return;
    }
    if (r.chatId !== cid) {
      r.chatId = cid;
      r.orderKey = key;
      setPinnedStripIndex(n - 1);
      return;
    }
    if (r.orderKey !== key) {
      r.orderKey = key;
      setPinnedStripIndex(n - 1);
    }
  }, [currentChat?.id, pinnedOrderKey, pinnedList.length]);

  const formatPinnedPreview = React.useCallback((pm) => {
    if (!pm) return '';
    const att = Array.isArray(pm.attachments) ? pm.attachments : [];
    if (att.length) {
      const fn = (att[0]?.original_filename || '').trim();
      if (fn) return `📎 ${fn}${att.length > 1 ? ` +${att.length - 1}` : ''}`;
      return '📎 Вложение';
    }
    const t = String(pm.content || '').trim().replace(/\s+/g, ' ');
    if (!t) return 'Сообщение';
    return t.length > 80 ? `${t.slice(0, 80)}…` : t;
  }, []);

  const closePinnedFeed = React.useCallback(() => {
    setPinnedFeedOpen(false);
    setPinnedFeedItems([]);
    setPinnedFeedBulkBusy(false);
    setPinnedFeedUnpinConfirmOpen(false);
  }, []);

  const pinnedFeedRows = React.useMemo(() => {
    const src = Array.isArray(pinnedFeedItems) ? [...pinnedFeedItems] : [];
    /* Как в истории чата: сверху старее, внизу новее */
    src.sort((a, b) => {
      const ta = new Date(a?.timestamp || 0).getTime();
      const tb = new Date(b?.timestamp || 0).getTime();
      return ta - tb;
    });
    const rows = [];
    let lastDay = null;
    for (const m of src) {
      const ts = m?.timestamp;
      let dayKey = '';
      if (ts) {
        const d = new Date(ts);
        if (!Number.isNaN(d.getTime())) dayKey = d.toDateString();
      }
      if (dayKey && dayKey !== lastDay) {
        rows.push({ kind: 'date', key: `d-${dayKey}`, ts });
        lastDay = dayKey;
      }
      rows.push({ kind: 'msg', key: `m-${m.id}`, message: m });
    }
    return rows;
  }, [pinnedFeedItems]);

  const togglePinnedFeed = React.useCallback(async (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (pinnedFeedOpen) {
      closePinnedFeed();
      return;
    }
    if (!activeChatId) return;
    try {
      const r = await apiFetch(`${API_URL}/chats/${activeChatId}/pinned-messages`);
      if (!r.ok) return;
      const d = await r.json();
      const items = Array.isArray(d?.items) ? d.items : Array.isArray(d) ? d : [];
      setPinnedFeedItems(items);
      setPinnedFeedOpen(true);
    } catch {
      /* ignore */
    }
  }, [activeChatId, pinnedFeedOpen, closePinnedFeed]);

  const navigateToPinnedMessage = React.useCallback(async (messageId, opts = {}) => {
    if (messageId == null) return false;
    onMessagesScroll?.(false);
    const ok = await jumpToPinnedMessage(messageId);
    if (!ok) return false;
    const stripIndexAfter = opts?.stripIndexAfter;
    if (stripIndexAfter != null) {
      const safeIndex = Math.max(0, Math.min(Number(stripIndexAfter) || 0, pinnedList.length - 1));
      setPinnedStripIndex(safeIndex);
    } else {
      const idx = pinnedList.findIndex((p) => String(p.id) === String(messageId));
      if (idx >= 0) {
        setPinnedStripIndex(idx);
      }
    }
    return true;
  }, [jumpToPinnedMessage, onMessagesScroll, pinnedList]);

  const handlePinnedFeedPick = React.useCallback(async (row) => {
    const id = row?.id;
    if (id == null) return;
    closePinnedFeed();
    await navigateToPinnedMessage(id);
  }, [closePinnedFeed, navigateToPinnedMessage]);

  const handlePinnedFeedUnpinAll = React.useCallback(async () => {
    if (!canPinOrUnpin || !onUnpinMessage || pinnedFeedBulkBusy) return;
    const ids = pinnedFeedItems.map((x) => x.id).filter((x) => x != null);
    if (!ids.length) return;
    setPinnedFeedBulkBusy(true);
    try {
      for (const id of ids) {
        await onUnpinMessage(id);
      }
      closePinnedFeed();
    } catch (e) {
      showToast?.(e?.message || 'Не удалось открепить сообщения');
    } finally {
      setPinnedFeedBulkBusy(false);
    }
  }, [canPinOrUnpin, onUnpinMessage, pinnedFeedBulkBusy, pinnedFeedItems, closePinnedFeed, showToast]);

  React.useEffect(() => {
    if (!pinnedFeedOpen) return;
    const onKey = (ev) => {
      if (ev.key === 'Escape') {
        if (pinnedFeedUnpinConfirmOpen) {
          if (pinnedFeedBulkBusy) return;
          setPinnedFeedUnpinConfirmOpen(false);
          return;
        }
        closePinnedFeed();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pinnedFeedOpen, pinnedFeedUnpinConfirmOpen, pinnedFeedBulkBusy, closePinnedFeed]);

  React.useEffect(() => {
    if (!currentChat?.id) {
      closePinnedFeed();
    }
  }, [currentChat?.id, closePinnedFeed]);

  // ====== CLICK-TO-RECORD ======
  function onMicClick() {
    if (isUploadingCurrentChat) return;
    if (isRecordingStartingRef.current) {
      cancelPendingMicStart();
      return;
    }
    if (isRecordingRef.current) {
      stopRecording();
      return;
    }
    void startRecording();
  }

  // стоп при сворачивании/уходе со вкладки
  React.useEffect(() => {
    const onVis = () => { if (document.hidden && isRecordingRef.current) stopRecording(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const sendBtnRef = React.useRef(null);

  // ----- HOLD-TO-IMPORTANT: долгое удержание Send делает сообщение важным -----
  const sendHoldTimerRef = React.useRef(null);          // таймер автосенд (3 сек)
  const previewImportantTimerRef = React.useRef(null);  // таймер показа рамки (1 сек)

  const HOLD_IMPORTANT_MS = 1000;      // автоотправка
  const PREVIEW_IMPORTANT_MS = 500;   // показать красную рамку

  const startSendHold = React.useCallback(() => {
    // при новом удержании сначала сбрасываем флаг
    setIsImportant(false);

    // чистим старые таймеры, если были
    if (sendHoldTimerRef.current) {
      clearTimeout(sendHoldTimerRef.current);
      sendHoldTimerRef.current = null;
    }
    if (previewImportantTimerRef.current) {
      clearTimeout(previewImportantTimerRef.current);
      previewImportantTimerRef.current = null;
    }

    // через 1 секунду — просто краснеет рамка
    previewImportantTimerRef.current = setTimeout(() => {
      previewImportantTimerRef.current = null;

      const text = (newMessage || '').trim();
      const hasAttachments = currentChatPickedItems.length > 0;

      if (!text && !hasAttachments) return;

      setIsImportant(true); // включаем красную рамку
    }, PREVIEW_IMPORTANT_MS);

    // через 3 секунды — помечаем важным и отправляем
    sendHoldTimerRef.current = setTimeout(() => {
      sendHoldTimerRef.current = null;

      const text = (newMessage || '').trim();
      const hasAttachments = currentChatPickedItems.length > 0;

      if (!text && !hasAttachments) return;

      // визуально подсветим (если вдруг ещё не подсветилось)
      setIsImportant(true);

      // КЛЮЧЕВОЕ: явно говорим, что это важное сообщение
      handleSubmit({
        preventDefault: () => { },
        forceImportant: true,
      });
    }, HOLD_IMPORTANT_MS);

  }, [newMessage, currentChatPickedItems]);

  const cancelSendHold = React.useCallback((resetFlag = true) => {
    if (sendHoldTimerRef.current) {
      clearTimeout(sendHoldTimerRef.current);
      sendHoldTimerRef.current = null;
    }
    if (previewImportantTimerRef.current) {
      clearTimeout(previewImportantTimerRef.current);
      previewImportantTimerRef.current = null;
    }
    if (resetFlag) {
      setIsImportant(false);
    }
  }, []);

  const handleSendPointerDown = (e) => {
    if (sendDisabled || isUploadingCurrentChat) return;
    startSendHold();
  };

  // pointerup: не сбрасываем isImportant, чтобы, если таймер успел, флаг остался до submit
  const handleSendPointerUp = (e) => {
    cancelSendHold(false);
  };

  // отмена/уход пальца — сбрасываем всё
  const handleSendPointerCancel = (e) => {
    cancelSendHold(true);
  };

  const reactionPickerRef = React.useRef(null);
  const messageActionMenuRef = React.useRef(null);
  const messageActionAnchorRef = React.useRef({ x: null, y: null, messageId: null });
  const chipLongPressRef = React.useRef({ timer: null, fired: false });
  const reactionPickerLeaveTimerRef = React.useRef(null);
  const reactionMiniHoverOpenRef = React.useRef({ timer: null, message: null, el: null });
  const mobileReactionPressRef = React.useRef({
    timer: null,
    startX: 0,
    startY: 0,
    anchorX: 0,
    anchorY: 0,
  });
  const suppressFirstReactionPickerTapRef = React.useRef(false);
  const messageBubbleReactionsSummaryPressRef = React.useRef({ timer: null, startX: 0, startY: 0 });
  const messageRowMobilePressRef = React.useRef({ timer: null, startX: 0, startY: 0 });
  const suppressNextMessageRowClickRef = React.useRef(false);
  const suppressMessageRowClicksUntilRef = React.useRef(0);

  const [reactionPicker, setReactionPicker] = React.useState(null);
  const [messageActionMenu, setMessageActionMenu] = React.useState(null);
  const [fileMenuSelection, setFileMenuSelection] = React.useState(null);
  /** PDF-предпросмотр вложения (blob URL + метаданные для скачивания оригинала) */
  const [attachmentPdfPreview, setAttachmentPdfPreview] = React.useState(null);
  const attachmentPreviewProgressTimerRef = React.useRef(null);
  const [messageActionReactionsExpanded, setMessageActionReactionsExpanded] = React.useState(false);
  const reactionSparkleIdRef = React.useRef(0);
  const [reactionSparkleBursts, setReactionSparkleBursts] = React.useState([]);
  const reactionEmojiBurstIdRef = React.useRef(0);
  const [reactionEmojiBursts, setReactionEmojiBursts] = React.useState([]);
  const [reactionsDetail, setReactionsDetail] = React.useState(null);
  const [reactionsDetailLoading, setReactionsDetailLoading] = React.useState(false);
  const [reactionsDetailTab, setReactionsDetailTab] = React.useState(null);

  React.useEffect(() => {
    const releaseFirstTapGuard = () => {
      if (!suppressFirstReactionPickerTapRef.current) return;
      window.setTimeout(() => {
        suppressFirstReactionPickerTapRef.current = false;
      }, 0);
    };
    window.addEventListener('pointerup', releaseFirstTapGuard, true);
    window.addEventListener('pointercancel', releaseFirstTapGuard, true);
    return () => {
      window.removeEventListener('pointerup', releaseFirstTapGuard, true);
      window.removeEventListener('pointercancel', releaseFirstTapGuard, true);
    };
  }, []);

  const [reactionHoverDesktop, setReactionHoverDesktop] = React.useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(hover: hover) and (pointer: fine)').matches
      : false
  );

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const fn = () => setReactionHoverDesktop(mq.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);

  React.useEffect(() => () => {
    if (reactionPickerLeaveTimerRef.current) clearTimeout(reactionPickerLeaveTimerRef.current);
    const h = reactionMiniHoverOpenRef.current;
    if (h.timer) clearTimeout(h.timer);
    const b = messageBubbleReactionsSummaryPressRef.current;
    if (b.timer) clearTimeout(b.timer);
  }, []);

  const cancelReactionPickerCloseTimer = React.useCallback(() => {
    if (reactionPickerLeaveTimerRef.current) {
      clearTimeout(reactionPickerLeaveTimerRef.current);
      reactionPickerLeaveTimerRef.current = null;
    }
  }, []);

  const cancelReactionMiniHoverOpenTimer = React.useCallback(() => {
    const h = reactionMiniHoverOpenRef.current;
    if (h.timer) {
      clearTimeout(h.timer);
      h.timer = null;
    }
    h.message = null;
    h.el = null;
  }, []);

  const scheduleReactionPickerClose = React.useCallback(() => {
    cancelReactionPickerCloseTimer();
    reactionPickerLeaveTimerRef.current = window.setTimeout(() => {
      reactionPickerLeaveTimerRef.current = null;
      setReactionPicker(null);
    }, 220);
  }, [cancelReactionPickerCloseTimer]);

  const clearAttachmentPreviewProgressTimer = React.useCallback(() => {
    if (attachmentPreviewProgressTimerRef.current != null) {
      clearInterval(attachmentPreviewProgressTimerRef.current);
      attachmentPreviewProgressTimerRef.current = null;
    }
  }, []);

  const closeAttachmentPdfPreview = React.useCallback(() => {
    clearAttachmentPreviewProgressTimer();
    setAttachmentPdfPreview((prev) => {
      if (!prev) return prev;
      if (prev.pdfObjectUrl) {
        try { URL.revokeObjectURL(prev.pdfObjectUrl); } catch { /* ignore */ }
      }
      return null;
    });
  }, [clearAttachmentPreviewProgressTimer]);

  React.useEffect(() => {
    return () => {
      if (attachmentPreviewProgressTimerRef.current != null) {
        clearInterval(attachmentPreviewProgressTimerRef.current);
        attachmentPreviewProgressTimerRef.current = null;
      }
      setAttachmentPdfPreview((prev) => {
        if (prev?.pdfObjectUrl) {
          try { URL.revokeObjectURL(prev.pdfObjectUrl); } catch { /* ignore */ }
        }
        return null;
      });
    };
  }, []);

  React.useEffect(() => {
    closeAttachmentPdfPreview();
  }, [activeChatId, closeAttachmentPdfPreview]);

  React.useEffect(() => {
    if (!reactionPicker && !reactionsDetail && !messageActionMenu && !attachmentPdfPreview) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (attachmentPdfPreview) {
          closeAttachmentPdfPreview();
          return;
        }
        cancelReactionPickerCloseTimer();
        setReactionPicker(null);
        setReactionsDetail(null);
        setMessageActionMenu(null);
        setFileMenuSelection(null);
        setMessageActionReactionsExpanded(false);
        onCloseActionBar?.();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [reactionPicker, reactionsDetail, messageActionMenu, attachmentPdfPreview, closeAttachmentPdfPreview, cancelReactionPickerCloseTimer, onCloseActionBar]);

  React.useEffect(() => {
    if (!reactionPicker) return;
    const onDown = (e) => {
      if (reactionPickerRef.current && !reactionPickerRef.current.contains(e.target)) {
        cancelReactionPickerCloseTimer();
        setReactionPicker(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [reactionPicker, cancelReactionPickerCloseTimer]);

  React.useEffect(() => {
    if (multiSelectMode) {
      setMessageActionMenu(null);
      setMessageActionReactionsExpanded(false);
      return;
    }
    if (!actionBarOpen || selectedCount !== 1 || !selectedMessage) {
      setMessageActionMenu(null);
      setMessageActionReactionsExpanded(false);
      return;
    }
    const isMine = String(selectedMessage.sender_id) === String(myId);
    const anchor = messageActionAnchorRef.current;
    if (
      anchor
      && String(anchor.messageId) === String(selectedMessage.id)
      && Number.isFinite(anchor.x)
      && Number.isFinite(anchor.y)
    ) {
      setMessageActionMenu({ messageId: selectedMessage.id, x: anchor.x, y: anchor.y, isMine });
      return;
    }
    const row = document.getElementById(`msg-${selectedMessage.id}`)
      || document.getElementById(`pinned-feed-msg-${selectedMessage.id}`);
    if (!row) return;
    const rect = row.getBoundingClientRect();
    setMessageActionMenu({
      messageId: selectedMessage.id,
      x: isMine ? rect.right - 18 : rect.left + 18,
      y: rect.top + Math.min(56, Math.max(18, rect.height * 0.45)),
      isMine,
    });
  }, [actionBarOpen, selectedCount, selectedMessage, myId]);

  React.useEffect(() => {
    if (!messageActionMenu) return;
    const onDown = (e) => {
      if (messageActionMenuRef.current && !messageActionMenuRef.current.contains(e.target)) {
        // Любой второй клик закрывает меню; для клика по сообщению
        // гасим следующий onClick, чтобы меню не открылось снова сразу.
        suppressNextMessageRowClickRef.current = true;
        setMessageActionMenu(null);
        setMessageActionReactionsExpanded(false);
        onCloseActionBar?.();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [messageActionMenu, onCloseActionBar]);

  React.useEffect(() => {
    if (!messageActionMenu) {
      setFileMenuSelection(null);
    }
  }, [messageActionMenu]);

  const copySelectedTextFromMessageActionMenu = React.useCallback(async () => {
    if (typeof window === 'undefined' || !messageActionMenu?.messageId) return false;
    const selection = window.getSelection?.();
    if (!selection || selection.isCollapsed) return false;
    const selectedText = String(selection).trim();
    if (!selectedText) return false;
    if (selection.rangeCount < 1) return false;

    const row = document.getElementById(`msg-${messageActionMenu.messageId}`)
      || document.getElementById(`pinned-feed-msg-${messageActionMenu.messageId}`);
    if (!row) return false;

    const range = selection.getRangeAt(0);
    const selectedInsideMessage = row.contains(range.commonAncestorContainer);
    if (!selectedInsideMessage) return false;

    try {
      if (!navigator?.clipboard?.writeText) return false;
      await navigator.clipboard.writeText(selectedText);
      showToast?.('Выделенный текст скопирован');
      return true;
    } catch {
      return false;
    }
  }, [messageActionMenu, showToast]);

  const openReactionPickerForMessage = React.useCallback((message, clientX, clientY) => {
    if (!chatId) return;
    if (message.__optimistic || message.__failed || message.deleted_at) return;
    const sys = (message.sender_name || '').trim().toLowerCase() === 'система';
    if (sys) return;
    cancelReactionMiniHoverOpenTimer();
    cancelReactionPickerCloseTimer();
    const x = Number.isFinite(clientX) ? clientX : window.innerWidth / 2;
    const y = Number.isFinite(clientY) ? clientY : window.innerHeight / 2;
    setReactionPicker({ messageId: message.id, x, y });
  }, [chatId, cancelReactionMiniHoverOpenTimer, cancelReactionPickerCloseTimer]);

  const spawnReactionSparkles = React.useCallback((clientX, clientY) => {
    if (
      typeof window === 'undefined'
      || !Number.isFinite(clientX)
      || !Number.isFinite(clientY)
    ) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const id = ++reactionSparkleIdRef.current;
    const n = 10;
    const particles = Array.from({ length: n }, (_, i) => {
      const base = (i / n) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
      const dist = 34 + Math.random() * 32;
      return {
        key: `${id}-${i}`,
        dx: Math.cos(base) * dist,
        dy: Math.sin(base) * dist,
        delay: i * 0.018 + Math.random() * 0.05,
        glyph: ['✦', '★', '✧', '·'][(i + Math.floor(Math.random() * 2)) % 4],
      };
    });

    setReactionSparkleBursts((prev) => [...prev, { id, x: clientX, y: clientY, particles }]);
    window.setTimeout(() => {
      setReactionSparkleBursts((prev) => prev.filter((b) => b.id !== id));
    }, 720);
  }, []);

  const spawnReactionEmojiBurst = React.useCallback((emoji, x, y) => {
    if (
      typeof window === 'undefined'
      || !emoji
      || !Number.isFinite(x)
      || !Number.isFinite(y)
    ) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const id = ++reactionEmojiBurstIdRef.current;
    setReactionEmojiBursts((prev) => [...prev, { id, emoji, x, y }]);
    window.setTimeout(() => {
      setReactionEmojiBursts((prev) => prev.filter((b) => b.id !== id));
    }, 700);
  }, []);

  const resolveReactionBurstAnchor = React.useCallback((messageId, preferredPoint = null) => {
    if (typeof window === 'undefined') return null;

    if (
      preferredPoint
      && Number.isFinite(
        preferredPoint.x ?? preferredPoint.clientX
      )
      && Number.isFinite(
        preferredPoint.y ?? preferredPoint.clientY
      )
    ) {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const safe = 8;
      const xRaw = preferredPoint.x ?? preferredPoint.clientX;
      const yRaw = preferredPoint.y ?? preferredPoint.clientY;
      const x = Math.max(safe, Math.min(xRaw, vw - safe));
      const y = Math.max(safe, Math.min(yRaw, vh - safe));
      return { x, y };
    }

    // Если открыт popover этого сообщения — фиксируем точку эффекта в одном месте у триггера.
    if (reactionPicker && String(reactionPicker.messageId) === String(messageId)) {
      const pickerMessage = messageById.get(String(messageId));
      const isMinePickerMessage = String(pickerMessage?.sender_id) === String(myId);
      return {
        x: reactionPicker.x + (isMinePickerMessage ? -20 : 20),
        y: reactionPicker.y - 16,
      };
    }

    // Фоллбэк: фиксированная точка внутри пузыря сообщения (не от курсора).
    const row = document.getElementById(`msg-${messageId}`)
      || document.getElementById(`pinned-feed-msg-${messageId}`);
    const bubble = row?.querySelector?.(`.${s.messageBubble}`);
    const rect = bubble?.getBoundingClientRect?.();
    if (!rect) return null;
    const msg = messageById.get(String(messageId));
    const isMineMessage = String(msg?.sender_id) === String(myId);
    return {
      x: isMineMessage ? rect.right - 26 : rect.left + 26,
      y: rect.top + 18,
    };
  }, [reactionPicker, messageById, myId]);

  const toggleMessageReaction = React.useCallback(async (messageId, emoji, anchorPoint = null) => {
    if (!chatId || !messageId || !emoji) return;
    const em = String(emoji).trim();
    if (!isAllowedReaction(em)) {
      showToast?.('Эта реакция недоступна');
      return;
    }

    onSuppressReactionToast?.(messageId);

    const msg = messageById.get(String(messageId));
    const prevReactions = Array.isArray(msg?.reactions) ? msg.reactions : [];
    const prevR = prevReactions.find((r) => r.emoji === em);
    const hadMine = Boolean(prevR?.reacted_by_me);

    try {
      const res = await apiFetch(`${API_URL}/chats/${chatId}/messages/${messageId}/reactions/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emoji: em }),
      });
      if (!res.ok) {
        let detail = '';
        try {
          const j = await res.json();
          detail = typeof j?.detail === 'string' ? j.detail : '';
        } catch { /* ignore */ }
        showToast?.(detail || 'Не удалось изменить реакцию');
        return;
      }
      const data = await res.json();
      const nextReactions = Array.isArray(data.reactions) ? data.reactions : [];
      const nextR = nextReactions.find((r) => r.emoji === em);
      const hasMine = Boolean(nextR?.reacted_by_me);
      onPatchMessage?.(messageId, { reactions: nextReactions });
      onSuppressReactionToast?.(messageId);

      if (!hadMine && hasMine) {
        const anchor = resolveReactionBurstAnchor(messageId, anchorPoint);
        if (anchor) {
          spawnReactionEmojiBurst(em, anchor.x, anchor.y);
          spawnReactionSparkles(anchor.x, anchor.y);
        }
      }
    } catch (err) {
      showToast?.(err?.message || 'Ошибка сети');
    }
  }, [chatId, messageById, onPatchMessage, onSuppressReactionToast, showToast, resolveReactionBurstAnchor, spawnReactionEmojiBurst, spawnReactionSparkles]);

  const openReactionsSummary = React.useCallback(async (messageId, focusEmoji) => {
    if (!chatId || !messageId) return;
    setReactionsDetailLoading(true);
    setReactionsDetail({ messageId, reactions: null, focusEmoji: focusEmoji || null });
    try {
      const res = await apiFetch(`${API_URL}/chats/${chatId}/messages/${messageId}/reactions`);
      if (!res.ok) {
        setReactionsDetail(null);
        showToast?.('Не удалось загрузить реакции');
        return;
      }
      const data = await res.json();
      const list = Array.isArray(data.reactions) ? data.reactions : [];
      setReactionsDetail({ messageId, reactions: list, focusEmoji: focusEmoji || null });
      const fe = focusEmoji && list.some(r => r.emoji === focusEmoji)
        ? focusEmoji
        : (list[0]?.emoji ?? null);
      setReactionsDetailTab(fe);
    } catch {
      setReactionsDetail(null);
      showToast?.('Ошибка сети');
    } finally {
      setReactionsDetailLoading(false);
    }
  }, [chatId, showToast]);

  const onMobileReactionBubblePointerDown = React.useCallback((e, message) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    if (e.target?.closest?.('[data-chat-selectable]')) return;
    const interactiveTarget = e.target?.closest?.(
      `a[href],button,input,textarea,select,label,video,audio,.${s.messageReactionsRow},.${s.reactionChip},.${s.reactionChipEmojiWrap},.${s.reactionChipCount}`,
    );
    if (interactiveTarget) return;
    const r = mobileReactionPressRef.current;
    const rect = e.currentTarget?.getBoundingClientRect?.();
    r.startX = e.clientX;
    r.startY = e.clientY;
    r.anchorX = e.clientX || (rect ? rect.left + rect.width / 2 : 0);
    r.anchorY = e.clientY || (rect ? rect.top : 0);
    if (r.timer) clearTimeout(r.timer);
    r.timer = window.setTimeout(() => {
      r.timer = null;
      suppressNextMessageRowClickRef.current = true;
      suppressMessageRowClicksUntilRef.current = Date.now() + 900;
      suppressFirstReactionPickerTapRef.current = true;
      setReactionPicker(null);
      setReactionsDetail(null);
      onEnableMultiSelect?.(message);
    }, 520);
  }, [onEnableMultiSelect]);

  const onDesktopBubbleReactionsSummaryPointerDown = React.useCallback((e, message) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    const r = messageBubbleReactionsSummaryPressRef.current;
    r.startX = e.clientX;
    r.startY = e.clientY;
    if (r.timer) clearTimeout(r.timer);
    r.timer = window.setTimeout(() => {
      r.timer = null;
      suppressNextMessageRowClickRef.current = true;
      openReactionsSummary(message.id, null);
    }, 450);
  }, [openReactionsSummary]);

  const onDesktopBubbleReactionsSummaryPointerMove = React.useCallback((e) => {
    const r = messageBubbleReactionsSummaryPressRef.current;
    if (!r.timer) return;
    if (Math.hypot(e.clientX - r.startX, e.clientY - r.startY) > 12) {
      clearTimeout(r.timer);
      r.timer = null;
    }
  }, []);

  const onDesktopBubbleReactionsSummaryPointerEnd = React.useCallback(() => {
    const r = messageBubbleReactionsSummaryPressRef.current;
    if (r.timer) {
      clearTimeout(r.timer);
      r.timer = null;
    }
  }, []);

  const onMobileReactionBubblePointerMove = React.useCallback((e) => {
    const r = mobileReactionPressRef.current;
    if (!r.timer) return;
    if (Math.hypot(e.clientX - r.startX, e.clientY - r.startY) > 12) {
      clearTimeout(r.timer);
      r.timer = null;
    }
  }, []);

  const onMobileReactionBubblePointerEnd = React.useCallback(() => {
    const r = mobileReactionPressRef.current;
    if (r.timer) {
      clearTimeout(r.timer);
      r.timer = null;
    }
  }, []);

  const onMobileMessageRowPointerDown = React.useCallback((e, message) => {
    if (reactionHoverDesktop || multiSelectMode) return;
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    if (e.target?.closest?.('[data-chat-selectable]')) return;
    const interactiveTarget = e.target?.closest?.('a,button,input,textarea,select,label,video,audio');
    if (interactiveTarget) return;
    const r = messageRowMobilePressRef.current;
    r.startX = e.clientX;
    r.startY = e.clientY;
    if (r.timer) clearTimeout(r.timer);
    r.timer = window.setTimeout(() => {
      r.timer = null;
      suppressNextMessageRowClickRef.current = true;
      suppressMessageRowClicksUntilRef.current = Date.now() + 900;
      setReactionPicker(null);
      setMessageActionMenu(null);
      setReactionsDetail(null);
      onEnableMultiSelect?.(message);
    }, 520);
  }, [multiSelectMode, onEnableMultiSelect, reactionHoverDesktop]);

  const onMobileMessageRowPointerMove = React.useCallback((e) => {
    const r = messageRowMobilePressRef.current;
    if (!r.timer) return;
    if (Math.hypot(e.clientX - r.startX, e.clientY - r.startY) > 12) {
      clearTimeout(r.timer);
      r.timer = null;
    }
  }, []);

  const onMobileMessageRowPointerEnd = React.useCallback(() => {
    const r = messageRowMobilePressRef.current;
    if (r.timer) {
      clearTimeout(r.timer);
      r.timer = null;
    }
  }, []);

  const onDesktopReactionMiniEnter = React.useCallback((e, message) => {
    cancelReactionMiniHoverOpenTimer();
    const el = e.currentTarget;
    const h = reactionMiniHoverOpenRef.current;
    h.message = message;
    h.el = el;
    h.timer = window.setTimeout(() => {
      h.timer = null;
      const msg = h.message;
      const btn = h.el;
      h.message = null;
      h.el = null;
      if (!msg || !btn || !document.contains(btn)) return;
      const r = btn.getBoundingClientRect();
      openReactionPickerForMessage(msg, r.left + r.width / 2, r.top);
    }, REACTION_MINI_HOVER_OPEN_MS);
  }, [cancelReactionMiniHoverOpenTimer, openReactionPickerForMessage]);

  const onDesktopReactionMiniLeave = React.useCallback(() => {
    cancelReactionMiniHoverOpenTimer();
  }, [cancelReactionMiniHoverOpenTimer]);

  const onDesktopReactionMiniClick = React.useCallback((e, message) => {
    e.stopPropagation();
    cancelReactionMiniHoverOpenTimer();
    cancelReactionPickerCloseTimer();
    const r = e.currentTarget.getBoundingClientRect();
    openReactionPickerForMessage(message, r.left + r.width / 2, r.top);
  }, [cancelReactionMiniHoverOpenTimer, cancelReactionPickerCloseTimer, openReactionPickerForMessage]);

  const onReactionChipPointerDown = React.useCallback((e, messageId, emoji) => {
    e.stopPropagation();
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    const ref = chipLongPressRef.current;
    ref.fired = false;
    if (ref.timer) clearTimeout(ref.timer);
    ref.timer = window.setTimeout(() => {
      ref.timer = null;
      ref.fired = true;
      openReactionsSummary(messageId, emoji);
    }, 450);
  }, [openReactionsSummary]);

  const onReactionChipPointerUp = React.useCallback((e, messageId, emoji) => {
    e.stopPropagation();
    if (e.button !== 0 && e.pointerType === 'mouse') {
      const ref = chipLongPressRef.current;
      if (ref.timer) {
        clearTimeout(ref.timer);
        ref.timer = null;
      }
      ref.fired = false;
      return;
    }
    const ref = chipLongPressRef.current;
    if (ref.timer) {
      clearTimeout(ref.timer);
      ref.timer = null;
    }
    if (!ref.fired) {
      toggleMessageReaction(messageId, emoji, { clientX: e.clientX, clientY: e.clientY });
    }
    ref.fired = false;
  }, [toggleMessageReaction]);

  const onReactionChipPointerCancel = React.useCallback(() => {
    const ref = chipLongPressRef.current;
    if (ref.timer) {
      clearTimeout(ref.timer);
      ref.timer = null;
    }
    ref.fired = false;
  }, []);

  const downloadAttachment = React.useCallback(async (previewUrl, downloadUrl, fileName) => {
    const sourceUrl = downloadUrl || previewUrl;
    if (!sourceUrl) {
      showToast?.('Не удалось скачать файл');
      return;
    }
    await openFileUrl(sourceUrl, fileName || 'file');
  }, [showToast]);

  const openAttachmentPdfPreview = React.useCallback(async ({
    chatId: chatIdArg,
    attachmentId,
    downloadUrl,
    fileName,
  }) => {
    const safeFileName = String(fileName || '').trim();
    const isZipAttachment = /\.zip$/i.test(safeFileName);
    if (isZipAttachment) {
      const sourceUrl = downloadUrl || null;
      if (!sourceUrl) {
        showToast?.('Не удалось скачать файл');
        return;
      }
      await openFileUrl(sourceUrl, safeFileName || 'file');
      return;
    }

    const cid = Number(chatIdArg ?? activeChatId);
    const aid = Number(attachmentId);
    if (!Number.isFinite(cid) || !Number.isFinite(aid)) {
      showToast?.('Не удалось открыть предпросмотр');
      return;
    }

    const startPreviewPercentTicker = () => {
      if (attachmentPreviewProgressTimerRef.current != null) {
        clearInterval(attachmentPreviewProgressTimerRef.current);
        attachmentPreviewProgressTimerRef.current = null;
      }
      attachmentPreviewProgressTimerRef.current = setInterval(() => {
        setAttachmentPdfPreview((p) => {
          if (!p?.loading) {
            if (attachmentPreviewProgressTimerRef.current != null) {
              clearInterval(attachmentPreviewProgressTimerRef.current);
              attachmentPreviewProgressTimerRef.current = null;
            }
            return p;
          }
          const cap = p.loadPhase === 'download' ? 96 : 86;
          const cur = Number(p.loadPercent) || 0;
          if (cur >= cap) return p;
          const step = cap - cur > 14 ? 3 : 2;
          return { ...p, loadPercent: Math.min(cur + step, cap) };
        });
      }, 105);
    };

    clearAttachmentPreviewProgressTimer();
    setAttachmentPdfPreview((prev) => {
      if (prev?.pdfObjectUrl) {
        try { URL.revokeObjectURL(prev.pdfObjectUrl); } catch { /* ignore */ }
      }
      return {
        loading: true,
        error: null,
        fileName: fileName || 'file',
        downloadUrl,
        pdfDirectUrl: null,
        pdfObjectUrl: null,
        loadPhase: 'prepare',
        loadPercent: 0,
      };
    });
    startPreviewPercentTicker();

    try {
      const prep = await apiFetch(`${API_URL}/chats/${cid}/attachments/${aid}/preview`, { method: 'POST' });
      if (prep.status === 415) {
        clearAttachmentPreviewProgressTimer();
        setAttachmentPdfPreview((prev) => (prev ? {
          ...prev,
          loading: false,
          error: 'unsupported',
          pdfDirectUrl: null,
          pdfObjectUrl: null,
        } : prev));
        return;
      }
      if (prep.status === 504) {
        clearAttachmentPreviewProgressTimer();
        setAttachmentPdfPreview((prev) => (prev ? {
          ...prev,
          loading: false,
          error: 'timeout',
          pdfDirectUrl: null,
          pdfObjectUrl: null,
        } : prev));
        return;
      }
      if (!prep.ok) {
        clearAttachmentPreviewProgressTimer();
        setAttachmentPdfPreview((prev) => (prev ? {
          ...prev,
          loading: false,
          error: 'failed_prepare',
          pdfDirectUrl: null,
          pdfObjectUrl: null,
        } : prev));
        return;
      }
      const data = await prep.json().catch(() => null);
      if (!data || data.status !== 'ready' || !data.preview_url) {
        clearAttachmentPreviewProgressTimer();
        setAttachmentPdfPreview((prev) => (prev ? {
          ...prev,
          loading: false,
          error: 'failed_payload',
          pdfDirectUrl: null,
          pdfObjectUrl: null,
        } : prev));
        return;
      }
      const pdfHref = resolveBackendAbsoluteUrl(data.preview_url);
      clearAttachmentPreviewProgressTimer();
      setAttachmentPdfPreview((prev) => {
        if (!prev?.loading) return prev;
        return {
          ...prev,
          loadPhase: 'download',
          loadPercent: Math.max(Number(prev.loadPercent) || 0, 88),
          pdfDirectUrl: pdfHref,
        };
      });
      startPreviewPercentTicker();

      const pdfRes = await apiFetch(pdfHref, { method: 'GET' });
      if (!pdfRes.ok) {
        clearAttachmentPreviewProgressTimer();
        setAttachmentPdfPreview((prev) => (prev ? {
          ...prev,
          loading: false,
          error: 'failed_download',
          pdfObjectUrl: null,
        } : prev));
        return;
      }
      const blob = await pdfRes.blob();
      clearAttachmentPreviewProgressTimer();
      const pdfObjectUrl = URL.createObjectURL(blob.type ? blob : new Blob([blob], { type: 'application/pdf' }));
      setAttachmentPdfPreview((prev) => (prev ? {
        ...prev,
        loading: false,
        error: null,
        pdfDirectUrl: pdfHref,
        pdfObjectUrl,
      } : prev));
    } catch (e) {
      console.error('[ChatThread] attachment preview failed', e);
      clearAttachmentPreviewProgressTimer();
      setAttachmentPdfPreview((prev) => (prev ? {
        ...prev,
        loading: false,
        error: prev?.loadPhase === 'download' ? 'failed_download' : 'failed_network',
        pdfDirectUrl: null,
        pdfObjectUrl: null,
      } : prev));
    }
  }, [activeChatId, showToast, clearAttachmentPreviewProgressTimer]);

  const resolveContextFileMime = React.useCallback((source) => {
    if (!source || !selectedMessage) return null;
    const attId = source.attachmentId;
    if (attId == null) return source.mime || null;
    const att = (selectedMessage.attachments || []).find((a) => {
      const id = a?.id ?? a?.attachment_id;
      return String(id) === String(attId);
    });
    return att?.mime || source.mime || null;
  }, [selectedMessage]);

  const handleShareContextFile = React.useCallback((source) => {
    if (!source?.downloadUrl) {
      showToast?.('Не удалось получить файл');
      return;
    }
    runShareAttachmentAction(
      {
        downloadUrl: source.downloadUrl,
        fileName: source.fileName,
        mime: resolveContextFileMime(source),
      },
      (err) => {
        reportShareError(err, {
          downloadUrl: source.downloadUrl,
          fileName: source.fileName,
        });
      },
    );
  }, [resolveContextFileMime, showToast]);

  if (notReady) return null;

  const editingMessage = isEditingMessageId
    ? (messageById.get(String(isEditingMessageId)) || null)
    : null;
  const composerValue = isEditingMessageId ? editingText : newMessage;
  const composerPlaceholder = isUploadingCurrentChat
    ? 'Загрузка файлов…'
    : (isEditingMessageId ? 'Измените сообщение…' : 'Сообщение…');
  const sendDisabled = isEditingMessageId
    ? (!editingText || !editingText.trim()) || isUploadingCurrentChat
    : (((!newMessage || !newMessage.trim()) && currentChatPickedItems.length === 0) || isUploadingCurrentChat);
  const textareaRequired = !isEditingMessageId && currentChatPickedItems.length === 0;
  const composerText = String(composerValue || '');
  let composerPreviewEnabled = false;
  if (composerText && emojiSpriteIndex?.size) {
    EMOJI_CLUSTER_RE.lastIndex = 0;
    let match;
    while ((match = EMOJI_CLUSTER_RE.exec(composerText)) !== null) {
      if (resolveEmojiSpriteStyle(emojiSpriteIndex, match[0])) {
        composerPreviewEnabled = true;
        break;
      }
    }
  }

  // ---------- иконки для файлового вложения ----------
  const FILE_ICON_BY_EXT = {
    pdf: '📕', // PDF
    doc: '📝', docx: '📝',
    xls: '📊', xlsx: '📊', xlsm: '📊', csv: '📊',
    pptx: '🖥️', ppsx: '🖥️',
    txt: '📄', md: '📄',
    zip: '🗂️', '7z': '🗂️', rar: '🗂️', tar: '🗂️', gz: '🗂️', bz2: '🗂️', xz: '🗂️', tgz: '🗂️', tbz2: '🗂️', txz: '🗂️',
    default: '📎',
  };

  function extractFilename(keyOrName) {
    try {
      const p = (keyOrName || '').split('/').pop() || 'file';
      return decodeURIComponent(p);
    } catch { return 'file'; }
  }

  function humanSize(bytes) {
    if (bytes === 0) return '0 Б';
    if (!bytes && bytes !== 0) return '';
    const units = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'];
    let i = 0, n = bytes;
    while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
    const str = n < 10 && i ? n.toFixed(1) : Math.round(n).toString();
    return str.replace('.', ',') + ' ' + units[i];
  }

  function inferExtFromName(name) {
    const lower = (name || '').toLowerCase();
    // составные архивы сначала
    const complex = ['.tar.gz', '.tar.bz2', '.tar.xz', '.tgz', '.tbz2', '.txz'];
    for (const cx of complex) if (lower.endsWith(cx)) return cx.replace('.', '');
    const idx = lower.lastIndexOf('.');
    return idx >= 0 ? lower.slice(idx + 1) : '';
  }

  function fileIcon(att) {
    const name = extractFilename(att.s3_key);
    const ext = inferExtFromName(name);
    return FILE_ICON_BY_EXT[ext] || FILE_ICON_BY_EXT.default;
  }

  /** Вложения в экране «все закреплённые» (без отдельного портала) */
  const renderPinnedFeedAttachments = (message, { isGroup: feedIsGroup = false, isMine: feedIsMine = false } = {}) => {
    const attachments = Array.isArray(message.attachments) ? message.attachments : [];
    if (!attachments.length) return null;
    const imageAttachments = attachments.filter((a) => a?.kind === 'image');
    const imageUrls = imageAttachments.map((a) => getViewUrl(a.s3_key));
    const mediaW = getChatMediaPreviewWidth({
      isGroup: feedIsGroup,
      isMine: feedIsMine,
      attachments,
    });

    return (
      <div
        className={s.pinnedFeedAttachments}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {attachments.map((att) => {
          const viewUrl = getViewUrl(att.s3_key);
          const { dimW, dimH, hasMediaDims } = getChatAttachmentDims(
            att,
            viewUrl,
            knownImageDimensionsByUrl,
          );
          const imageFrameStyle = buildChatSingleImageFrameStyle(mediaW, dimW, dimH);
          const videoFrameStyle = buildChatSingleVideoFrameStyle(mediaW, dimW, dimH);

          if (att.kind === 'image') {
            return (
              <div key={att.id || att.s3_key} style={imageFrameStyle}>
                <img
                  src={viewUrl}
                  alt=""
                  loading="lazy"
                  width={hasMediaDims ? dimW : undefined}
                  height={hasMediaDims ? dimH : undefined}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    cursor: 'zoom-in',
                  }}
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    rememberImageNaturalSize(viewUrl, img.naturalWidth, img.naturalHeight);
                    onChatMediaLoaded();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openImageFromGallery(viewUrl, imageUrls);
                  }}
                />
              </div>
            );
          }

          if (att.kind === 'video') {
            return (
              <div key={att.id || att.s3_key} style={videoFrameStyle}>
                <ChatInlineVideo src={viewUrl} onLoadedMetadata={onChatMediaLoaded} />
              </div>
            );
          }

          if (att.kind === 'audio') {
            const audioFileName = att.original_filename
              || extractFilename(att.s3_key);
            return (
              <div key={att.id || att.s3_key} className={s.audioMessage}>
                <AudioWaveform src={viewUrl} filename={audioFileName} onLoadedMetadata={undefined} />
              </div>
            );
          }

          const fileName =
            att.original_filename
            || extractFilename(att.s3_key);
          const previewUrl = getViewUrl(att.s3_key, fileName);
          const attachmentId = att?.id ?? att?.attachment_id ?? null;
          const downloadUrl = getAttachmentDownloadUrl(activeChatId, attachmentId) || previewUrl;
          const icon = fileIcon(att);
          return (
            <div
              key={att.id || att.s3_key}
              style={{
                display: 'flex',
                alignItems: 'stretch',
                gap: 8,
                maxWidth: '100%',
                width: '100%',
              }}
            >
              <button
                type="button"
                data-chat-file-preview="true"
                onClick={(e) => {
                  e.stopPropagation();
                  const modifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1;
                  if (modifiedClick) return;
                  e.preventDefault();
                  setFileMenuSelection(null);
                  openAttachmentPdfPreview({
                    chatId: activeChatId,
                    attachmentId,
                    downloadUrl,
                    fileName,
                  });
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setFileMenuSelection({
                    messageId: message.id,
                    downloadUrl,
                    fileName,
                    attachmentId,
                  });
                  messageActionAnchorRef.current = {
                    x: e.clientX,
                    y: e.clientY,
                    messageId: message.id,
                  };
                  setMessageActionReactionsExpanded(false);
                  onMessageClick?.(message);
                }}
                title={`Предпросмотр: ${fileName}${att.mime ? `\n${att.mime}` : ''}${att.size ? ` • ${humanSize(att.size)}` : ''}`}
                style={{
                  display: 'flex', gap: 12, alignItems: 'center',
                  padding: '10px 12px',
                  borderRadius: 12, textDecoration: 'none', background: '#E9C6FE',
                  maxWidth: '100%',
                  border: '2.5px solid #BD23DB',
                  color: '#111',
                  flex: 1,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 36, height: 36, borderRadius: 18, display: 'grid', placeItems: 'center',
                    background: '#BD23DB', fontSize: 20, flex: '0 0 36px',
                  }}
                >{icon}
                </span>
                <span style={{ minWidth: 0, display: 'grid', flex: 1 }}>
                  <span style={{
                    fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden',
                    textOverflow: 'ellipsis', maxWidth: 260,
                  }}>{fileName}
                  </span>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>
                    {att.size ? humanSize(att.size) : (att.mime || 'file')}
                  </span>
                </span>
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  const reactionPickerIsMine = Boolean(
    reactionPicker
    && String(messageById.get(String(reactionPicker.messageId))?.sender_id) === String(myId),
  );
  const pickerSelectedReactions = (() => {
    if (!reactionPicker) return new Set();
    const msg = messageById.get(String(reactionPicker.messageId));
    const list = Array.isArray(msg?.reactions) ? msg.reactions : [];
    return new Set(list.filter((r) => r.reacted_by_me).map((r) => r.emoji));
  })();
  /** Высота якоря (кнопка «реакция» `.reactionMiniBtn`: 30px) для открытия панели снизу */
  const REACTION_MINI_BTN_H = 30;
  const reactionPickerPos = reactionPicker && typeof window !== 'undefined'
    ? (() => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const safe = 8;
      const popoverEstW = Math.min(360, Math.max(220, vw - safe * 2));
      const isLastInThread = Boolean(
        chatMessages.length
        && String(chatMessages[chatMessages.length - 1]?.id) === String(reactionPicker.messageId),
      );
      // Меньше зазор у последнего сообщения — панель ближе к пузырю; реальная высота ≈ padding + max-height из CSS
      const gapAboveAnchor = isLastInThread ? 50 : 52;
      const estH = Math.min(296, Math.max(120, 12 + Math.min(280, vh * 0.42)));
      let top = reactionPicker.y - gapAboveAnchor;
      if (top + estH > vh - safe) {
        const gapBelow = 8;
        const belowTop = reactionPicker.y + REACTION_MINI_BTN_H + gapBelow;
        if (belowTop + estH <= vh - safe) {
          top = belowTop;
        } else {
          top = vh - safe - estH;
        }
      }
      top = Math.max(safe, top);
      const maxL = Math.max(safe, vw - popoverEstW - safe);
      if (reactionPickerIsMine) {
        let left = reactionPicker.x - popoverEstW;
        left = Math.max(safe, Math.min(left, maxL));
        return { left, top };
      }
      let left = reactionPicker.x;
      left = Math.max(safe, Math.min(left, maxL));
      return { left, top };
    })()
    : { left: 8, top: 8 };

  const showContextActionMenu = !multiSelectMode
    && actionBarOpen
    && selectedCount === 1
    && !!selectedMessage
    && !!messageActionMenu;
  const activeFileMenuSelection = (
    showContextActionMenu
    && fileMenuSelection
    && selectedMessage
    && String(fileMenuSelection.messageId) === String(selectedMessage.id)
  ) ? fileMenuSelection : null;
  const mobileSelectedMessageFile = (() => {
    if (reactionHoverDesktop || !selectedMessage || !Array.isArray(selectedMessage.attachments)) return null;
    const fileAtt = selectedMessage.attachments.find((att) => (
      att
      && att.s3_key
      && att.kind !== 'image'
      && att.kind !== 'video'
      && att.kind !== 'audio'
    ));
    if (!fileAtt) return null;
    const fileName = fileAtt.original_filename || extractFilename(fileAtt.s3_key);
    const previewUrl = getViewUrl(fileAtt.s3_key, fileName);
    const attachmentId = fileAtt?.id ?? fileAtt?.attachment_id ?? null;
    const downloadUrl = getAttachmentDownloadUrl(chatId, attachmentId) || previewUrl;
    return { fileName, downloadUrl, attachmentId, mime: fileAtt.mime };
  })();
  const webShareAvailable = canShareAttachmentOnDevice();
  const hasFileContextActions = !messageActionReactionsExpanded && (
    (!reactionHoverDesktop && (!!activeFileMenuSelection || !!mobileSelectedMessageFile))
    || (reactionHoverDesktop && !!activeFileMenuSelection)
  );
  const isPinnedFeedMessageMenu = pinnedFeedOpen && showContextActionMenu;
  const pinCtxExtra = canPinOrUnpin && !isPinnedFeedMessageMenu ? 44 : 0;
  const pinnedFeedCanCopyText = Boolean((selectedMessage?.content || '').trim()) && !selectedMessage?.deleted_at;
  const messageActionMenuPos = messageActionMenu && typeof window !== 'undefined'
    ? (() => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const safe = 8;
      const estW = 250;
      const FILE_ROWS_EXTRA = webShareAvailable ? 138 : 92;
      const baseEstH = isPinnedFeedMessageMenu
        ? 132
        : (messageActionReactionsExpanded ? 268 : 314 + pinCtxExtra);
      const estH = hasFileContextActions && !isPinnedFeedMessageMenu
        ? baseEstH + FILE_ROWS_EXTRA
        : baseEstH;
      let top = messageActionMenu.y - (messageActionReactionsExpanded ? 52 : 36);
      if (top + estH > vh - safe) top = vh - safe - estH;
      top = Math.max(safe, top);
      const maxL = Math.max(safe, vw - estW - safe);
      let left = messageActionMenu.isMine ? (messageActionMenu.x - estW) : messageActionMenu.x;
      left = Math.max(safe, Math.min(left, maxL));
      return { left, top };
    })()
    : { left: 8, top: 8 };
  const centerExpandedReactionsMenuMobile = typeof window !== 'undefined'
    && messageActionReactionsExpanded
    && (
      window.innerWidth <= 900
      || window.matchMedia?.('(hover: none), (pointer: coarse)').matches
    );

  const selectedMessageReactions = (() => {
    if (!selectedMessage) return new Set();
    const list = Array.isArray(selectedMessage.reactions) ? selectedMessage.reactions : [];
    return new Set(list.filter((r) => r.reacted_by_me).map((r) => r.emoji));
  })();
  const contextReactionPreview = ALLOWED_MESSAGE_REACTIONS_LIST.slice(0, 4);
  const viewerItems = Array.isArray(imageView.urls)
    ? imageView.urls.filter(Boolean).map((u) => ({ url: u, kind: 'image' }))
    : [];
  const handleViewerIndexChange = (nextIndex) => {
    setImageView((prev) => {
      const max = Math.max(0, (prev.urls?.length || 1) - 1);
      const safe = Math.max(0, Math.min(Number(nextIndex) || 0, max));
      if (safe === prev.index) return prev;
      return { ...prev, index: safe };
    });
  };
  const showLegacyActionBar = multiSelectMode && actionBarOpen;
  const showMobileMultiSelectBar = multiSelectMode && !reactionHoverDesktop;

  return (
    <>
      <div
        ref={chatModalBackdropRef}
        className={s.chatModalBackdrop}
        role="dialog"
        aria-modal="true"
        onClick={onClose}
      >
        <div
          ref={chatCardRef}
          className={s.chatModalCard}
          data-chat-type={currentChatType}
          onClick={(e) => e.stopPropagation()}
          onDragEnter={handleChatDragEnter}
          onDragOver={handleChatDragOver}
          onDragLeave={handleChatDragLeave}
          onDrop={handleChatDrop}
        >
          {/* Header */}
          <header className={s.chatModalHeader}>
            <button type="button" aria-label="Назад" className={s.backBtn} onClick={onClose}>
              <LeaveChatIcon className={s.icon} />
            </button>

            <div className={s.chatTitleWrap}>
              <div
                className={`${s.headerAvatarWrap} ${isFavoritesChat(currentChat) ? '' : s.headerAvatarClickable}`}
                role={isFavoritesChat(currentChat) ? undefined : 'button'}
                tabIndex={isFavoritesChat(currentChat) ? undefined : 0}
                aria-label={isFavoritesChat(currentChat) ? undefined : 'Открыть профиль'}
                onClick={isFavoritesChat(currentChat) ? undefined : handleHeaderAvatarClick}
                onKeyDown={isFavoritesChat(currentChat) ? undefined : (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleHeaderAvatarClick(e);
                  }
                }}
              >
                <ChatCounterpartyAvatar
                  chat={currentChat}
                  getPeerEmployeeId={getPeerEmployeeId}
                  employeeTaskStatsMap={employeeTaskStatsMap}
                  resolveImageUrl={resolveImageUrl}
                  size={48}
                  favoritesClassName={s.favoritesChatAvatarHeader}
                  imgClassName={s.chatHeaderAvatar}
                />

                {counterpartyPresence?.show && (
                  <span
                    className={`${s.statusDotHeader} ${counterpartyPresence.online ? s.statusOnline : s.statusOffline}`}
                    aria-label={counterpartyPresence.online ? 'Онлайн' : 'Оффлайн'}
                    title={counterpartyPresence.online ? 'Онлайн' : 'Оффлайн'}
                  />
                )}
              </div>

              <h2
                className={`${s.chatTitle} ${s.chatTitleClickable}`}
                role="button"
                tabIndex={0}
                aria-label="Открыть медиа"
                onClick={handleHeaderTitleClick}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleHeaderTitleClick(e);
                  }
                }}
              >
                {getChatDisplayTitle(currentChat)}
              </h2>
            </div>
            <div className={s.headerRightControls}>
              <button
                type="button"
                className={s.headerSearchBtn}
                aria-label="Поиск"
                title="Поиск"
                onClick={() => {
                  setSearchOpen(true);
                  setSearchIds([]);
                  setSearchTotal(0);
                  setActiveMatchIndex(-1);
                  searchEnterStartedRef.current = false;
                }}
              >
                <svg width="20" height="20" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 21L14.3334 14.3333M16.5556 8.77778C16.5556 13.0733 13.0733 16.5556 8.77778 16.5556C4.48223 16.5556 1 13.0733 1 8.77778C1 4.48223 4.48223 1 8.77778 1C13.0733 1 16.5556 4.48223 16.5556 8.77778Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              <div className={s.editWrap} ref={headerMenuRef}>
                <button
                  type="button"
                  className={s.headerMenuBtn}
                  aria-label="Меню"
                  title="Меню"
                  onClick={() => setHeaderMenuOpen(v => !v)}
                >
                  <svg width="20" height="20" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.50768 21.8453L10.2907 23.4227C10.5235 23.8922 10.9033 24.2911 11.3842 24.5711C11.8652 24.8511 12.4264 25.0001 13 25C13.5736 25.0001 14.1348 24.8511 14.6158 24.5711C15.0967 24.2911 15.4765 23.8922 15.7093 23.4227L16.4923 21.8453C16.7711 21.2856 17.2399 20.819 17.8321 20.512C18.428 20.2041 19.1174 20.073 19.8015 20.1373L21.7174 20.32C22.2877 20.374 22.8633 20.2787 23.3743 20.0456C23.8853 19.8125 24.3098 19.4516 24.5964 19.0067C24.8834 18.562 25.0201 18.0523 24.99 17.5395C24.9598 17.0266 24.7642 16.5326 24.4267 16.1173L23.2924 14.7213C22.8885 14.2206 22.6727 13.6178 22.6761 13C22.676 12.3839 22.8938 11.7836 23.2983 11.2853L24.4327 9.88933C24.7701 9.47411 24.9658 8.98008 24.9959 8.46721C25.026 7.95433 24.8893 7.44465 24.6024 7C24.3158 6.55508 23.8913 6.19419 23.3802 5.96107C22.8692 5.72796 22.2937 5.63264 21.7234 5.68667L19.8075 5.86933C19.1233 5.93369 18.434 5.80255 17.838 5.49467C17.2447 5.1859 16.7757 4.71683 16.4983 4.15467L15.7093 2.57733C15.4765 2.10781 15.0967 1.70886 14.6158 1.42888C14.1348 1.1489 13.5736 0.999916 13 1C12.4264 0.999916 11.8652 1.1489 11.3842 1.42888C10.9033 1.70886 10.5235 2.10781 10.2907 2.57733L9.50768 4.15467C9.23025 4.71683 8.76126 5.1859 8.16791 5.49467C7.572 5.80255 6.88263 5.93369 6.19845 5.86933L4.27663 5.68667C3.70634 5.63264 3.13078 5.72796 2.61977 5.96107C2.10875 6.19419 1.68421 6.55508 1.39762 7C1.11067 7.44465 0.973968 7.95433 1.00409 8.46721C1.03422 8.98008 1.22987 9.47411 1.56733 9.88933L2.70166 11.2853C3.10619 11.7836 3.32404 12.3839 3.32391 13C3.32404 13.6161 3.10619 14.2164 2.70166 14.7147L1.56733 16.1107C1.22987 16.5259 1.03422 17.0199 1.00409 17.5328C0.973968 18.0457 1.11067 18.5554 1.39762 19C1.68449 19.4447 2.10908 19.8054 2.62002 20.0385C3.13097 20.2716 3.70638 20.367 4.27663 20.3133L6.1925 20.1307C6.87668 20.0663 7.56605 20.1975 8.16196 20.5053C8.75752 20.8132 9.22869 21.2824 9.50768 21.8453Z" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M12.9976 16.6C15.2174 16.6 17.0169 14.9882 17.0169 13C17.0169 11.0118 15.2174 9.4 12.9976 9.4C10.7778 9.4 8.97831 11.0118 8.97831 13C8.97831 14.9882 10.7778 16.6 12.9976 16.6Z" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>

                {headerMenuOpen && (
                  <div className={`${s.dropdownMenu} ${s.dropLeft}`} onClick={(e) => e.stopPropagation()}>
                    <div className={s.fontCardRow}>
                      <span className={s.fontCardLeft}>
                        <span className={s.fontCardIconWrap} aria-hidden="true">
                          <span className={s.fontCardIconText}>A</span>
                        </span>
                        <span className={s.fontCardTitle}>Размер шрифта</span>
                      </span>
                      <span className={s.fontControls} aria-label="Размер текста">
                        <button type="button" className={s.fontBtn} onClick={onDecFont} aria-label="Меньше">−</button>
                        <span className={s.fontBadge} title={`Текущий размер: ${fontSize || ''}`}>{fontSize || 'A'}</span>
                        <button type="button" className={s.fontBtn} onClick={onIncFont} aria-label="Больше">+</button>
                      </span>
                    </div>
                    {!!currentChat?.id && typeof onToggleChatMute === 'function' && (
                      <button
                        type="button"
                        className={s.notifyCardButton}
                        aria-label={currentChat.is_muted ? 'Включить уведомления' : 'Выключить уведомления'}
                        title={currentChat.is_muted ? 'Включить уведомления' : 'Выключить уведомления'}
                        onClick={async () => {
                          await onToggleChatMute(currentChat.id, !Boolean(currentChat.is_muted));
                        }}
                      >
                        <span className={s.notifyCardLeft}>
                          <span className={s.notifyCardIconWrap} aria-hidden="true">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M9.35419 21C10.0593 21.6224 10.9856 22 12 22C13.0145 22 13.9407 21.6224 14.6458 21M18 8C18 6.4087 17.3679 4.88258 16.2427 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.8826 2.63214 7.75738 3.75736C6.63216 4.88258 6.00002 6.4087 6.00002 8C6.00002 11.0902 5.22049 13.206 4.34968 14.6054C3.61515 15.7859 3.24788 16.3761 3.26134 16.5408C3.27626 16.7231 3.31488 16.7926 3.46179 16.9016C3.59448 17 4.19261 17 5.38887 17H18.6112C19.8074 17 20.4056 17 20.5382 16.9016C20.6852 16.7926 20.7238 16.7231 20.7387 16.5408C20.7522 16.3761 20.3849 15.7859 19.6504 14.6054C18.7795 13.206 18 11.0902 18 8Z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          <span className={s.notifyCardText}>
                            <span className={s.notifyCardTitle}>Push-уведомления</span>
                            <span className={s.notifyCardSubtitle}>{currentChat.is_muted ? 'Выключены' : 'Включены'}</span>
                          </span>
                        </span>
                        <motion.span
                          className={s.notifyToggleSwitch}
                          animate={{
                            backgroundColor: currentChat.is_muted ? '#ead8ff' : '#bd23db',
                            borderColor: currentChat.is_muted ? '#d8b4fe' : '#bd23db',
                            justifyContent: currentChat.is_muted ? 'flex-start' : 'flex-end',
                          }}
                          transition={{ type: 'spring', stiffness: 520, damping: 34, mass: 0.7 }}
                        >
                          <motion.span
                            layout
                            className={s.notifyToggleThumb}
                            transition={{ type: 'spring', stiffness: 520, damping: 34, mass: 0.7 }}
                          />
                        </motion.span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Поиск поверх шапки */}
            {searchOpen && (
              <div className={s.chatHeaderSearchOverlay} onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  aria-label="Выйти из поиска"
                  className={s.backBtn}
                  onClick={closeSearch}
                >
                  <LeaveChatIcon className={s.icon} />
                </button>

                <div className={s.chatHeaderSearchBox}>
                  <Input
                    inputRef={searchInputRef}
                    className={s.chatHeaderSearchField}
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      if (!e.target.value) searchEnterStartedRef.current = false;
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      if (e.nativeEvent?.isComposing) return;
                      e.preventDefault();
                      handleSearchInputEnter();
                    }}
                    placeholder="Поиск"
                    ariaLabel="Поиск"
                  />
                </div>

                <SearchIcon className={s.chatHeaderSearchIcon} />
              </div>
            )}
          </header>

          <div
            className={`${s.actionBar} ${showLegacyActionBar ? s.actionBarOpen : ''} ${multiSelectMode ? s.actionBarMultiSelect : ''}`}
            role="toolbar"
          >
            {!multiSelectMode && (
              <button
                type="button"
                className={`${s.actionIconBtn} ${s.actionCloseLeft}`}
                onClick={onCloseActionBar}
                title="Закрыть панель"
                aria-label="Закрыть панель"
              >
                <QuitMessageActionsIcon className={s.actionIcon} />
              </button>
            )}

            {multiSelectMode ? (
              showMobileMultiSelectBar ? (
                <div className={s.mobileMultiSelectBarInner}>
                  <button
                    type="button"
                    className={`${s.actionIconBtn} ${s.multiSelectIconBtnSmall}`}
                    onClick={onCloseActionBar}
                    aria-label="Назад"
                    title="Назад"
                  >
                    <LeaveChatIcon className={s.actionIcon} />
                  </button>
                  <div className={s.mobileMultiSelectRightGroup}>
                    <button
                      type="button"
                      className={s.actionIconBtn}
                      onClick={onCopy}
                      aria-label="Копировать"
                      title="Копировать"
                      disabled={selectedCount === 0 || !selectionHasContent}
                    >
                      <CopyMessageIcon className={s.actionIcon} />
                    </button>
                    <button
                      type="button"
                      className={s.actionIconBtn}
                      onClick={onForward}
                      aria-label={`Переслать ${selectedCount}`}
                      title="Переслать"
                      disabled={selectedCount === 0}
                    >
                      <ForwardMessageIcon className={s.actionIcon} />
                    </button>
                    <button
                      type="button"
                      className={`${s.actionIconBtn} ${s.danger}`}
                      onClick={() => setDeleteConfirmOpen(true)}
                      aria-label={`Удалить ${selectedCount}`}
                      title="Удалить"
                      disabled={selectedCount === 0 || deletableCount === 0}
                    >
                      <DeleteMessageIcon className={s.actionIcon} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className={s.multiSelectBarInner}>
                  <div className={s.multiSelectLeftGroup}>
                    <button
                      type="button"
                      className={s.multiSelectSendBtn}
                      onClick={onForward}
                      aria-label={`Переслать ${selectedCount}`}
                      disabled={selectedCount === 0}
                    >
                      Переслать {selectedCount}
                    </button>
                    <button
                      type="button"
                      className={s.multiSelectDeleteBtn}
                      onClick={() => setDeleteConfirmOpen(true)}
                      aria-label={`Удалить ${selectedCount}`}
                      disabled={selectedCount === 0 || deletableCount === 0}
                    >
                      Удалить {selectedCount}
                    </button>
                    <button
                      type="button"
                      className={`${s.actionIconBtn} ${s.multiSelectIconBtnSmall}`}
                      onClick={onCopy}
                      aria-label="Копировать"
                      disabled={selectedCount === 0 || !selectionHasContent}
                    >
                      <CopyMessageIcon className={s.actionIcon} />
                    </button>
                  </div>
                  <button
                    type="button"
                    className={s.multiSelectCancelBtn}
                    onClick={onCloseActionBar}
                    aria-label="Отмена"
                  >
                    Отмена
                  </button>
                </div>
              )
            ) : (
              <div className={s.actionBarButtons}>
                <button
                  type="button"
                  className={s.actionIconBtn}
                  onClick={onReply}
                  title="Ответить"
                  aria-label="Ответить"
                  disabled={selectedCount !== 1 || !selectedMessage || selectedMessage.deleted_at}
                >
                  <AnswerMessageIcon className={s.actionIcon} />
                </button>

                <button
                  type="button"
                  className={s.actionIconBtn}
                  onClick={onCopy}
                  title="Копировать"
                  aria-label="Копировать"
                  disabled={selectedCount === 0 || !selectionHasContent}
                >
                  <CopyMessageIcon className={s.actionIcon} />
                </button>

                <button
                  type="button"
                  className={`${s.actionIconBtn} ${s.danger}`}
                  onClick={() => setDeleteConfirmOpen(true)}
                  title="Удалить"
                  aria-label="Удалить"
                  disabled={selectedCount === 0 || deletableCount === 0}
                >
                  <DeleteMessageIcon className={s.actionIcon} />
                </button>

                <button
                  type="button"
                  className={s.actionIconBtn}
                  onClick={onForward}
                  title="Переслать"
                  aria-label="Переслать"
                  disabled={selectedCount === 0}
                >
                  <ForwardMessageIcon className={s.actionIcon} />
                </button>

                <button
                  type="button"
                  className={s.actionIconBtn}
                  onClick={onStartEdit}
                  title="Редактировать"
                  aria-label="Редактировать"
                  disabled={selectedCount !== 1 || !selectedMessage || !canEditMessage(selectedMessage) || selectedMessage.deleted_at}
                >
                  <EditMessageIcon className={s.actionIcon} />
                </button>
              </div>
            )}
          </div>

          <div ref={chatModalMainRef} className={s.chatModalMain}>
          {pinnedList.length > 0 && (
            <div className={s.pinnedMessagesStrip} role="region" aria-label="Закреплённые сообщения">
              <div className={s.pinnedMessagesStripAccent} aria-hidden />
              <button
                type="button"
                className={s.pinnedMessagesStripTap}
                onClick={async () => {
                  const n = pinnedList.length;
                  if (aroundLoading || !n) return;
                  const cur = Math.min(pinnedStripIndex, n - 1);
                  const mid = pinnedList[cur]?.id;
                  if (mid == null) return;
                  const nextIndex = (cur - 1 + n) % n;
                  await navigateToPinnedMessage(mid, { stripIndexAfter: nextIndex });
                }}
                disabled={aroundLoading}
              >
                <div className={s.pinnedMessagesStripTitle}>Закреплённое сообщение</div>
                <div className={s.pinnedMessagesStripPreview}>
                  {formatPinnedPreview(pinnedList[Math.min(pinnedStripIndex, pinnedList.length - 1)])}
                </div>
              </button>
              <div className={s.pinnedMessagesStripRight}>
                {pinnedList.length > 1 ? (
                  <div className={s.pinnedMessagesStripCounterSlot} aria-live="polite">
                    <span className={s.pinnedMessagesStripNavCount}>
                      {pinnedList.length - pinnedStripIndex}/{pinnedList.length}
                    </span>
                  </div>
                ) : null}
                <button
                  type="button"
                  className={s.pinnedMessagesStripPinBtn}
                  aria-label="Все закреплённые сообщения"
                  aria-expanded={pinnedFeedOpen}
                  aria-haspopup="dialog"
                  onClick={togglePinnedFeed}
                >
                  <PinMessageIcon className={s.pinnedMessagesStripPinSvg} />
                </button>
              </div>
            </div>
          )}

          {/* Messages */}
          <div
            ref={messagesContainerRef}
            id="chat-thread-scrollable"
            className={`${s.chatModalBody} ${isEditingMessageId ? s.withDockPadding : ''}`}
            onScroll={(e) => {
              const el = e.currentTarget;
              const now = Date.now();
              const currentTop = el.scrollTop;
              const previousTop = lastScrollTopRef.current;
              const deltaTop = currentTop - previousTop;
              const isPrependProgrammaticScroll = now < prependProgrammaticScrollUntilRef.current;
              if (!isPrependProgrammaticScroll && Math.abs(deltaTop) > 1) {
                const nextDirection = deltaTop > 0 ? 'down' : 'up';
                lastScrollDirectionRef.current = nextDirection;
                if (
                  nextDirection === 'up'
                  && now >= topReachCooldownUntilRef.current
                ) {
                  topLoadRequiresUserScrollRef.current = false;
                }
              }
              lastScrollTopRef.current = currentTop;
              schedulePinnedStripScrollSync();

              const anchor = scrollAnchorStateRef.current;
              if (anchor && Date.now() < anchor.until) {
                const moved = Math.abs(currentTop - scrollAnchorUserScrollRef.current);
                if (moved > 16) {
                  endScrollAnchorLock();
                }
              }
              scrollAnchorUserScrollRef.current = currentTop;

              if (!isPrependProgrammaticScroll && now < prependStabilizeUntilRef.current) {
                prependStabilizeUserAdjustedRef.current = true;
              }

              const diff = Math.max(0, el.scrollHeight - (el.scrollTop + el.clientHeight));
              const isNearBottom = diff < CHAT_NEAR_BOTTOM_PX || el.scrollHeight <= el.clientHeight + 1;
              if (!isNearBottom) {
                openBottomClampUntilRef.current = 0;
                autoscrollEnabledRef.current = false;
              }
              const preloadEdge = Math.max(
                CHAT_PRELOAD_EDGE_PX,
                Math.round(el.clientHeight * 0.45),
              );
              const isPrependStabilizing = now < prependStabilizeUntilRef.current;
              if (
                !isPrependStabilizing
                && currentTop < preloadEdge
                && lastScrollDirectionRef.current === 'up'
              ) {
                tryLoadOlderRef.current?.();
              }
              if (diff < preloadEdge) {
                tryLoadNewerRef.current?.();
              }
              // Плашку непрочитанного не скрываем по скроллу — только при наборе текста или уходе из чата

              setShowScrollDown(diff > 300);

              if (typeof onMessagesScroll === 'function') {
                onMessagesScroll(isNearBottom);
              }
            }}
          >
            {loadingOlder ? (
              <div className={s.olderLoadingOverlay} aria-live="polite" aria-busy="true">
                Загрузка…
              </div>
            ) : null}
            {chatMessages.length === 0 ? (
              <p>Нет сообщений в этом чате.</p>
            ) : (
              <InfiniteScroll
                dataLength={chatMessages.length}
                next={tryLoadOlder}
                hasMore={hasMoreOlder && !loadingOlder}
                inverse
                loader={null}
                scrollThreshold={`${CHAT_PRELOAD_EDGE_PX}px`}
                scrollableTarget="chat-thread-scrollable"
                style={{ overflow: 'visible' }}
              >
                <ul ref={messagesListRef} className={s.messagesList}>
                  <li ref={topLoadSentinelRef} className={s.historyLoadSentinel} aria-hidden="true" />
                  {chatMessages.map((message) => {
                    const isMine = String(message.sender_id) === String(myId);
                    const isGroup = currentChatType === 'group' || currentChatType === 'rank';
                    const isEditing = isEditingMessageId === message.id;
                    const isDeleted = Boolean(message.deleted_at);
                    const readByOpponent = isMessageReadByCounterparty(message);
                    const isImportantMsg = Boolean(message.is_important);

                    const isTemp = message.__optimistic === true;
                    const isFailed = message.__failed === true;

                    // reply-превью: поддерживаем оба формата от бэка —
                    // (a) legacy: reply_to_message_id + ищем сообщение в списке
                    // (b) новый: reply_to: { id, sender_name, content, ... }
                    const replyId =
                      message.reply_to_message_id ??
                      (message.reply_to && message.reply_to.id != null
                        ? message.reply_to.id
                        : null);
                    const hasReply = !!replyId;
                    const replyFromList = hasReply
                      ? messageById.get(String(replyId))
                      : null;
                    const replyTo = replyFromList || message.reply_to || null;

                    const isSystem = (message.sender_name || '').trim().toLowerCase() === 'система';
                    const raw = message.sender_avatar_url || message.sender_avatar || message.avatar;
                    const avatarSrc =
                      resolveImageUrl(raw) ||
                      (isSystem ? resolveImageUrl('/uploads/user_avatars/system_avatar.png') : null);

                    // ===== подсветка поиска (все совпадения + активное совпадение) =====
                    const isHit =
                      searchOpen &&
                      normalizedSearch &&
                      (searchableContentById?.get(String(message.id)) || '').includes(normalizedSearch);

                    const isActiveHit =
                      isHit && activeMatchId != null && String(activeMatchId) === String(message.id);

                    const isFirstUnread =
                      !hideUnreadDivider &&
                      firstUnreadId != null &&
                      String(message.id) === String(firstUnreadId);

                    const msgReactions = Array.isArray(message.reactions) ? message.reactions : [];
                    const hasAudioAttachment = Array.isArray(message.attachments)
                      && message.attachments.some((att) => att?.kind === 'audio');
                    const hasVisualMediaAttachment = Array.isArray(message.attachments)
                      && message.attachments.some((att) => att?.kind === 'image' || att?.kind === 'video');
                    const mediaPreviewWidth = hasVisualMediaAttachment
                      ? getChatMediaPreviewWidth({
                        isGroup,
                        isMine,
                        attachments: message.attachments,
                      })
                      : null;
                    const messageContentStyle = mediaPreviewWidth
                      ? { maxWidth: mediaPreviewWidth }
                      : undefined;
                    const allowReactionDisplay =
                      !isDeleted && !isTemp && !isSystem && !isEditing && Boolean(chatId);
                    const showReactionStrip = allowReactionDisplay && msgReactions.length > 0;

                    return (
                      <React.Fragment key={message.id}>
                        {isFirstUnread && (
                          <li className={s.unreadDivider} aria-label="Непрочитанные сообщения">
                            <span className={s.unreadDividerLine} />
                            <span className={s.unreadDividerText}>Непрочитанные сообщения</span>
                            <span className={s.unreadDividerLine} />
                          </li>
                        )}
                        <li
                          key={message.id}
                          id={`msg-${message.id}`}
                          data-msg-id={message.id}
                          className={[
                            s.messageRow,
                            isGroup && !isMine ? s.messageRowWithAvatar : '',
                            multiSelectMode ? s.messageRowMultiSelectMode : '',
                            selectedMessageIdSet.has(String(message.id)) ? s.messageRowSelected : '',
                            String(flashMessageId) === String(message.id) ? s.messageRowFlash : '',
                            isHit ? s.searchHit : '',
                            isActiveHit ? s.searchHitActive : '',
                            !IsIOS && message.__justPrepended ? s.messageRowPrependEnter : '',
                            reactionHoverDesktop
                              && reactionPicker
                              && String(reactionPicker.messageId) === String(message.id)
                              ? s.messageRowKeepsReactionMini
                              : '',
                          ].filter(Boolean).join(' ')}
                          style={{
                            justifyContent: isMine ? 'flex-end' : 'flex-start',
                          }}
                          onMouseEnter={() => {
                            if (!reactionHoverDesktop) return;
                            if (
                              reactionPicker
                              && String(reactionPicker.messageId) !== String(message.id)
                            ) {
                              cancelReactionPickerCloseTimer();
                              setReactionPicker(null);
                              return;
                            }
                            cancelReactionPickerCloseTimer();
                          }}
                          onMouseLeave={(e) => {
                            if (!reactionHoverDesktop || !reactionPicker) return;
                            const to = e.relatedTarget;
                            if (to && reactionPickerRef.current?.contains(to)) return;
                            scheduleReactionPickerClose();
                          }}
                          onContextMenu={(e) => {
                            if (isDeleted || isEditing) return;
                            const preferNativeTextMenu = typeof window !== 'undefined'
                              && window.matchMedia?.('(pointer: coarse)').matches;
                            if (preferNativeTextMenu && e.target?.closest?.('[data-chat-selectable]')) return;
                            if (Date.now() < suppressMessageRowClicksUntilRef.current || multiSelectMode) {
                              e.preventDefault();
                              e.stopPropagation();
                              return;
                            }
                            const interactiveTarget = e.target?.closest?.('a[href],button,input,textarea,select,label,video,audio');
                            if (interactiveTarget) return;
                            e.preventDefault();
                            e.stopPropagation();

                            if (
                              messageActionMenu
                              && String(messageActionMenu.messageId) === String(message.id)
                            ) {
                              setMessageActionMenu(null);
                              setMessageActionReactionsExpanded(false);
                              onCloseActionBar?.();
                              return;
                            }
                            if (messageActionMenu) {
                              setMessageActionMenu(null);
                              setMessageActionReactionsExpanded(false);
                              onCloseActionBar?.();
                              return;
                            }

                            messageActionAnchorRef.current = {
                              x: e.clientX,
                              y: e.clientY,
                              messageId: message.id,
                            };
                            onMessageClick(message);
                          }}
                          onClick={(e) => {
                            if (isDeleted || isEditing) return;
                            if (Date.now() < suppressMessageRowClicksUntilRef.current) {
                              return;
                            }
                            if (suppressNextMessageRowClickRef.current) {
                              suppressNextMessageRowClickRef.current = false;
                              return;
                            }
                            const selection = window.getSelection?.();
                            const hasTextSelection = !!selection && !selection.isCollapsed && String(selection).trim().length > 0;
                            const selectedInsideMessage = hasTextSelection && selection.rangeCount > 0
                              ? e.currentTarget.contains(selection.getRangeAt(0).commonAncestorContainer)
                              : false;
                            if (selectedInsideMessage) return;
                            const mediaTarget = e.target?.closest?.(
                              `img,video,audio,.${s.audioWave},.${s.audioMessage},.${s.imageAlbumTileBtn}`,
                            );
                            if (mediaTarget) return;
                            const interactiveTarget = e.target?.closest?.('a[href],button,input,textarea,select,label');
                            if (interactiveTarget) return;
                            const tappedInsideBubble = !!e.target?.closest?.(`.${s.messageBubble}`);
                            if (!tappedInsideBubble) {
                              setReactionPicker(null);
                              setMessageActionMenu(null);
                              setReactionsDetail(null);
                              if (multiSelectMode) {
                                onMessageClick?.(message);
                              } else {
                                onEnableMultiSelect?.(message);
                              }
                              return;
                            }
                            if (!reactionHoverDesktop && multiSelectMode) {
                              setReactionPicker(null);
                              setMessageActionMenu(null);
                              setReactionsDetail(null);
                              onMessageClick?.(message);
                              return;
                            }
                            if (
                              messageActionMenu
                              && String(messageActionMenu.messageId) === String(message.id)
                            ) {
                              setMessageActionMenu(null);
                              setMessageActionReactionsExpanded(false);
                              onCloseActionBar?.();
                              return;
                            }
                            if (messageActionMenu) {
                              setMessageActionMenu(null);
                              setMessageActionReactionsExpanded(false);
                              onCloseActionBar?.();
                              return;
                            }

                            if (
                              reactionHoverDesktop
                              && !multiSelectMode
                              && typeof e?.button === 'number'
                              && e.button !== 2
                            ) return;

                            messageActionAnchorRef.current = {
                              x: e.clientX,
                              y: e.clientY,
                              messageId: message.id,
                            };
                            onMessageClick(message);
                          }}
                          onPointerDown={!reactionHoverDesktop ? (e) => onMobileMessageRowPointerDown(e, message) : undefined}
                          onPointerMove={!reactionHoverDesktop ? onMobileMessageRowPointerMove : undefined}
                          onPointerUp={!reactionHoverDesktop ? onMobileMessageRowPointerEnd : undefined}
                          onPointerCancel={!reactionHoverDesktop ? onMobileMessageRowPointerEnd : undefined}
                        >
                          {isGroup && !isMine && (
                            isSystem ? (
                              <Avatar
                                className={`${s.avatar} ${s.headerAvatarClickable}`}
                                src={avatarSrc}
                                name={message.sender_name || `Пользователь ${message.sender_id}`}
                                alt={message.sender_name || `Пользователь ${message.sender_id}`}
                                size={48}
                                role="button"
                                tabIndex={0}
                                onClick={(e) => handleMessageAvatarClick(e, message.sender_id, message.sender_name)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    handleMessageAvatarClick(e, message.sender_id, message.sender_name);
                                  }
                                }}
                              />
                            ) : (
                              <ChatSenderAvatar
                                employeeId={message.sender_id}
                                avatarUrl={avatarSrc}
                                employeeTaskStatsMap={employeeTaskStatsMap}
                                size={48}
                                className={`${s.avatar} ${s.headerAvatarClickable}`}
                                alt={message.sender_name || `Пользователь ${message.sender_id}`}
                                onClick={(e) => handleMessageAvatarClick(e, message.sender_id, message.sender_name)}
                              />
                            )
                          )}

                          <div
                            className={[
                              s.messageColumn,
                              hasAudioAttachment ? s.messageColumnWithAudio : '',
                            ].filter(Boolean).join(' ')}
                            style={{ alignItems: isMine ? 'flex-end' : 'flex-start' }}
                          >
                            <div
                              className={[
                                s.messageBubble,
                                isMine ? s.messageBubbleMine : '',
                                (isGroup && !isMine && message.sender_name) || message.forwarded_from_message_id
                                  ? s.messageBubbleWithSender
                                  : '',
                                hasAudioAttachment ? s.messageBubbleWithAudio : '',
                                hasVisualMediaAttachment ? s.messageBubbleWithMedia : '',
                                isImportantMsg ? s.messageBubbleImportant : '',
                              ].filter(Boolean).join(' ')}
                              style={{
                                ...(isMine ? { background: '#f4e2ff' } : {}),
                                ...(isGroup ? { boxSizing: 'border-box' } : {}),
                                ...(isFailed ? { opacity: 1, outline: '1px dashed #e11', background: '#fff5f5' } : {}),
                              }}
                              onClickCapture={(e) => {
                                if (reactionHoverDesktop || multiSelectMode || isDeleted || isEditing) return;
                                if (Date.now() < suppressMessageRowClicksUntilRef.current) return;
                                const mediaTarget = e.target?.closest?.(
                                  `a,img,video,audio,[data-chat-file-preview="true"],.${s.audioWave},.${s.audioMessage},.${s.imageAlbumTileBtn},.${s.messageReactionsRow},.${s.reactionChip},.${s.reactionChipEmojiWrap},.${s.reactionChipCount}`,
                                );
                                if (mediaTarget) return;
                                if (suppressNextMessageRowClickRef.current) {
                                  suppressNextMessageRowClickRef.current = false;
                                  return;
                                }
                                const selection = window.getSelection?.();
                                const hasTextSelection = !!selection && !selection.isCollapsed && String(selection).trim().length > 0;
                                const selectedInsideBubble = hasTextSelection && selection.rangeCount > 0
                                  ? e.currentTarget.contains(selection.getRangeAt(0).commonAncestorContainer)
                                  : false;
                                if (selectedInsideBubble) return;
                                messageActionAnchorRef.current = {
                                  x: e.clientX,
                                  y: e.clientY,
                                  messageId: message.id,
                                };
                                onMessageClick(message);
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onPointerDown={
                                (reactionHoverDesktop && showReactionStrip)
                                  ? (e) => onDesktopBubbleReactionsSummaryPointerDown(e, message)
                                  : (!reactionHoverDesktop && allowReactionDisplay
                                    ? (e) => onMobileReactionBubblePointerDown(e, message)
                                    : undefined)
                              }
                              onPointerMove={
                                (reactionHoverDesktop && showReactionStrip)
                                  ? onDesktopBubbleReactionsSummaryPointerMove
                                  : (!reactionHoverDesktop && allowReactionDisplay
                                    ? onMobileReactionBubblePointerMove
                                    : undefined)
                              }
                              onPointerUp={
                                (reactionHoverDesktop && showReactionStrip)
                                  ? onDesktopBubbleReactionsSummaryPointerEnd
                                  : (!reactionHoverDesktop && allowReactionDisplay
                                    ? onMobileReactionBubblePointerEnd
                                    : undefined)
                              }
                              onPointerCancel={
                                (reactionHoverDesktop && showReactionStrip)
                                  ? onDesktopBubbleReactionsSummaryPointerEnd
                                  : (!reactionHoverDesktop && allowReactionDisplay
                                    ? onMobileReactionBubblePointerEnd
                                    : undefined)
                              }
                            >
                              {allowReactionDisplay && reactionHoverDesktop && (
                                <button
                                  type="button"
                                  className={[
                                    s.reactionMiniBtn,
                                    isMine ? s.reactionMiniBtnOutwardStart : s.reactionMiniBtnOutwardEnd,
                                  ].join(' ')}
                                  aria-label="Реакция"
                                  title="Реакция"
                                  onMouseEnter={(e) => onDesktopReactionMiniEnter(e, message)}
                                  onMouseLeave={onDesktopReactionMiniLeave}
                                  onClick={(e) => onDesktopReactionMiniClick(e, message)}
                                  onFocus={() => cancelReactionPickerCloseTimer()}
                                >
                                  <ReactionGlyph
                                    emoji={ALLOWED_MESSAGE_REACTIONS_LIST[0]}
                                    className={`${s.reactionChipEmoji} ${s.reactionCustomGlyph}`}
                                  />
                                </button>
                              )}
                              {!isEditing && (
                                message.forwarded_from_message_id ? (
                                  <div className={s.messageBubbleTopRow}>
                                    <div className={s.msgForwarded} data-chat-selectable="1">
                                      Переслано от {message.forwarded_from_sender_name || 'неизвестный автор'}
                                    </div>
                                    {renderMessageTimeRow(message)}
                                  </div>
                                ) : isGroup && !isMine && message.sender_name ? (
                                  <div className={s.messageBubbleTopRow}>
                                    <p className={s.messageSender} data-chat-selectable="1">
                                      <strong>{message.sender_name || `Пользователь ${message.sender_id}`}</strong>
                                    </p>
                                    {renderMessageTimeRow(message)}
                                  </div>
                                ) : renderMessageTimeRow(message)
                              )}
                              {/* иконку важности убрали — важность теперь только красной рамкой */}

                              {isMine && !hasVisualMediaAttachment && (
                                <>
                                  {isTemp ? (
                                    <span className={s.msgTicks} aria-label="Отправляется" title="Отправляется" style={{ color: '#9CA3AF' }}>
                                      <SendingTickIcon className={s.tick} aria-hidden="true" />
                                    </span>
                                  ) : (
                                    <span
                                      className={`${s.msgTicks} ${readByOpponent ? s.msgTicksRead : ''}`}
                                      aria-label={readByOpponent ? 'Прочитано' : 'Отправлено'}
                                      title={readByOpponent ? 'Прочитано' : 'Отправлено'}
                                    >
                                      <ReadTicksIcon className={s.tick} aria-hidden="true" />
                                    </span>
                                  )}
                                </>
                              )}

                              {hasReply && (
                                <div
                                  data-chat-selectable="1"
                                  className={s.replyPreview}
                                  role="button"
                                  tabIndex={0}
                                  title="Перейти к сообщению"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (aroundLoading) return;
                                    onMessagesScroll?.(false);
                                    if (replyId) {
                                      jumpToMessage(replyId);
                                      onFlashMessage?.(replyId);
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (aroundLoading) return;
                                      onMessagesScroll?.(false);
                                      if (replyId) {
                                        jumpToMessage(replyId);
                                        onFlashMessage?.(replyId);
                                      }
                                    }
                                  }}
                                >
                                  {renderReplyPreviewBlock(replyTo)}
                                </div>
                              )}

                              {message.forwarded_from_message_id ? (
                                <p className={s.messageContent} data-chat-selectable="1" style={messageContentStyle}>
                                  {isDeleted ? (
                                    <span className={s.muted}>Сообщение удалено</span>
                                  ) : (
                                    renderTextWithLinks(message.content, (searchOpen && normalizedSearch) ? normalizedSearch : '')
                                  )}
                                </p>
                              ) : (
                                <p className={s.messageContent} data-chat-selectable="1" style={messageContentStyle}>
                                  {isDeleted ? (
                                    <span className={s.muted}>Сообщение удалено</span>
                                  ) : (
                                    renderTextWithLinks(message.content, (searchOpen && normalizedSearch) ? normalizedSearch : '')
                                  )}
                                </p>
                              )}

                              {Array.isArray(message.attachments) && message.attachments.length > 0 && (() => {
                                const attachments = message.attachments;
                                const imageAttachments = attachments.filter((att) => att?.kind === 'image');
                                const imageUrls = imageAttachments.map((att) => getViewUrl(att.s3_key));
                                const hasImageAlbum = imageAttachments.length > 1 && imageAttachments.length === attachments.length;
                                const renderAttachment = (att) => {
                                  const viewUrl = getViewUrl(att.s3_key);
                                  const { dimW, dimH, hasMediaDims } = getChatAttachmentDims(
                                    att,
                                    viewUrl,
                                    knownImageDimensionsByUrl,
                                  );
                                  const imageFrameStyle = buildChatSingleImageFrameStyle(
                                    mediaPreviewWidth,
                                    dimW,
                                    dimH,
                                  );
                                  const videoFrameStyle = buildChatSingleVideoFrameStyle(
                                    mediaPreviewWidth,
                                    dimW,
                                    dimH,
                                  );

                                  if (att.kind === 'image') {
                                    return (
                                      <div key={att.id || att.s3_key} style={imageFrameStyle}>
                                        <img
                                          src={viewUrl}
                                          alt=""
                                          loading="lazy"
                                          width={hasMediaDims ? dimW : undefined}
                                          height={hasMediaDims ? dimH : undefined}
                                          style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            display: 'block',
                                            cursor: 'zoom-in',
                                          }}
                                          onLoad={(e) => {
                                            const img = e.currentTarget;
                                            rememberImageNaturalSize(viewUrl, img.naturalWidth, img.naturalHeight);
                                            onChatMediaLoaded();
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openImageFromGallery(viewUrl, imageUrls);
                                          }}
                                        />
                                      </div>
                                    );
                                  }

                                  if (att.kind === 'video') {
                                    return (
                                      <div key={att.id || att.s3_key} style={videoFrameStyle}>
                                        <ChatInlineVideo src={viewUrl} onLoadedMetadata={onChatMediaLoaded} />
                                      </div>
                                    );
                                  }

                                  if (att.kind === 'audio') {
                                    const audioFileName = att.original_filename ||
                                      (() => { try { return decodeURIComponent(att.s3_key?.split('/').pop() || 'audio'); } catch { return 'audio'; } })();
                                    return (
                                      <div key={att.id || att.s3_key} className={s.audioMessage}>
                                        <AudioWaveform src={viewUrl} filename={audioFileName} onLoadedMetadata={undefined} />
                                      </div>
                                    );
                                  }

                                  // ---------- FILE/DOC/ARCHIVE: карточка «иконка + имя + размер» + предпросмотр PDF ----------
                                  const fileName =
                                    att.original_filename ||
                                    (() => { try { return decodeURIComponent(att.s3_key.split('/').pop() || 'file'); } catch { return 'file'; } })();
                                  const previewUrl = getViewUrl(att.s3_key, fileName);
                                  const attachmentId = att?.id ?? att?.attachment_id ?? null;
                                  const downloadUrl = getAttachmentDownloadUrl(chatId, attachmentId) || previewUrl;
                                  const icon = fileIcon(att);
                                  return (
                                    <div
                                      key={att.id || att.s3_key}
                                      onPointerDown={(e) => {
                                        e.stopPropagation();
                                      }}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'stretch',
                                        gap: 8,
                                        maxWidth: '100%',
                                        width: '100%',
                                      }}
                                    >
                                      <button
                                        type="button"
                                        data-chat-file-preview="true"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const modifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1;
                                          if (modifiedClick) return;
                                          e.preventDefault();
                                          setFileMenuSelection(null);
                                          openAttachmentPdfPreview({
                                            chatId,
                                            attachmentId,
                                            downloadUrl,
                                            fileName,
                                          });
                                        }}
                                        onContextMenu={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setFileMenuSelection({
                                            messageId: message.id,
                                            downloadUrl,
                                            fileName,
                                            attachmentId,
                                          });
                                          messageActionAnchorRef.current = {
                                            x: e.clientX,
                                            y: e.clientY,
                                            messageId: message.id,
                                          };
                                          setMessageActionReactionsExpanded(false);
                                          onMessageClick?.(message);
                                        }}
                                        title={`Предпросмотр: ${fileName}${att.mime ? `\n${att.mime}` : ''}${att.size ? ` • ${humanSize(att.size)}` : ''}`}
                                        style={{
                                          display: 'flex', gap: 12, alignItems: 'center',
                                          padding: '10px 12px',
                                          borderRadius: 12, textDecoration: 'none', background: '#E9C6FE',
                                          maxWidth: '100%',
                                          border: '2.5px solid #BD23DB',
                                          color: '#111',
                                          flex: 1,
                                          cursor: 'pointer',
                                          textAlign: 'left',
                                        }}
                                      >
                                        <span aria-hidden="true" style={{
                                          width: 36, height: 36, borderRadius: 18, display: 'grid', placeItems: 'center',
                                          background: '#BD23DB', fontSize: 20, flex: '0 0 36px'
                                        }}>{icon}</span>
                                        <span style={{ minWidth: 0, display: 'grid', flex: 1 }}>
                                          <span style={{
                                            fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden',
                                            textOverflow: 'ellipsis', maxWidth: 260
                                          }}>{fileName}</span>
                                          <span style={{ fontSize: 12, color: '#6b7280' }}>
                                            {att.size ? humanSize(att.size) : (att.mime || 'file')}
                                          </span>
                                        </span>
                                      </button>
                                    </div>
                                  );
                                };

                                if (hasImageAlbum) {
                                  const isEvenAlbum = imageAttachments.length % 2 === 0;
                                  const ratioOf = (att) => {
                                    const viewUrl = getViewUrl(att.s3_key);
                                    const { dimW, dimH } = getChatAttachmentDims(
                                      att,
                                      viewUrl,
                                      knownImageDimensionsByUrl,
                                    );
                                    if (dimW > 0 && dimH > 0) return dimW / dimH;
                                    return 1;
                                  };
                                  const distributeSpans = (weights, totalCols = 12) => {
                                    if (!weights.length) return [];
                                    const minSpan = 2;
                                    const sum = weights.reduce((a, b) => a + b, 0) || 1;
                                    const spans = weights.map((w) => {
                                      const safe = Number.isFinite(w) ? w : 1;
                                      return Math.max(minSpan, Math.round((safe / sum) * totalCols));
                                    });
                                    let diff = totalCols - spans.reduce((a, b) => a + b, 0);
                                    while (diff !== 0) {
                                      if (diff > 0) {
                                        let idx = 0;
                                        while (diff > 0) {
                                          spans[idx % spans.length] += 1;
                                          diff -= 1;
                                          idx += 1;
                                        }
                                      } else {
                                        let idx = spans.length - 1;
                                        let guard = 0;
                                        while (diff < 0 && guard < 200) {
                                          const j = ((idx % spans.length) + spans.length) % spans.length;
                                          if (spans[j] > minSpan) {
                                            spans[j] -= 1;
                                            diff += 1;
                                          }
                                          idx -= 1;
                                          guard += 1;
                                        }
                                        if (guard >= 200) break;
                                      }
                                    }
                                    return spans;
                                  };
                                  const buildEvenRows = (items) => {
                                    const rows = [];
                                    let i = 0;
                                    while (i < items.length) {
                                      const remain = items.length - i;
                                      if (remain <= 4) {
                                        rows.push(items.slice(i, i + remain));
                                        break;
                                      }

                                      const nextFour = items.slice(i, i + 4);
                                      const portraitCount = nextFour.filter((att) => ratioOf(att) < 0.92).length;
                                      const portraitShare = nextFour.length ? (portraitCount / nextFour.length) : 0;
                                      const desiredCount = portraitShare >= 0.5 ? 4 : 3;

                                      const candidates = [4, 3, 2]
                                        .filter((n) => n <= remain)
                                        .filter((n) => {
                                          const tail = remain - n;
                                          return tail === 0 || tail >= 2;
                                        });

                                      let take = 3;
                                      let bestScore = Number.POSITIVE_INFINITY;
                                      for (let c = 0; c < candidates.length; c += 1) {
                                        const n = candidates[c];
                                        let ratioSum = 0;
                                        for (let k = 0; k < n; k += 1) {
                                          ratioSum += ratioOf(items[i + k]);
                                        }
                                        const avgRatio = n > 0 ? ratioSum / n : 1;
                                        const densityPenalty = avgRatio > 1.35 && n === 4 ? 0.55 : 0;
                                        const sparsePenalty = n === 2 ? 0.35 : 0;
                                        const score = Math.abs(n - desiredCount) + densityPenalty + sparsePenalty;
                                        if (score < bestScore) {
                                          bestScore = score;
                                          take = n;
                                        }
                                      }

                                      rows.push(items.slice(i, i + take));
                                      i += take;
                                    }
                                    return rows;
                                  };

                                  if (isEvenAlbum) {
                                    const albumRows = buildEvenRows(imageAttachments);
                                    return (
                                      <div className={s.messageMediaAttach} style={{ display: 'grid', gap: 8, marginTop: 8 }}>
                                        <div className={s.imageAlbumAdaptive} style={{ width: mediaPreviewWidth, maxWidth: mediaPreviewWidth }}>
                                          {albumRows.map((row, rowIdx) => {
                                            const weights = row.map((att) => Math.max(0.66, Math.min(1.9, ratioOf(att))));
                                            const spans = distributeSpans(weights, 12);
                                            const rowHeight = `calc((${mediaPreviewWidth} - ${(row.length - 1) * 2}px) / ${row.length})`;
                                            return (
                                              <div
                                                key={`row-${rowIdx}`}
                                                className={s.imageAlbumRow}
                                                style={{ height: rowHeight }}
                                              >
                                                {row.map((att, idx) => {
                                                  const viewUrl = getViewUrl(att.s3_key);
                                                  return (
                                                    <button
                                                      key={att.id || att.s3_key}
                                                      type="button"
                                                      className={s.imageAlbumTileBtn}
                                                      style={{
                                                        gridColumn: `span ${spans[idx] || 6}`,
                                                        height: '100%',
                                                        aspectRatio: 'auto',
                                                      }}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        openImageFromGallery(viewUrl, imageUrls);
                                                      }}
                                                      aria-label="Открыть фото"
                                                    >
                                                      <img
                                                        src={viewUrl}
                                                        alt=""
                                                        loading="lazy"
                                                        className={s.imageAlbumTileImg}
                                                        onLoad={(e) => {
                                                          const img = e.currentTarget;
                                                          rememberImageNaturalSize(
                                                            viewUrl,
                                                            img.naturalWidth,
                                                            img.naturalHeight,
                                                          );
                                                          onChatMediaLoaded();
                                                        }}
                                                      />
                                                    </button>
                                                  );
                                                })}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  }

                                  const albumClassName = [
                                    s.imageAlbum,
                                    imageAttachments.length === 2 ? s.imageAlbumTwo : '',
                                    imageAttachments.length === 3 ? s.imageAlbumThree : '',
                                    imageAttachments.length >= 4 ? s.imageAlbumFour : '',
                                    imageAttachments.length > 3 && imageAttachments.length % 2 === 1 ? s.imageAlbumOddHead : '',
                                  ].filter(Boolean).join(' ');

                                  return (
                                    <div className={s.messageMediaAttach} style={{ display: 'grid', gap: 8, marginTop: 8 }}>
                                      <div className={albumClassName} style={{ width: mediaPreviewWidth, maxWidth: mediaPreviewWidth }}>
                                        {imageAttachments.map((att) => {
                                          const viewUrl = getViewUrl(att.s3_key);
                                          return (
                                            <button
                                              key={att.id || att.s3_key}
                                              type="button"
                                              className={s.imageAlbumTileBtn}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openImageFromGallery(viewUrl, imageUrls);
                                              }}
                                              aria-label="Открыть фото"
                                            >
                                              <img
                                                src={viewUrl}
                                                alt=""
                                                loading="lazy"
                                                className={s.imageAlbumTileImg}
                                                onLoad={(e) => {
                                                  const img = e.currentTarget;
                                                  rememberImageNaturalSize(
                                                    viewUrl,
                                                    img.naturalWidth,
                                                    img.naturalHeight,
                                                  );
                                                  onChatMediaLoaded();
                                                }}
                                              />
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                }

                                return (
                                  <div className={s.messageMediaAttach} style={{ display: 'grid', gap: 8, marginTop: 8 }}>
                                    {attachments.map((att) => renderAttachment(att))}
                                  </div>
                                );
                              })()}

                              {isMine && hasVisualMediaAttachment && (
                                <>
                                  {isTemp ? (
                                    <span className={s.msgTicks} aria-label="Отправляется" title="Отправляется" style={{ color: '#9CA3AF' }}>
                                      <SendingTickIcon className={s.tick} aria-hidden="true" />
                                    </span>
                                  ) : (
                                    <span
                                      className={`${s.msgTicks} ${readByOpponent ? s.msgTicksRead : ''}`}
                                      aria-label={readByOpponent ? 'Прочитано' : 'Отправлено'}
                                      title={readByOpponent ? 'Прочитано' : 'Отправлено'}
                                    >
                                      <ReadTicksIcon className={s.tick} aria-hidden="true" />
                                    </span>
                                  )}
                                </>
                              )}

                              {isFailed && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                                  <span style={{ fontSize: 12, color: '#b00' }}>Не отправлено</span>
                                  {isMine && typeof onRetrySend === 'function' && (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); onRetrySend(message); }}
                                      className={s.editDockSave}
                                      title="Повторить отправку"
                                      aria-label="Повторить отправку"
                                      style={{ padding: '4px 8px' }}
                                    >
                                      Повторить
                                    </button>
                                  )}
                                </div>
                              )}

                              <AnimatePresence initial={false}>
                                {showReactionStrip && (
                                <motion.div
                                  className={[
                                    s.messageReactionsRow,
                                    s.messageReactionsRowInBubble,
                                    isMine ? s.messageReactionsRowInBubbleMine : '',
                                  ].filter(Boolean).join(' ')}
                                  role="group"
                                  initial={{ opacity: 0, height: 0, marginTop: 0, y: 4, scale: 0.98 }}
                                  animate={{ opacity: 1, height: 'auto', marginTop: 14, y: 0, scale: 1 }}
                                  exit={{ opacity: 0, height: 0, marginTop: 0, y: 3, scale: 0.98 }}
                                  transition={REACTION_ROW_TRANSITION}
                                  style={{
                                    overflow: 'hidden',
                                    transformOrigin: isMine ? 'right top' : 'left top',
                                  }}
                                  aria-label="Реакции"
                                  onClickCapture={(e) => {
                                    e.stopPropagation();
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <AnimatePresence initial={false}>
                                    {msgReactions.map((r) => {
                                      return (
                                        <motion.button
                                          key={r.emoji}
                                          type="button"
                                          className={[
                                            s.reactionChip,
                                            s.reactionChipInBubble,
                                            r.reacted_by_me ? s.reactionChipMine : '',
                                          ].filter(Boolean).join(' ')}
                                          title="Короткое нажатие — поставить или снять. Удерживание или ПКМ — кто отреагировал."
                                          aria-label={`Реакция ${r.emoji}, ${r.count}`}
                                          initial={{ opacity: 0, scale: 0.86, y: 4 }}
                                          animate={{ opacity: 1, scale: 1, y: 0 }}
                                          exit={{ opacity: 0, scale: 0.9, y: -3 }}
                                          whileHover={{ scale: 1.06 }}
                                          whileTap={{ scale: 0.94 }}
                                          transition={REACTION_SPRING_FAST}
                                          onClickCapture={(e) => {
                                            e.stopPropagation();
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                          }}
                                          onPointerDown={(e) => onReactionChipPointerDown(e, message.id, r.emoji)}
                                          onPointerUp={(e) => onReactionChipPointerUp(e, message.id, r.emoji)}
                                          onPointerCancel={onReactionChipPointerCancel}
                                          onPointerLeave={onReactionChipPointerCancel}
                                          onContextMenu={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const ref = chipLongPressRef.current;
                                            if (ref.timer) {
                                              clearTimeout(ref.timer);
                                              ref.timer = null;
                                            }
                                            ref.fired = true;
                                            openReactionsSummary(message.id, r.emoji);
                                          }}
                                        >
                                          <motion.span
                                            className={s.reactionChipEmojiWrap}
                                            animate={r.reacted_by_me ? { scale: [1, 1.15, 1], y: [0, -2, 0] } : { scale: 1, y: 0 }}
                                            transition={{ duration: 0.28 }}
                                          >
                                            <ReactionGlyph
                                              emoji={r.emoji}
                                              className={`${s.reactionChipEmoji} ${s.reactionCustomGlyph}`}
                                            />
                                          </motion.span>
                                          {r.count > 0 ? (
                                            <span className={s.reactionChipCount}>{r.count}</span>
                                          ) : null}
                                        </motion.button>
                                      );
                                    })}
                                  </AnimatePresence>
                                </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          </div>
                        </li>
                      </React.Fragment>
                    );
                  })}
                  <li ref={bottomLoadSentinelRef} className={s.historyLoadSentinel} aria-hidden="true" />
                  <div ref={messagesEndRef} id="chat-end-anchor" />
                </ul>
              </InfiniteScroll>
            )}
          </div>

          {isOpen && !searchOpen && !searchMode
            && ((unseenReactionMessageIds && unseenReactionMessageIds.length > 0) || reactionJumpHint) ? (
            <button
              type="button"
              className={`${s.reactionJumpFab}${stackReactionJumpFab ? ` ${s.reactionJumpFabStacked}` : ''}`}
              onClick={onReactionJumpFabClick}
              aria-label="Перейти к сообщениям с непросмотренными реакциями"
              title="По очереди к вашим сообщениям с новыми реакциями (выше по ленте)"
            >
              <span className={s.reactionJumpFabBadge} aria-hidden="true">
                {(unseenReactionMessageIds && unseenReactionMessageIds.length > 0)
                  ? (unseenReactionMessageIds.length > 99 ? '99+' : unseenReactionMessageIds.length)
                  : '1'}
              </span>
              <svg
                className={s.reactionJumpHeartOutline}
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                />
              </svg>
            </button>
          ) : null}

          <button
            className={`${s.scrollDownBtn}${showScrollDownFab ? ` ${s.visible}` : ''}`}
            onClick={async () => {
              if (jumpingToBottom) return;
              setJumpingToBottom(true);
              try {
                endScrollAnchorLock();
                prependStabilizeUntilRef.current = 0;
                scrollJumpLockRef.current = false;
                initialFeedClampPendingRef.current = false;
                openBottomClampUntilRef.current = Date.now() + CHAT_OPEN_BOTTOM_CLAMP_MS;
                onRequestLiveBottom?.();

                if (hasMoreNewer && typeof onJumpToLatestWindow === 'function') {
                  await onJumpToLatestWindow();
                } else {
                  const latestVisibleId = Array.isArray(chatMessages) && chatMessages.length
                    ? chatMessages[chatMessages.length - 1]?.id
                    : null;
                  if (hasMoreNewer && latestVisibleId && typeof onLoadAroundForSearch === 'function') {
                    await onLoadAroundForSearch(latestVisibleId, { before: 8, after: 120 });
                  }
                }

                const container = messagesContainerRef?.current;
                if (container) {
                  forceScrollContainerToBottom(container);
                  requestAnimationFrame(() => {
                    clampChatToBottom('open');
                    clampChatToBottom('contentResize');
                  });
                }
              } finally {
                setJumpingToBottom(false);
              }
            }}
            aria-label="Прокрутить вниз"
            title="Прокрутить вниз"
          >
            {unreadBelowCount > 0 && (
              <span className={s.scrollDownBadge}>
                {unreadBelowCount > 99 ? '99+' : unreadBelowCount}
              </span>
            )}
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {/* Edit dock */}
          {isEditingMessageId && (
            <div ref={editDockRef} className={s.editDock} role="dialog" aria-label="Редактирование сообщения">
              <div className={s.editDockHeader}>
                <div className={s.editDockMeta}>
                  <div className={s.editDockLabel}>Редактирование</div>
                  <div className={s.editDockTitle}>
                    {(editingMessage?.content || '').trim() || 'Сообщение без текста'}
                  </div>
                </div>
                <button
                  type="button"
                  className={s.editDockClose}
                  aria-label="Закрыть редактирование"
                  onClick={onCancelEdit}
                >
                  ×
                </button>
              </div>

              <div className={s.editDockBody}>
              </div>
            </div>
          )}

          {/* Reply preview — клик ведёт к сообщению той же логикой, что поиск */}
          {replyDraft && (
            <div ref={replyDockRef} className={s.replyPreviewBar}>
              <div
                className={s.replyPreviewText}
                role="button"
                tabIndex={0}
                title="Перейти к сообщению"
                onClick={() => {
                  if (!replyDraft.id || aroundLoading) return;
                  onMessagesScroll?.(false);
                  jumpToMessage(replyDraft.id);
                  onFlashMessage?.(replyDraft.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (!replyDraft.id || aroundLoading) return;
                    onMessagesScroll?.(false);
                    jumpToMessage(replyDraft.id);
                    onFlashMessage?.(replyDraft.id);
                  }
                }}
              >
                <div className={s.replyPreviewLine}>
                  <span className={s.replyPreviewLabel}>Ответить:</span>
                  {replyDraft.author}
                </div>
                <div className={`${s.replyPreviewLine} ${s.replyPreviewLineWithMedia}`}>
                  <span className={s.replyPreviewLabel}>На сообщение:</span>
                  {renderReplyPreviewInline(replyDraft.replyTo, { maxTextLen: 80 })}
                </div>
              </div>
              <button
                type="button"
                className={s.replyClose}
                onClick={() => setReplyDraft(null)}
                aria-label="Отменить ответ"
                title="Отменить ответ"
              >
                ×
              </button>
            </div>
          )}

          {currentChatPickedItems.length > 0 && (
            <div className={s.pickedPanel} aria-live="polite" onClick={(e) => e.stopPropagation()}>
              <div className={s.pickedHeader}>
                <div className={s.pickedTitle}>выбрано файлов: {currentChatPickedItems.length}</div>
                <button
                  type="button"
                  onClick={handleClearPicked}
                  title={isUploadingCurrentChat ? 'Отменить загрузку всех файлов' : 'Снять выбор'}
                  aria-label={isUploadingCurrentChat ? 'Отменить загрузку всех файлов' : 'Снять выбор файлов'}
                  className={s.pickedClear}
                >
                  ×
                </button>
              </div>

              <div className={s.pickedGrid}>
                {currentChatPickedItems.map((it) => {
                  const pct = isUploadingCurrentChat ? (uploadProgress?.[it.id] ?? 0) : null;
                  const name = it?.file?.name || 'file';
                  const size = it?.file?.size ? humanSize(it.file.size) : '';
                  const showMedia = Boolean(it.previewUrl) && (it.kind === 'image' || it.kind === 'video');
                  const showAudio = Boolean(it.previewUrl) && it.kind === 'audio';

                  return (
                    <div key={it.id} className={s.pickedItem}>
                      <div className={s.pickedThumb}>
                        {showMedia && it.kind === 'image' && (
                          <img className={s.pickedMedia} src={it.previewUrl} alt="" />
                        )}
                        {showMedia && it.kind === 'video' && (
                          <video className={s.pickedMedia} src={it.previewUrl} muted playsInline />
                        )}
                        {showAudio && <div className={s.pickedAudioIcon} aria-hidden="true">🎵</div>}
                        {!it.previewUrl && <div className={s.pickedFileIcon} aria-hidden="true">📎</div>}

                        {isUploadingCurrentChat && (
                          <div className={s.pickedOverlay}>
                            <div className={s.pickedSpinner} aria-hidden="true" />
                            <div className={s.pickedPct}>{pct}%</div>
                          </div>
                        )}
                      </div>

                      <div className={s.pickedMeta} title={`${name}${size ? ` • ${size}` : ''}`}>
                        <div className={s.pickedName}>{name}</div>
                        <div className={s.pickedSize}>{size || (it.file?.type || 'file')}</div>
                      </div>

                      <button
                        type="button"
                        className={s.pickedRemove}
                        aria-label={isUploadingCurrentChat ? `Отменить загрузку ${name}` : `Убрать ${name}`}
                        title={isUploadingCurrentChat ? 'Отменить загрузку' : 'Убрать'}
                        onClick={() => removePickedItem(it.id)}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {searchOpen && normalizedSearch && (
            <div className={s.searchNav} onClick={(e) => e.stopPropagation()}>
              <div className={s.searchNavCounter}>
                {searchIds.length
                  ? `${activeMatchIndex + 1} из ${searchTotal || searchIds.length}`
                  : (searchLoading ? 'Поиск…' : '0 из 0')}
              </div>

              <div className={s.searchNavBtns}>
                <button
                  type="button"
                  className={s.searchNavBtn}
                  aria-label="Предыдущее совпадение"
                  disabled={disableSearchPrev}
                  onClick={() => goToMatch(activeMatchIndex - 1)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M7 14l5-5 5 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                <button
                  type="button"
                  className={s.searchNavBtn}
                  aria-label="Следующее совпадение"
                  disabled={disableSearchNext}
                  onClick={() => goToMatch(activeMatchIndex + 1)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          </div>

          {/* Send form */}
          <form ref={sendFormRef} className={s.sendForm} onSubmit={handleSubmit}>
            <input
              id="chatFileInput"
              type="file"
              accept="image/*,video/*,audio/*,application/pdf,application/vnd.ms-outlook,.msg,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.presentationml.slideshow,text/plain,application/zip,application/x-zip-compressed,application/x-7z-compressed,application/vnd.rar,application/x-rar-compressed,application/x-tar,application/gzip,application/x-gzip,application/x-bzip2,application/x-xz"
              multiple
              onChange={handlePickFiles}
              style={{ display: 'none' }}
            />

            <div
              className={s.sendInputWrap}
              style={{ position: 'relative', flex: '1 1 auto', display: 'flex', minWidth: 0, '--composer-right-pad': `${isRecording ? 160 : 130}px` }}
            >
              {composerPreviewEnabled && (
                <div
                  ref={composerPreviewRef}
                  className={s.sendTextareaEmojiPreview}
                  aria-hidden="true"
                >
                  {renderTextWithAppleEmoji(composerValue)}
                </div>
              )}
              <Textarea
                key={currentChat?.id || 'none'}
                textareaRef={textareaRef}
                wrapperClassName={s.sendTextareaWrap}
                className={`${s.sendTextarea} ${isImportant && !isEditingMessageId ? s.sendTextareaImportant : ''} ${composerPreviewEnabled ? s.sendTextareaWithPreview : ''}`}
                onPaste={handlePaste}
                placeholder={composerPlaceholder}
                value={composerValue}
                onChange={(e) => {
                  if (isEditingMessageId) {
                    setEditingText(e.target.value);
                  } else {
                    setNewMessage(e.target.value);
                  }
                }}
                rows={1}
                maxRows={6}
                onKeyDown={(e) => {
                  if (
                    e.key === 'ArrowUp'
                    && !isEditingMessageId
                    && !isUploadingCurrentChat
                    && currentChatPickedItems.length === 0
                    && String(composerValue || '').trim().length === 0
                    && (e.currentTarget.selectionStart ?? 0) === 0
                    && (e.currentTarget.selectionEnd ?? 0) === 0
                  ) {
                    e.preventDefault();
                    onStartEditLastMessage?.();
                    return;
                  }

                  if (e.key === 'Enter' && reactionHoverDesktop && !e.shiftKey) {
                    e.preventDefault();
                    if (isUploadingCurrentChat) return;
                    const hasText = (composerValue || '').trim().length > 0;
                    const hasFiles = !isEditingMessageId && currentChatPickedItems.length > 0;
                    if (!hasText && !hasFiles) return;
                    handleSubmit(e);
                  }
                }}
                autoComplete="off"
                required={textareaRequired}
                disabled={isUploadingCurrentChat}
                onScroll={(e) => {
                  if (!composerPreviewRef.current) return;
                  composerPreviewRef.current.scrollTop = e.currentTarget.scrollTop;
                  composerPreviewRef.current.scrollLeft = e.currentTarget.scrollLeft;
                }}
                style={{ paddingRight: 'var(--composer-right-pad)' }}
              />

              {!isEditingMessageId && (
                <>
                  {/* Кнопка записи голосового: клик для старта/остановки */}
                  <button
                    type="button"
                    title={isRecording ? `Остановить запись: ${recSeconds} c` : 'Начать запись'}
                    aria-label={isRecording ? 'Остановить запись' : 'Начать запись'}
                    disabled={isUploadingCurrentChat}
                    onClick={onMicClick}
                    onContextMenu={(e) => e.preventDefault()}
                    className={`${s.chatMicBtn} ${isRecording ? s.chatMicBtnRecording : ''}`}
                  >
                    <MicrophoneIcon className={s.chatMicIcon} />
                    {isRecording && (
                      <span className={s.chatMicTimer} aria-live="polite">
                        {fmtRec(recSeconds)}
                      </span>
                    )}
                  </button>

                  <label
                    htmlFor="chatFileInput"
                    className={s.attachBtnInside}
                    title={currentChatPickedItems.length ? `Выбрано файлов: ${currentChatPickedItems.length}` : 'Прикрепить файл'}
                    aria-label="Прикрепить файл"
                  >
                    <SendAttachmentIcon
                      className={s.attachIcon}
                      style={{ width: 18, height: 18 }}
                    />
                  </label>
                  <div ref={emojiPickerRef} style={{ position: 'relative' }}>
                    <button
                      type="button"
                      className={s.emojiBtn}
                      aria-label="Эмодзи"
                      title="Эмодзи"
                      onClick={() => setEmojiPickerOpen(v => !v)}
                      disabled={isUploadingCurrentChat}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                        <line x1="9" y1="9" x2="9.01" y2="9" />
                        <line x1="15" y1="9" x2="15.01" y2="9" />
                      </svg>
                    </button>

                    {emojiPickerOpen && (
                      <div className={s.emojiPickerWrap}>
                        {emojiMartData ? (
                          <Picker
                            data={emojiMartData}
                            set="google"
                            onEmojiSelect={handleEmojiSelect}
                            locale="ru"
                            theme="light"
                            previewPosition="none"
                            skinTonePosition="none"
                          />
                        ) : (
                          <div className={s.emojiPickerLoading}>
                            {emojiMartDataLoading ? 'Загружаем эмодзи…' : 'Не удалось загрузить эмодзи'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <button
              ref={sendBtnRef}
              type="submit"
              className={s.sendBtn}
              disabled={sendDisabled}
              aria-label={isEditingMessageId ? 'Сохранить изменения' : 'Отправить сообщение'}
              title={isEditingMessageId ? 'Сохранить' : 'Отправить'}
              onPointerDown={isEditingMessageId ? undefined : handleSendPointerDown}
              onPointerUp={isEditingMessageId ? undefined : handleSendPointerUp}
              onPointerCancel={isEditingMessageId ? undefined : handleSendPointerCancel}
              onPointerLeave={isEditingMessageId ? undefined : handleSendPointerCancel}
            >
              {isEditingMessageId ? (
                <ConfirmEditIcon className={s.sendIcon} aria-hidden="true" focusable="false" />
              ) : (
                <SendMessageIcon className={s.sendIcon} />
              )}
            </button>
          </form>

          {deleteConfirmOpen && (
            <div
              className={s.deleteConfirmOverlay}
              role="dialog"
              aria-modal="true"
              aria-label="Подтверждение удаления"
              onClick={() => {
                if (deleteConfirmBusy) return;
                setDeleteConfirmOpen(false);
                onCloseActionBar?.();
              }}
            >
              <div
                className={s.deleteConfirmCard}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={s.deleteConfirmHeader}>
                  <div className={s.deleteConfirmTitle}>
                    Удалить {selectedCount === 1 ? 'сообщение' : 'сообщения'}?
                  </div>
                  <button
                    type="button"
                    className={s.modalClose}
                    aria-label="Закрыть"
                    onClick={() => {
                      if (deleteConfirmBusy) return;
                      setDeleteConfirmOpen(false);
                      onCloseActionBar?.();
                    }}
                    disabled={deleteConfirmBusy}
                  >
                    ✕
                  </button>
                </div>

                <div className={s.deleteConfirmBody}>
                  {selectedCount === 1 ? (
                    <>Это действие удалит сообщение без возможности восстановления.</>
                  ) : (
                    <>Это действие удалит {selectedCount} сообщений без возможности восстановления.</>
                  )}
                </div>

                <div className={s.deleteConfirmActions}>
                  <button
                    type="button"
                    className={s.deleteConfirmBtn + ' ' + s.deleteConfirmBtnCancel}
                    onClick={() => {
                      if (deleteConfirmBusy) return;
                      setDeleteConfirmOpen(false);
                      onCloseActionBar?.();
                    }}
                    disabled={deleteConfirmBusy}
                  >
                    Отменить
                  </button>
                  <button
                    type="button"
                    className={s.deleteConfirmBtn + ' ' + s.deleteConfirmBtnDanger}
                    onClick={handleConfirmDelete}
                    disabled={deleteConfirmBusy}
                    title="Удалить"
                  >
                    {deleteConfirmBusy ? 'Удаляю…' : 'Удалить'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {pinnedFeedOpen && (
            <div
              className={s.pinnedFeedScreen}
              role="dialog"
              aria-modal="true"
              aria-label="Закреплённые сообщения"
            >
              <header className={s.pinnedFeedHeader}>
                <button
                  type="button"
                  className={s.backBtn}
                  aria-label="Назад к чату"
                  onClick={() => closePinnedFeed()}
                >
                  <LeaveChatIcon className={s.icon} />
                </button>
                <div className={s.pinnedFeedTitle}>
                  {ruPinnedScreenTitle(pinnedFeedItems.length)}
                </div>
                <span className={s.pinnedFeedHeaderSpacer} aria-hidden="true" />
              </header>

              <div className={s.pinnedFeedBody}>
                {pinnedFeedRows.length === 0 ? (
                  <div className={s.pinnedFeedEmpty}>Нет закреплённых сообщений</div>
                ) : (
                  <ul className={s.messagesList}>
                    {pinnedFeedRows.map((row) => {
                      if (row.kind === 'date') {
                        const d = new Date(row.ts);
                        const label = Number.isNaN(d.getTime())
                          ? ''
                          : d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
                        return (
                          <li key={row.key} className={s.pinnedFeedRowDate} role="presentation">
                            <span className={s.pinnedFeedDateChip}>{label}</span>
                          </li>
                        );
                      }

                      const message = row.message;
                      const msgForActions = messageById.get(String(message.id)) || message;
                      const isDeleted = Boolean(msgForActions.deleted_at);
                      const isEditing = isEditingMessageId === msgForActions.id;
                      const isMine = String(msgForActions.sender_id) === String(myId);
                      const isGroup = currentChatType === 'group' || currentChatType === 'rank';
                      const readByOpponent = isMessageReadByCounterparty(msgForActions);
                      const isSystem = (msgForActions.sender_name || '').trim().toLowerCase() === 'система';
                      const raw = msgForActions.sender_avatar_url || msgForActions.sender_avatar || msgForActions.avatar;
                      const avatarSrc =
                        resolveImageUrl(raw)
                        || (isSystem ? resolveImageUrl('/uploads/user_avatars/system_avatar.png') : null);
                      const hasAudioAttachment = Array.isArray(msgForActions.attachments)
                        && msgForActions.attachments.some((att) => att?.kind === 'audio');
                      const hasVisualMediaAttachment = Array.isArray(msgForActions.attachments)
                        && msgForActions.attachments.some((att) => att?.kind === 'image' || att?.kind === 'video');
                      const pinnedMediaPreviewWidth = hasVisualMediaAttachment
                        ? getChatMediaPreviewWidth({
                          isGroup,
                          isMine,
                          attachments: msgForActions.attachments,
                        })
                        : null;
                      const pinnedMessageContentStyle = pinnedMediaPreviewWidth
                        ? { maxWidth: pinnedMediaPreviewWidth }
                        : undefined;
                      const replyId =
                        msgForActions.reply_to_message_id
                        ?? (msgForActions.reply_to && msgForActions.reply_to.id != null ? msgForActions.reply_to.id : null);
                      const hasReply = !!replyId;
                      const replyFromList = hasReply
                        ? messageById.get(String(replyId))
                        : null;
                      const replyTo = replyFromList || msgForActions.reply_to || null;

                      return (
                        <li
                          id={`pinned-feed-msg-${msgForActions.id}`}
                          key={row.key}
                          role="button"
                          tabIndex={0}
                          className={[
                            s.messageRow,
                            isGroup && !isMine ? s.messageRowWithAvatar : '',
                          ].filter(Boolean).join(' ')}
                          style={{
                            justifyContent: isMine ? 'flex-end' : 'flex-start',
                          }}
                          onClick={(e) => {
                            if (isDeleted || isEditing) return;
                            if (Date.now() < suppressMessageRowClicksUntilRef.current) return;
                            if (suppressNextMessageRowClickRef.current) {
                              suppressNextMessageRowClickRef.current = false;
                              return;
                            }
                            const tappedInsideBubble = !!e.target?.closest?.(`.${s.messageBubble}`);
                            if (tappedInsideBubble) return;
                            const mediaTarget = e.target?.closest?.(
                              `img,video,audio,.${s.audioWave},.${s.audioMessage},.${s.imageAlbumTileBtn}`,
                            );
                            if (mediaTarget) return;
                            handlePinnedFeedPick(msgForActions);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              if (!isDeleted && !isEditing) handlePinnedFeedPick(msgForActions);
                            }
                          }}
                          onContextMenu={(e) => {
                            if (isDeleted || isEditing) return;
                            const preferNativeTextMenu = typeof window !== 'undefined'
                              && window.matchMedia?.('(pointer: coarse)').matches;
                            if (preferNativeTextMenu && e.target?.closest?.('[data-chat-selectable]')) return;
                            if (Date.now() < suppressMessageRowClicksUntilRef.current || multiSelectMode) {
                              e.preventDefault();
                              e.stopPropagation();
                              return;
                            }
                            const interactiveTarget = e.target?.closest?.('a[href],button,input,textarea,select,label,video,audio');
                            if (interactiveTarget) return;
                            e.preventDefault();
                            e.stopPropagation();

                            if (
                              messageActionMenu
                              && String(messageActionMenu.messageId) === String(msgForActions.id)
                            ) {
                              setMessageActionMenu(null);
                              setMessageActionReactionsExpanded(false);
                              onCloseActionBar?.();
                              return;
                            }
                            if (messageActionMenu) {
                              setMessageActionMenu(null);
                              setMessageActionReactionsExpanded(false);
                              onCloseActionBar?.();
                              return;
                            }

                            messageActionAnchorRef.current = {
                              x: e.clientX,
                              y: e.clientY,
                              messageId: msgForActions.id,
                            };
                            onMessageClick?.(msgForActions);
                          }}
                          onPointerDown={!reactionHoverDesktop ? (e) => onMobileMessageRowPointerDown(e, msgForActions) : undefined}
                          onPointerMove={!reactionHoverDesktop ? onMobileMessageRowPointerMove : undefined}
                          onPointerUp={!reactionHoverDesktop ? onMobileMessageRowPointerEnd : undefined}
                          onPointerCancel={!reactionHoverDesktop ? onMobileMessageRowPointerEnd : undefined}
                        >
                          {isGroup && !isMine && (
                            isSystem ? (
                              <Avatar
                                className={`${s.avatar} ${s.headerAvatarClickable}`}
                                src={avatarSrc}
                                name={msgForActions.sender_name || `Пользователь ${msgForActions.sender_id}`}
                                alt={msgForActions.sender_name || `Пользователь ${msgForActions.sender_id}`}
                                size={48}
                                role="button"
                                tabIndex={0}
                                onClick={(e) => handleMessageAvatarClick(e, msgForActions.sender_id, msgForActions.sender_name)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    handleMessageAvatarClick(e, msgForActions.sender_id, msgForActions.sender_name);
                                  }
                                }}
                              />
                            ) : (
                              <ChatSenderAvatar
                                employeeId={msgForActions.sender_id}
                                avatarUrl={avatarSrc}
                                employeeTaskStatsMap={employeeTaskStatsMap}
                                size={48}
                                className={`${s.avatar} ${s.headerAvatarClickable}`}
                                alt={msgForActions.sender_name || `Пользователь ${msgForActions.sender_id}`}
                                onClick={(e) => handleMessageAvatarClick(e, msgForActions.sender_id, msgForActions.sender_name)}
                              />
                            )
                          )}
                          <div
                            className={`${s.pinnedFeedMsgMain} ${isMine ? s.pinnedFeedMsgMainMine : ''}`}
                          >
                            <div
                              className={[
                                s.messageColumn,
                                hasAudioAttachment ? s.messageColumnWithAudio : '',
                              ].filter(Boolean).join(' ')}
                              style={{ alignItems: isMine ? 'flex-end' : 'flex-start' }}
                            >
                              <div
                                className={[
                                  s.messageBubble,
                                  isMine ? s.messageBubbleMine : '',
                                  (isGroup && !isMine && msgForActions.sender_name) || msgForActions.forwarded_from_message_id
                                    ? s.messageBubbleWithSender
                                    : '',
                                  hasAudioAttachment ? s.messageBubbleWithAudio : '',
                                  hasVisualMediaAttachment ? s.messageBubbleWithMedia : '',
                                ].filter(Boolean).join(' ')}
                              style={{
                                ...(isMine ? { background: '#f4e2ff' } : {}),
                                ...(isGroup ? { boxSizing: 'border-box' } : {}),
                              }}
                              onClickCapture={(e) => {
                                if (reactionHoverDesktop || multiSelectMode || isDeleted || isEditing) return;
                                if (Date.now() < suppressMessageRowClicksUntilRef.current) return;
                                const mediaTarget = e.target?.closest?.(
                                  `a,img,video,audio,[data-chat-file-preview="true"],.${s.audioWave},.${s.audioMessage},.${s.imageAlbumTileBtn},.${s.messageReactionsRow},.${s.reactionChip},.${s.reactionChipEmojiWrap},.${s.reactionChipCount},.${s.pinnedFeedReactionsReadonly},.${s.pinnedFeedJumpBtn}`,
                                );
                                if (mediaTarget) return;
                                if (suppressNextMessageRowClickRef.current) {
                                  suppressNextMessageRowClickRef.current = false;
                                  return;
                                }
                                const selection = window.getSelection?.();
                                const hasTextSelection = !!selection && !selection.isCollapsed && String(selection).trim().length > 0;
                                const selectedInsideBubble = hasTextSelection && selection.rangeCount > 0
                                  ? e.currentTarget.contains(selection.getRangeAt(0).commonAncestorContainer)
                                  : false;
                                if (selectedInsideBubble) return;
                                messageActionAnchorRef.current = {
                                  x: e.clientX,
                                  y: e.clientY,
                                  messageId: msgForActions.id,
                                };
                                onMessageClick?.(msgForActions);
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              {msgForActions.forwarded_from_message_id ? (
                                <div className={s.messageBubbleTopRow}>
                                  <div className={s.msgForwarded} data-chat-selectable="1">
                                    Переслано от {msgForActions.forwarded_from_sender_name || 'неизвестный автор'}
                                  </div>
                                  {renderMessageTimeRow(msgForActions)}
                                </div>
                              ) : isGroup && !isMine && msgForActions.sender_name ? (
                                <div className={s.messageBubbleTopRow}>
                                  <p className={s.messageSender} data-chat-selectable="1">
                                    <strong>{msgForActions.sender_name || `Пользователь ${msgForActions.sender_id}`}</strong>
                                  </p>
                                  {renderMessageTimeRow(msgForActions)}
                                </div>
                              ) : renderMessageTimeRow(msgForActions)}

                              {isMine && !hasVisualMediaAttachment && (
                                <span
                                  className={`${s.msgTicks} ${readByOpponent ? s.msgTicksRead : ''}`}
                                  aria-label={readByOpponent ? 'Прочитано' : 'Отправлено'}
                                  title={readByOpponent ? 'Прочитано' : 'Отправлено'}
                                >
                                  <ReadTicksIcon className={s.tick} aria-hidden="true" />
                                </span>
                              )}

                              {hasReply && (
                                <div
                                  data-chat-selectable="1"
                                  className={s.replyPreview}
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => e.stopPropagation()}
                                >
                                  {renderReplyPreviewBlock(replyTo, { fallbackText: 'Ответ' })}
                                </div>
                              )}

                              {msgForActions.forwarded_from_message_id ? (
                                <p className={s.messageContent} data-chat-selectable="1" style={pinnedMessageContentStyle}>
                                  {isDeleted ? (
                                    <span className={s.muted}>Сообщение удалено</span>
                                  ) : (
                                    renderTextWithLinks(msgForActions.content, '')
                                  )}
                                </p>
                              ) : (
                                <p className={s.messageContent} data-chat-selectable="1" style={pinnedMessageContentStyle}>
                                  {isDeleted ? (
                                    <span className={s.muted}>Сообщение удалено</span>
                                  ) : (
                                    renderTextWithLinks(msgForActions.content, '')
                                  )}
                                </p>
                              )}

                              {renderPinnedFeedAttachments(msgForActions, { isGroup, isMine })}

                              {isMine && hasVisualMediaAttachment && (
                                <span
                                  className={`${s.msgTicks} ${readByOpponent ? s.msgTicksRead : ''}`}
                                  aria-label={readByOpponent ? 'Прочитано' : 'Отправлено'}
                                  title={readByOpponent ? 'Прочитано' : 'Отправлено'}
                                >
                                  <ReadTicksIcon className={s.tick} aria-hidden="true" />
                                </span>
                              )}
                            </div>
                          </div>
                            <button
                              type="button"
                              className={s.pinnedFeedJumpBtn}
                              aria-label="К сообщению в чате"
                              title="К сообщению в чате"
                              disabled={aroundLoading}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (aroundLoading) return;
                                void handlePinnedFeedPick(msgForActions);
                              }}
                            >
                              {isMine ? (
                                <PinnedJumpToChatLeftIcon className={s.pinnedFeedJumpBtnSvg} aria-hidden />
                              ) : (
                                <PinnedJumpToChatRightIcon className={s.pinnedFeedJumpBtnSvg} aria-hidden />
                              )}
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {canPinOrUnpin && pinnedFeedItems.length > 0 ? (
                <footer className={s.pinnedFeedFooter}>
                  <button
                    type="button"
                    className={s.pinnedFeedBulkBtn}
                    disabled={pinnedFeedBulkBusy}
                    onClick={() => setPinnedFeedUnpinConfirmOpen(true)}
                  >
                    {ruUnpinAllCta(pinnedFeedItems.length)}
                  </button>
                </footer>
              ) : null}

              {pinnedFeedUnpinConfirmOpen && (
                <div
                  className={`${s.deleteConfirmOverlay} ${s.pinnedFeedUnpinConfirmOverlay}`}
                  role="dialog"
                  aria-modal="true"
                  aria-label="Подтверждение открепления"
                  onClick={() => {
                    if (pinnedFeedBulkBusy) return;
                    setPinnedFeedUnpinConfirmOpen(false);
                  }}
                >
                  <div
                    className={s.deleteConfirmCard}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className={s.deleteConfirmHeader}>
                      <div className={s.deleteConfirmTitle}>
                        {ruUnpinConfirmTitle(pinnedFeedItems.length)}
                      </div>
                      <button
                        type="button"
                        className={s.modalClose}
                        aria-label="Закрыть"
                        onClick={() => {
                          if (pinnedFeedBulkBusy) return;
                          setPinnedFeedUnpinConfirmOpen(false);
                        }}
                        disabled={pinnedFeedBulkBusy}
                      >
                        ✕
                      </button>
                    </div>

                    <div className={s.deleteConfirmBody}>
                      {ruUnpinConfirmBody(pinnedFeedItems.length)}
                    </div>

                    <div className={s.deleteConfirmActions}>
                      <button
                        type="button"
                        className={`${s.deleteConfirmBtn} ${s.deleteConfirmBtnCancel}`}
                        onClick={() => {
                          if (pinnedFeedBulkBusy) return;
                          setPinnedFeedUnpinConfirmOpen(false);
                        }}
                        disabled={pinnedFeedBulkBusy}
                      >
                        Отменить
                      </button>
                      <button
                        type="button"
                        className={`${s.deleteConfirmBtn} ${s.deleteConfirmBtnAccent}`}
                        onClick={() => handlePinnedFeedUnpinAll()}
                        disabled={pinnedFeedBulkBusy}
                        title="Открепить"
                      >
                        {pinnedFeedBulkBusy ? 'Открепляю…' : 'Открепить'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <MediaLightbox
            open={imageView.open}
            items={viewerItems}
            thumbs={imageView.thumbUrls}
            index={imageView.index}
            reverseNavigation
            onClose={closeImageViewer}
            onIndexChange={handleViewerIndexChange}
          />

        </div>

        {isChatSwitching && (!Array.isArray(chatMessages) || chatMessages.length === 0) && (
          <div className={s.chatLoadingOverlay}>
            <div className={s.chatLoader}></div>
          </div>
        )}

        <ChatMediaPanel
          isOpen={mediaPanelOpen}
          onClose={() => setMediaPanelOpen(false)}
          chat={currentChat}
          resolveImageUrl={resolveImageUrl}
          getPeerEmployeeId={getPeerEmployeeId}
          employeeTaskStatsMap={employeeTaskStatsMap}
          onOpenAttachmentPdfPreview={openAttachmentPdfPreview}
          suppressEscapeClose={Boolean(attachmentPdfPreview)}
        />

        {chatDragActive && !isUploadingCurrentChat && !isEditingMessageId && (
          <div className={s.chatDropOverlay} aria-hidden="true">
            <div className={s.chatDropCard}>
              <div className={s.chatDropTitle}>Отпустите файлы, чтобы прикрепить</div>
              <div className={s.chatDropHint}>Файлы будут добавлены как вложения к сообщению</div>
            </div>
          </div>
        )}
      </div>

      {typeof document !== 'undefined' && createPortal(
        <>
          {showContextActionMenu && (
            <motion.div
              ref={messageActionMenuRef}
              className={`${s.messageContextMenu} ${messageActionReactionsExpanded ? s.messageContextMenuExpandedOnly : ''}`}
              style={centerExpandedReactionsMenuMobile
                ? {
                  left: 20,
                  right: 20,
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  top: messageActionMenuPos.top,
                }
                : { left: messageActionMenuPos.left, top: messageActionMenuPos.top }}
              role="dialog"
              aria-label="Меню сообщения"
              initial={{ opacity: 0, y: 10, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.96 }}
              transition={{ duration: 0.16 }}
              onClick={(e) => e.stopPropagation()}
            >
              {!messageActionReactionsExpanded && !isPinnedFeedMessageMenu && (
                <motion.div className={s.contextReactionRow}>
                  {contextReactionPreview.map((em) => {
                    const active = selectedMessageReactions.has(em);
                    return (
                      <motion.button
                        key={`ctx-rx-${em}`}
                        type="button"
                        className={[
                          s.reactionPickerEmojiBtn,
                          s.contextReactionBtn,
                          active ? s.reactionPickerEmojiBtnActive : '',
                          active ? s.contextReactionBtnActive : '',
                        ].filter(Boolean).join(' ')}
                        whileHover={{ y: -2, scale: 1.12 }}
                        whileTap={{ scale: 0.88 }}
                        transition={REACTION_SPRING_FAST}
                        onClick={(ev) => {
                          if (!selectedMessage) return;
                          toggleMessageReaction(selectedMessage.id, em, { clientX: ev.clientX, clientY: ev.clientY });
                          setMessageActionMenu(null);
                          setMessageActionReactionsExpanded(false);
                          onCloseActionBar?.();
                        }}
                      >
                        <motion.span
                          className={s.reactionPickerEmojiGlyph}
                          animate={
                            active
                              ? { scale: [1, 1.14, 1], y: [0, -1, 0] }
                              : { scale: 1, y: 0 }
                          }
                          transition={{ duration: 0.28 }}
                        >
                          <ReactionGlyph emoji={em} className={s.reactionCustomGlyph} />
                        </motion.span>
                      </motion.button>
                    );
                  })}
                  <motion.button
                    type="button"
                    className={s.contextReactionExpandBtn}
                    aria-label="Показать все реакции"
                    onClick={() => setMessageActionReactionsExpanded(true)}
                    whileTap={{ scale: 0.92 }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </motion.button>
                </motion.div>
              )}

              {messageActionReactionsExpanded && !isPinnedFeedMessageMenu && (
                <motion.div
                  className={s.contextReactionsExpandedPanel}
                  initial={{ opacity: 0, y: -6, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.16 }}
                >
                  {ALLOWED_MESSAGE_REACTIONS_LIST.map((em) => {
                    const active = selectedMessageReactions.has(em);
                    return (
                      <motion.button
                        key={`ctx-rx-full-${em}`}
                        type="button"
                        className={[
                          s.reactionPickerEmojiBtn,
                          s.contextReactionBtn,
                          s.contextReactionExpandedBtn,
                          active ? s.reactionPickerEmojiBtnActive : '',
                          active ? s.contextReactionBtnActive : '',
                        ].filter(Boolean).join(' ')}
                        whileHover={{ y: -2, scale: 1.12 }}
                        whileTap={{ scale: 0.88 }}
                        transition={REACTION_SPRING_FAST}
                        onClick={(ev) => {
                          if (!selectedMessage) return;
                          toggleMessageReaction(selectedMessage.id, em, { clientX: ev.clientX, clientY: ev.clientY });
                          setMessageActionMenu(null);
                          setMessageActionReactionsExpanded(false);
                          onCloseActionBar?.();
                        }}
                      >
                        <motion.span
                          className={s.reactionPickerEmojiGlyph}
                          animate={
                            active
                              ? { scale: [1, 1.14, 1], y: [0, -1, 0] }
                              : { scale: 1, y: 0 }
                          }
                          transition={{ duration: 0.28 }}
                        >
                          <ReactionGlyph emoji={em} className={s.reactionCustomGlyph} />
                        </motion.span>
                      </motion.button>
                    );
                  })}
                </motion.div>
              )}

              {!messageActionReactionsExpanded && (
                <motion.div className={s.contextActionsList}>
                  {isPinnedFeedMessageMenu ? (
                    <>
                      {canPinOrUnpin ? (
                        <button
                          type="button"
                          className={s.contextActionItem}
                          onClick={async () => {
                            if (!selectedMessage) return;
                            try {
                              await onUnpinMessage?.(selectedMessage.id);
                            } catch {
                              /* onUnpinMessage уже показывает тост */
                            }
                            setMessageActionMenu(null);
                            setMessageActionReactionsExpanded(false);
                            onCloseActionBar?.();
                          }}
                          disabled={
                            selectedCount !== 1
                            || !selectedMessage
                            || selectedMessage.deleted_at
                            || selectedMessage.__optimistic
                            || selectedMessage.__failed
                          }
                        >
                          <PinMessageIcon className={s.contextActionIcon} />
                          <span>Открепить</span>
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className={s.contextActionItem}
                        onClick={async () => {
                          const copiedSelection = await copySelectedTextFromMessageActionMenu();
                          if (!copiedSelection) {
                            const text = (selectedMessage?.content || '').trim();
                            if (text && navigator?.clipboard?.writeText) {
                              try {
                                await navigator.clipboard.writeText(text);
                                showToast?.('Скопировано');
                              } catch {
                                onCopy?.();
                              }
                            } else {
                              onCopy?.();
                            }
                          }
                          setMessageActionMenu(null);
                          setMessageActionReactionsExpanded(false);
                          onCloseActionBar?.();
                        }}
                        disabled={
                          selectedCount !== 1
                          || !selectedMessage
                          || selectedMessage.deleted_at
                          || !pinnedFeedCanCopyText
                        }
                      >
                        <CopyMessageIcon className={s.contextActionIcon} />
                        <span>Копировать текст</span>
                      </button>
                      <button
                        type="button"
                        className={s.contextActionItem}
                        onClick={() => {
                          onForward?.();
                          setMessageActionMenu(null);
                          setMessageActionReactionsExpanded(false);
                          onCloseActionBar?.();
                        }}
                        disabled={
                          selectedCount !== 1
                          || !selectedMessage
                          || selectedMessage.deleted_at
                        }
                      >
                        <ForwardMessageIcon className={s.contextActionIcon} />
                        <span>Переслать</span>
                      </button>
                    </>
                  ) : (
                    <>
                  <button
                    type="button"
                    className={s.contextActionItem}
                    onClick={() => {
                      onReply?.();
                      setMessageActionMenu(null);
                      setMessageActionReactionsExpanded(false);
                      onCloseActionBar?.();
                    }}
                    disabled={selectedCount !== 1 || !selectedMessage || selectedMessage.deleted_at}
                  >
                    <AnswerMessageIcon className={s.contextActionIcon} />
                    <span>Ответить</span>
                  </button>
                  <button
                    type="button"
                    className={s.contextActionItem}
                    onClick={() => {
                      onStartEdit?.();
                      setMessageActionMenu(null);
                      setMessageActionReactionsExpanded(false);
                      onCloseActionBar?.();
                    }}
                    disabled={selectedCount !== 1 || !selectedMessage || !canEditMessage(selectedMessage) || selectedMessage.deleted_at}
                  >
                    <EditMessageIcon className={s.contextActionIcon} />
                    <span>Изменить</span>
                  </button>
                  {canPinOrUnpin ? (
                    <button
                      type="button"
                      className={s.contextActionItem}
                      onClick={async () => {
                        if (!selectedMessage) return;
                        const pinned = Boolean(selectedMessage.is_pinned);
                        try {
                          if (pinned) await onUnpinMessage?.(selectedMessage.id);
                          else await onPinMessage?.(selectedMessage.id);
                        } catch {
                          /* onPinMessage / onUnpinMessage уже показывают тост */
                        }
                        setMessageActionMenu(null);
                        setMessageActionReactionsExpanded(false);
                        onCloseActionBar?.();
                      }}
                      disabled={
                        selectedCount !== 1
                        || !selectedMessage
                        || selectedMessage.deleted_at
                        || selectedMessage.__optimistic
                        || selectedMessage.__failed
                      }
                    >
                      <PinMessageIcon className={s.contextActionIcon} />
                      <span>{selectedMessage?.is_pinned ? 'Открепить' : 'Закрепить'}</span>
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={s.contextActionItem}
                    onClick={async () => {
                      const copiedSelection = await copySelectedTextFromMessageActionMenu();
                      if (!copiedSelection) onCopy?.();
                      setMessageActionMenu(null);
                      setMessageActionReactionsExpanded(false);
                      onCloseActionBar?.();
                    }}
                    disabled={selectedCount === 0 || !selectionHasContent}
                  >
                    <CopyMessageIcon className={s.contextActionIcon} />
                    <span>Копировать текст</span>
                  </button>
                  <button
                    type="button"
                    className={s.contextActionItem}
                    onClick={() => {
                      onForward?.();
                      setMessageActionMenu(null);
                      setMessageActionReactionsExpanded(false);
                      onCloseActionBar?.();
                    }}
                    disabled={selectedCount === 0}
                  >
                    <ForwardMessageIcon className={s.contextActionIcon} />
                    <span>Переслать</span>
                  </button>
                  {!reactionHoverDesktop && (activeFileMenuSelection || mobileSelectedMessageFile) && (
                    <>
                      {webShareAvailable && (
                        <button
                          type="button"
                          className={s.contextActionItem}
                          onPointerDown={() => {
                            const source = activeFileMenuSelection || mobileSelectedMessageFile;
                            if (!source?.downloadUrl) return;
                            prefetchShareableFile({
                              downloadUrl: source.downloadUrl,
                              fileName: source.fileName,
                              mime: resolveContextFileMime(source),
                            });
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const source = activeFileMenuSelection || mobileSelectedMessageFile;
                            if (!source) return;
                            handleShareContextFile(source);
                            setMessageActionMenu(null);
                            setMessageActionReactionsExpanded(false);
                            onCloseActionBar?.();
                          }}
                        >
                          <ShareActionIcon />
                          <span>Поделиться</span>
                        </button>
                      )}
                      <button
                        type="button"
                        className={s.contextActionItem}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const source = activeFileMenuSelection || mobileSelectedMessageFile;
                          if (!source) return;
                          openFileUrl(source.downloadUrl, source.fileName);
                          setMessageActionMenu(null);
                          setMessageActionReactionsExpanded(false);
                          onCloseActionBar?.();
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M21 15V16.2C21 17.8802 21 18.7202 20.673 19.362C20.3854 19.9265 19.9265 20.3854 19.362 20.673C18.7202 21 17.8802 21 16.2 21H7.8C6.11984 21 5.27976 21 4.63803 20.673C4.07354 20.3854 3.6146 19.9265 3.32698 19.362C3 18.7202 3 17.8802 3 16.2V15M17 10L12 15M12 15L7 10M12 15V3"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>Сохранить</span>
                      </button>
                    </>
                  )}
                  {reactionHoverDesktop && activeFileMenuSelection && (
                    <>
                      {webShareAvailable && (
                        <button
                          type="button"
                          className={s.contextActionItem}
                          onPointerDown={() => {
                            if (!activeFileMenuSelection?.downloadUrl) return;
                            prefetchShareableFile({
                              downloadUrl: activeFileMenuSelection.downloadUrl,
                              fileName: activeFileMenuSelection.fileName,
                              mime: resolveContextFileMime(activeFileMenuSelection),
                            });
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleShareContextFile(activeFileMenuSelection);
                            setMessageActionMenu(null);
                            setMessageActionReactionsExpanded(false);
                            onCloseActionBar?.();
                          }}
                        >
                          <ShareActionIcon />
                          <span>Поделиться</span>
                        </button>
                      )}
                      <button
                        type="button"
                        className={s.contextActionItem}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openFileUrl(activeFileMenuSelection.downloadUrl, activeFileMenuSelection.fileName);
                          setMessageActionMenu(null);
                          setMessageActionReactionsExpanded(false);
                          onCloseActionBar?.();
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M21 15V16.2C21 17.8802 21 18.7202 20.673 19.362C20.3854 19.9265 19.9265 20.3854 19.362 20.673C18.7202 21 17.8802 21 16.2 21H7.8C6.11984 21 5.27976 21 4.63803 20.673C4.07354 20.3854 3.6146 19.9265 3.32698 19.362C3 18.7202 3 17.8802 3 16.2V15M17 10L12 15M12 15L7 10M12 15V3"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>Сохранить</span>
                      </button>
                    </>
                  )}
                  {reactionHoverDesktop && (
                    <button
                      type="button"
                      className={s.contextActionItem}
                      onClick={() => {
                        onEnableMultiSelect?.(selectedMessage);
                        setMessageActionMenu(null);
                        setMessageActionReactionsExpanded(false);
                      }}
                      disabled={selectedCount !== 1 || !selectedMessage || selectedMessage.deleted_at}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={s.contextActionIcon}
                        aria-hidden="true"
                      >
                        <path
                          d="M7.5 12L10.5 15L16.5 9M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                      <span>Выделить</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className={`${s.contextActionItem} ${s.contextActionDanger}`}
                    onClick={() => {
                      setDeleteConfirmOpen(true);
                      setMessageActionMenu(null);
                      setMessageActionReactionsExpanded(false);
                    }}
                    disabled={selectedCount === 0 || deletableCount === 0}
                  >
                    <DeleteMessageIcon className={s.contextActionIcon} />
                    <span>Удалить</span>
                  </button>
                    </>
                  )}
                </motion.div>
              )}
            </motion.div>
          )}
          {reactionPicker && (
            <motion.div
              ref={reactionPickerRef}
              className={`${s.messageContextMenu} ${s.messageContextMenuExpandedOnly}`}
              style={{ left: reactionPickerPos.left, top: reactionPickerPos.top }}
              role="dialog"
              aria-label="Выбор реакции"
              onClick={(e) => e.stopPropagation()}
              onMouseEnter={cancelReactionPickerCloseTimer}
              onMouseLeave={() => scheduleReactionPickerClose()}
              initial={{ opacity: 0, y: 10, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.96 }}
              transition={{ duration: 0.18 }}
            >
              <div className={s.contextReactionsExpandedPanel}>
                {ALLOWED_MESSAGE_REACTIONS_LIST.map((em) => {
                  const active = pickerSelectedReactions.has(em);
                  return (
                    <motion.button
                      key={em}
                      type="button"
                      className={[
                        s.reactionPickerEmojiBtn,
                        s.contextReactionBtn,
                        s.contextReactionExpandedBtn,
                        active ? s.reactionPickerEmojiBtnActive : '',
                        active ? s.contextReactionBtnActive : '',
                      ].filter(Boolean).join(' ')}
                      whileHover={{ y: -2, scale: 1.12 }}
                      whileTap={{ scale: 0.88 }}
                      transition={REACTION_SPRING_FAST}
                      onClick={(ev) => {
                        if (suppressFirstReactionPickerTapRef.current) {
                          ev.preventDefault();
                          ev.stopPropagation();
                          return;
                        }
                        toggleMessageReaction(reactionPicker.messageId, em, { clientX: ev.clientX, clientY: ev.clientY });
                        setReactionPicker(null);
                      }}
                    >
                      <motion.span
                        className={s.reactionPickerEmojiGlyph}
                        animate={
                          active
                            ? { scale: [1, 1.14, 1], y: [0, -1, 0] }
                            : { scale: 1, y: 0 }
                        }
                        transition={{ duration: 0.28 }}
                      >
                        <ReactionGlyph emoji={em} className={s.reactionCustomGlyph} />
                      </motion.span>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </>,
        document.body,
      )}

      {reactionSparkleBursts.length > 0 && typeof document !== 'undefined' && createPortal(
        (
          <>
            {reactionSparkleBursts.map((burst) => (
              <div
                key={burst.id}
                className={s.reactionSparkleBurst}
                style={{ left: burst.x, top: burst.y }}
                aria-hidden="true"
              >
                {burst.particles.map((p) => (
                  <span
                    key={p.key}
                    className={s.reactionSparkleParticle}
                    style={{
                      '--spark-dx': `${p.dx}px`,
                      '--spark-dy': `${p.dy}px`,
                      animationDelay: `${p.delay}s`,
                    }}
                  >
                    {p.glyph}
                  </span>
                ))}
              </div>
            ))}
          </>
        ),
        document.body,
      )}

      {reactionEmojiBursts.length > 0 && typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {reactionEmojiBursts.map((burst) => (
            <motion.div
              key={burst.id}
              className={s.reactionEmojiBurst}
              style={{ left: burst.x, top: burst.y }}
              aria-hidden="true"
              initial={{ opacity: 0, y: 10, scale: 0.7 }}
              animate={{ opacity: 1, y: -34, scale: 1.34 }}
              exit={{ opacity: 0, y: -58, scale: 0.86 }}
              transition={{ duration: 0.62 }}
            >
              <ReactionGlyph
                emoji={burst.emoji}
                className={`${s.reactionEmojiBurstGlyph} ${s.reactionCustomGlyph}`}
              />
            </motion.div>
          ))}
        </AnimatePresence>,
        document.body,
      )}

      {reactionsDetail && typeof document !== 'undefined' && createPortal(
        <div
          className={s.reactionsOverlay}
          role="presentation"
          onClick={() => { setReactionsDetail(null); }}
        >
          <div
            className={s.reactionsSheet}
            role="dialog"
            aria-modal="true"
            aria-label="Реакции на сообщение"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={s.reactionsSheetHeader}>
              <span className={s.reactionsSheetTitle}>Реакции</span>
              <button
                type="button"
                className={s.reactionsSheetClose}
                aria-label="Закрыть"
                onClick={() => { setReactionsDetail(null); }}
              >
                ×
              </button>
            </div>
            {reactionsDetailLoading ? (
              <div className={s.reactionsSheetLoading}>Загрузка…</div>
            ) : (
              <>
                <div className={s.reactionsEmojiTabs} role="tablist">
                  {(reactionsDetail.reactions || []).map((r) => (
                    <button
                      key={r.emoji}
                      type="button"
                      role="tab"
                      aria-selected={reactionsDetailTab === r.emoji}
                      className={`${s.reactionsEmojiTab} ${reactionsDetailTab === r.emoji ? s.reactionsEmojiTabActive : ''}`}
                      onClick={() => { setReactionsDetailTab(r.emoji); }}
                    >
                      <ReactionGlyph emoji={r.emoji} className={s.reactionCustomGlyph} />
                      <span className={s.reactionsEmojiTabCount}>{r.count}</span>
                    </button>
                  ))}
                </div>
                <ul className={s.reactionsActorsList}>
                  {((reactionsDetail.reactions || []).find((x) => x.emoji === reactionsDetailTab)?.actors || []).map((a) => (
                    <li
                      key={`${reactionsDetailTab}-${a.employee_id}`}
                      className={`${s.reactionsActorRow} ${s.headerAvatarClickable}`}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => handleMessageAvatarClick(e, a.employee_id, a.name)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleMessageAvatarClick(e, a.employee_id, a.name);
                        }
                      }}
                    >
                      <ChatSenderAvatar
                        employeeId={a.employee_id}
                        avatarUrl={resolveImageUrl(a.avatar_url)}
                        employeeTaskStatsMap={employeeTaskStatsMap}
                        size={40}
                        className={s.reactionsActorAvatar}
                      />
                      <span className={s.reactionsActorName}>{a.name || `Сотрудник ${a.employee_id}`}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}

      {attachmentPdfPreview && typeof document !== 'undefined' && createPortal(
        <div
          className={s.attachmentPdfOverlay}
          role="dialog"
          aria-modal="true"
          aria-label="Предпросмотр вложения"
          onClick={closeAttachmentPdfPreview}
        >
          <div
            className={s.attachmentPdfSheet}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={s.attachmentPdfToolbar}>
              <span className={s.attachmentPdfTitle} title={attachmentPdfPreview.fileName}>
                {attachmentPdfPreview.fileName}
              </span>
              <div className={s.attachmentPdfToolbarActions}>
                {canShareAttachmentOnDevice() && (
                  <button
                    type="button"
                    className={s.attachmentPdfToolbarBtn}
                    onPointerDown={() => {
                      const u = attachmentPdfPreview.downloadUrl;
                      if (!u) return;
                      prefetchShareableFile({
                        downloadUrl: u,
                        fileName: attachmentPdfPreview.fileName,
                      });
                    }}
                    onClick={() => {
                      const u = attachmentPdfPreview.downloadUrl;
                      if (!u) return;
                      runShareAttachmentAction(
                        { downloadUrl: u, fileName: attachmentPdfPreview.fileName },
                        (err) => {
                          reportShareError(err, {
                            downloadUrl: u,
                            fileName: attachmentPdfPreview.fileName,
                          });
                        },
                      );
                    }}
                    disabled={!attachmentPdfPreview.downloadUrl}
                  >
                    Поделиться
                  </button>
                )}
                <button
                  type="button"
                  className={s.attachmentPdfToolbarBtn}
                  onClick={() => {
                    const u = attachmentPdfPreview.downloadUrl;
                    if (u) openFileUrl(u, attachmentPdfPreview.fileName);
                  }}
                  disabled={!attachmentPdfPreview.downloadUrl}
                >
                  Скачать
                </button>
                <button
                  type="button"
                  className={s.attachmentPdfClose}
                  aria-label="Закрыть"
                  onClick={closeAttachmentPdfPreview}
                >
                  ×
                </button>
              </div>
            </div>
            <div className={s.attachmentPdfBody}>
              {attachmentPdfPreview.loading && (
                <div className={s.attachmentPdfLoading}>
                  <div className={s.attachmentPdfLoadingInner}>
                    <span className={s.attachmentPdfSpinner} aria-hidden="true" />
                    <div className={s.attachmentPdfLoadingText}>
                      <span className={s.attachmentPdfLoadingLabel}>
                        {attachmentPdfPreview.loadPhase === 'download'
                          ? 'Загрузка PDF…'
                          : 'Подготовка предпросмотра…'}
                      </span>
                      <span className={s.attachmentPdfLoadingPercent}>
                        {Math.min(99, Math.round(Number(attachmentPdfPreview.loadPercent) || 0))}
                        %
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {!attachmentPdfPreview.loading && attachmentPdfPreview.error === 'unsupported' && (
                <div className={s.attachmentPdfError}>
                  Предпросмотр недоступен для этого типа файла. Можно скачать оригинал.
                </div>
              )}
              {(() => {
                const isLikelyMobileBrowser = typeof window !== 'undefined'
                  && (
                    window.matchMedia?.('(hover: none), (pointer: coarse)').matches
                    || window.innerWidth <= 820
                  );
                const hasBlobUrl = Boolean(attachmentPdfPreview.pdfObjectUrl);
                const hasDefinitiveConversionError = attachmentPdfPreview.error === 'unsupported';
                const fallbackTextByReason = {
                  timeout: 'Конвертация заняла слишком много времени. Можно открыть файл во внешнем приложении или скачать.',
                  failed_prepare: 'Сервер не смог подготовить предпросмотр. Можно открыть файл во внешнем приложении или скачать.',
                  failed_payload: 'Предпросмотр сформирован с ошибкой данных. Можно открыть файл во внешнем приложении или скачать.',
                  failed_download: 'Не удалось загрузить готовый PDF-предпросмотр. Можно открыть файл во внешнем приложении или скачать.',
                  failed_network: 'Сетевой сбой при открытии предпросмотра. Проверьте соединение или скачайте файл.',
                };
                const fallbackText = fallbackTextByReason[attachmentPdfPreview.error]
                  || 'Встроенный PDF-просмотр на этом устройстве может не поддерживаться.';
                const canOpenOnFallback = !attachmentPdfPreview.error && hasBlobUrl;
                const shouldShowFallback = !attachmentPdfPreview.loading
                  && !hasDefinitiveConversionError
                  && (!hasBlobUrl || isLikelyMobileBrowser || Boolean(attachmentPdfPreview.error));

                if (shouldShowFallback) {
                  return (
                    <div className={s.attachmentPdfError} style={{ color: '#e5e7eb', flexDirection: 'column', gap: 14 }}>
                      <span>
                        {fallbackText}
                      </span>
                      {canOpenOnFallback && (
                        <button
                          type="button"
                          className={s.attachmentPdfToolbarBtn}
                          onClick={() => {
                            openExternalUrl(attachmentPdfPreview.pdfObjectUrl);
                          }}
                        >
                          Открыть PDF
                        </button>
                      )}
                    </div>
                  );
                }
                return attachmentPdfPreview.pdfObjectUrl ? (
                  <iframe
                    title={attachmentPdfPreview.fileName}
                    className={s.attachmentPdfFrame}
                    src={attachmentPdfPreview.pdfObjectUrl}
                  />
                ) : null;
              })()}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}


const TABS = [
  { key: 'image', label: 'Фото' },
  { key: 'video', label: 'Видео' },
  { key: 'audio', label: 'Аудио' },
  { key: 'file', label: 'Файлы' },
];

const PAGE_SIZE = 40;

// ---------- хелперы ----------
function getViewUrl(key, filename) {
  let k = (key || '').replace(/^\/+/, '');
  if (!k.startsWith('media/')) k = `media/${k}`;
  const base = `${API_URL}/media/${k}`;
  if (!filename) return base;
  return `${base}?download_name=${encodeURIComponent(filename)}`;
}

const REPLY_ATTACHMENT_KIND_LABELS = {
  image: 'Фото',
  video: 'Видео',
  audio: 'Голосовое сообщение',
  file: 'Файл',
};

function getReplyAttachments(replyTo) {
  if (!replyTo) return [];
  const att = replyTo.attachments;
  return Array.isArray(att) ? att.filter((a) => a && (a.s3_key || a.kind)) : [];
}

function getReplyPreviewAttachment(replyTo) {
  const attachments = getReplyAttachments(replyTo);
  if (!attachments.length) return null;
  return (
    attachments.find((a) => a.kind === 'image' || a.kind === 'video')
    || attachments[0]
  );
}

function formatReplyAttachmentLabel(att) {
  const kind = att?.kind || 'file';
  const label = REPLY_ATTACHMENT_KIND_LABELS[kind] || 'Вложение';
  const filename = String(att?.original_filename || '').trim();
  if (kind === 'file' && filename) return `📎 ${filename}`;
  return label;
}

function formatReplyExcerpt(replyTo, { limit = 140 } = {}) {
  if (!replyTo) return 'Открыть сообщение';
  if (replyTo.deleted_at) return 'Сообщение удалено';

  const text = String(replyTo.content || '').trim().replace(/\s+/g, ' ');
  const attachments = getReplyAttachments(replyTo);

  if (attachments.length > 0) {
    const mediaPart = formatReplyAttachmentLabel(attachments[0])
      + (attachments.length > 1 ? ` (+${attachments.length - 1})` : '');
    if (text) {
      if (text.length <= limit) return text;
      return `${text.slice(0, limit).trimEnd()}…`;
    }
    return mediaPart;
  }

  if (!text) return 'Сообщение';
  if (text.length <= limit) return text;
  return `${text.slice(0, limit).trimEnd()}…`;
}

function renderReplyPreviewThumb(attachment) {
  if (!attachment) return null;

  const url = getViewUrl(attachment.s3_key, attachment.original_filename);
  const kind = attachment.kind || 'file';

  return (
    <div className={s.replyPreviewThumb} aria-hidden="true">
      {kind === 'image' && url ? (
        <img className={s.replyPreviewThumbMedia} src={url} alt="" />
      ) : null}
      {kind === 'video' && url ? (
        <>
          <video
            className={s.replyPreviewThumbMedia}
            src={url}
            muted
            playsInline
            preload="metadata"
          />
          <span className={s.replyPreviewThumbPlay}>▶</span>
        </>
      ) : null}
      {kind === 'audio' ? <span className={s.replyPreviewThumbIcon}>🎤</span> : null}
      {kind === 'file' ? <span className={s.replyPreviewThumbIcon}>📎</span> : null}
      {!url && kind !== 'audio' && kind !== 'file' ? (
        <span className={s.replyPreviewThumbIcon}>📎</span>
      ) : null}
    </div>
  );
}

function renderReplyPreviewBlock(replyTo, {
  fallbackAuthor = 'Сообщение',
  fallbackText = 'Открыть сообщение',
  maxTextLen = 140,
  showAuthor = true,
} = {}) {
  const author = replyTo?.sender_name || fallbackAuthor;
  const text = replyTo ? formatReplyExcerpt(replyTo, { limit: maxTextLen }) : fallbackText;
  const thumbAtt = replyTo && !replyTo.deleted_at ? getReplyPreviewAttachment(replyTo) : null;

  return (
    <div className={s.replyPreviewBody}>
      <div className={s.replyPreviewMain}>
        {showAuthor ? <div className={s.replyAuthor}>{author}</div> : null}
        <div className={s.replyText}>{text}</div>
      </div>
      {thumbAtt ? renderReplyPreviewThumb(thumbAtt) : null}
    </div>
  );
}

function renderReplyPreviewInline(replyTo, { maxTextLen = 80 } = {}) {
  const text = formatReplyExcerpt(replyTo, { limit: maxTextLen });
  const thumbAtt = replyTo && !replyTo.deleted_at ? getReplyPreviewAttachment(replyTo) : null;

  return (
    <span className={s.replyPreviewInline}>
      <span className={s.replyPreviewInlineText} title={text}>{text}</span>
      {thumbAtt ? renderReplyPreviewThumb(thumbAtt) : null}
    </span>
  );
}

function getAttachmentDownloadUrl(chatId, attachmentId) {
  const chat = Number(chatId);
  const att = Number(attachmentId);
  if (!Number.isFinite(chat) || !Number.isFinite(att)) return null;
  return `${API_URL}/chats/${chat}/attachments/${att}/download`;
}

/** Собирает абсолютный URL из пути вида `/api/v1/...`, который возвращает бэкенд в preview_url */
function resolveBackendAbsoluteUrl(pathOrUrl) {
  if (!pathOrUrl) return '';

  const s = String(pathOrUrl).trim();
  if (/^https?:\/\//i.test(s)) return s;

  const browserOrigin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : '';

  let apiBase = null;

  try {
    apiBase = new URL(API_URL || '/api/v1', browserOrigin || undefined);
  } catch {
    apiBase = null;
  }

  const apiOrigin = apiBase?.origin || browserOrigin || '';
  const apiPath = (apiBase?.pathname || '/api/v1').replace(/\/+$/, '') || '/api/v1';

  if (s.startsWith('/')) {
    // Backend уже вернул путь с API-префиксом: /api/v1/...
    // В этом случае НЕ добавляем API_URL повторно.
    if (apiPath && (s === apiPath || s.startsWith(`${apiPath}/`))) {
      return apiOrigin ? `${apiOrigin}${s}` : s;
    }

    // На всякий случай: любой абсолютный API-путь тоже не префиксуем повторно.
    if (s.startsWith('/api/')) {
      return apiOrigin ? `${apiOrigin}${s}` : s;
    }

    // Если backend вернул путь без API-префикса, добавляем API_URL.
    if (apiOrigin) {
      return `${apiOrigin}${apiPath}${s}`;
    }

    return `${apiPath}${s}`;
  }

  // Если пришёл относительный путь без начального слеша: chats/...
  if (apiOrigin) {
    return `${apiOrigin}${apiPath}/${s.replace(/^\/+/, '')}`;
  }

  return `${apiPath}/${s.replace(/^\/+/, '')}`;
}

function openExternalUrl(url) {
  try {
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (!win) window.location.href = url;
  } catch {
    window.location.href = url;
  }
}

async function fetchDownloadBlob(url) {
  const firstRes = await apiFetch(url, { method: 'GET' });
  if (!firstRes.ok) throw new Error(`download failed: ${firstRes.status}`);

  const contentType = (firstRes.headers.get('content-type') || '').toLowerCase();
  if (contentType.includes('application/json')) {
    const payload = await firstRes.json().catch(() => null);
    const presignedUrl = payload?.url;
    if (!presignedUrl) throw new Error('download payload missing url');

    const fileRes = await fetch(presignedUrl, { method: 'GET' });
    if (!fileRes.ok) throw new Error(`presigned download failed: ${fileRes.status}`);
    return await fileRes.blob();
  }

  return await firstRes.blob();
}

const MIME_BY_EXT = {
  '.pdf': 'application/pdf',
  '.msg': 'application/vnd.ms-outlook',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xlsm': 'application/vnd.ms-excel.sheet.macroEnabled.12',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.ppsx': 'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
  '.txt': 'text/plain',
  '.csv': 'text/csv',
  '.json': 'application/json',
  '.xml': 'application/xml',
  '.zip': 'application/zip',
  '.7z': 'application/x-7z-compressed',
  '.rar': 'application/vnd.rar',
  '.tar': 'application/x-tar',
  '.gz': 'application/gzip',
  '.bz2': 'application/x-bzip2',
  '.xz': 'application/x-xz',
  '.mp3': 'audio/mpeg',
  '.wav': 'audio/wav',
  '.ogg': 'audio/ogg',
  '.m4a': 'audio/mp4',
  '.mp4': 'video/mp4',
  '.webm': 'video/webm',
  '.mov': 'video/quicktime',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.svg': 'image/svg+xml',
};

function guessMimeFromFilename(filename) {
  const name = String(filename || '').toLowerCase();
  if (!name) return '';
  const compound = ['.tar.gz', '.tar.bz2', '.tar.xz', '.tgz', '.tbz2', '.txz'];
  for (const ext of compound) {
    if (name.endsWith(ext)) {
      if (ext === '.tar.gz' || ext === '.tgz') return 'application/gzip';
      if (ext === '.tar.bz2' || ext === '.tbz2') return 'application/x-bzip2';
      if (ext === '.tar.xz' || ext === '.txz') return 'application/x-xz';
    }
  }
  const dot = name.lastIndexOf('.');
  if (dot === -1) return '';
  return MIME_BY_EXT[name.slice(dot)] || '';
}

function resolveShareMime(blob, fileName, mimeHint) {
  const hint = String(mimeHint || '').toLowerCase().trim();
  const blobType = String(blob?.type || '').toLowerCase().trim();
  if (hint && hint !== 'application/octet-stream' && !hint.includes('json')) return hint;
  if (blobType && blobType !== 'application/octet-stream' && !blobType.includes('json')) return blobType;
  return guessMimeFromFilename(fileName) || 'application/octet-stream';
}

function canUseWebShare() {
  return typeof navigator !== 'undefined'
    && typeof navigator.share === 'function'
    && (typeof window === 'undefined' || window.isSecureContext);
}

function isIosDevice() {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function canShareAttachmentOnDevice() {
  return canUseWebShare() && isIosDevice();
}

function isShareAbortError(err) {
  const errName = err?.name || '';
  const errMsg = String(err?.message || '').toLowerCase();
  return errName === 'AbortError' || errMsg.includes('abort') || errMsg.includes('cancel');
}

function isUserGestureShareError(err) {
  const errMsg = String(err?.message || '').toLowerCase();
  return errMsg.includes('user gesture') || errMsg.includes('gesture');
}

function isSharePermissionDeniedError(err) {
  const errName = err?.name || '';
  const errMsg = String(err?.message || '').toLowerCase();
  return errName === 'NotAllowedError'
    || errMsg.includes('permission denied')
    || errMsg.includes('not allowed');
}

function formatShareError(err) {
  if (isShareAbortError(err)) return null;
  if (isUserGestureShareError(err)) {
    return 'Файл готов. Нажмите «Поделиться» ещё раз.';
  }
  if (isSharePermissionDeniedError(err)) {
    return 'Браузер не разрешил отправку файла.';
  }
  if (err?.code === 'CANNOT_SHARE') {
    return 'Это устройство или браузер не поддерживает отправку файлов такого типа.';
  }
  return err?.message || 'Не удалось открыть меню «Поделиться».';
}

function reportShareError(err, { downloadUrl, fileName } = {}) {
  const text = formatShareError(err);
  if (!text) return;
  if (isSharePermissionDeniedError(err) && downloadUrl) {
    const wantDownload = window.confirm(`${text}\n\nСкачать файл сейчас?`);
    if (wantDownload) {
      void openFileUrl(downloadUrl, fileName || 'file');
    }
    return;
  }
  alert(text);
}

const pendingShareFiles = new Map();
const sharePrefetchPromises = new Map();

function makeShareCacheKey(downloadUrl, fileName) {
  return `${downloadUrl}::${String(fileName || 'file').trim() || 'file'}`;
}

async function buildShareableFile({ downloadUrl, fileName, mime }) {
  const blob = await fetchDownloadBlob(downloadUrl);
  const name = String(fileName || 'file').trim() || 'file';
  const type = resolveShareMime(blob, name, mime);
  return new File([blob], name, { type });
}

function invokeWebShare(file, title) {
  const name = String(title || file?.name || 'file').trim() || 'file';
  const payloads = [{ files: [file] }, { title: name, files: [file] }];
  let canShareRejected = false;
  for (const payload of payloads) {
    if (navigator.canShare && !navigator.canShare(payload)) {
      canShareRejected = true;
      continue;
    }
    return navigator.share(payload);
  }
  if (canShareRejected) {
    const err = new Error('CANNOT_SHARE');
    err.code = 'CANNOT_SHARE';
    throw err;
  }
  return Promise.reject(new Error('Не удалось открыть меню «Поделиться».'));
}

function prefetchShareableFile({ downloadUrl, fileName, mime } = {}) {
  if (!downloadUrl || !canShareAttachmentOnDevice()) return;
  const key = makeShareCacheKey(downloadUrl, fileName);
  if (pendingShareFiles.has(key) || sharePrefetchPromises.has(key)) return;
  const promise = buildShareableFile({ downloadUrl, fileName, mime })
    .then((file) => {
      pendingShareFiles.set(key, file);
      sharePrefetchPromises.delete(key);
      return file;
    })
    .catch(() => {
      sharePrefetchPromises.delete(key);
    });
  sharePrefetchPromises.set(key, promise);
}

/** Web Share требует user gesture: share вызывается синхронно из click, файл кэшируется для повторного нажатия */
function runShareAttachmentAction({ downloadUrl, fileName, mime } = {}, onError, onSuccess) {
  if (!downloadUrl) {
    onError?.(new Error('Не удалось получить файл'));
    return;
  }
  if (!canShareAttachmentOnDevice()) {
    onError?.(new Error('Отправка файла через «Поделиться» доступна только на iPhone и iPad.'));
    return;
  }

  const name = String(fileName || 'file').trim() || 'file';
  const key = makeShareCacheKey(downloadUrl, name);
  const cached = pendingShareFiles.get(key);

  if (cached) {
    pendingShareFiles.delete(key);
    invokeWebShare(cached, name).then(() => {
      onSuccess?.();
    }).catch((err) => {
      if (isShareAbortError(err)) return;
      if (isUserGestureShareError(err)) {
        pendingShareFiles.set(key, cached);
        onError?.(new Error('Файл готов. Нажмите «Поделиться» ещё раз.'));
        return;
      }
      if (err?.code === 'CANNOT_SHARE') {
        onError?.(new Error('Это устройство или браузер не поддерживает отправку файлов такого типа.'));
        return;
      }
      onError?.(err);
    });
    return;
  }

  void (async () => {
    try {
      const inflight = sharePrefetchPromises.get(key);
      const file = inflight ? await inflight : await buildShareableFile({ downloadUrl, fileName: name, mime });
      if (!file) throw new Error('Не удалось получить файл');
      try {
        await invokeWebShare(file, name);
        pendingShareFiles.delete(key);
        sharePrefetchPromises.delete(key);
        onSuccess?.();
      } catch (err) {
        if (isShareAbortError(err)) return;
        if (isUserGestureShareError(err)) {
          pendingShareFiles.set(key, file);
          onError?.(new Error('Файл готов. Нажмите «Поделиться» ещё раз.'));
          return;
        }
        if (err?.code === 'CANNOT_SHARE') {
          onError?.(new Error('Это устройство или браузер не поддерживает отправку файлов такого типа.'));
          return;
        }
        onError?.(err);
      }
    } catch (err) {
      onError?.(err);
    }
  })();
}

function shareAttachmentFile({ downloadUrl, fileName, mime } = {}) {
  return new Promise((resolve, reject) => {
    runShareAttachmentAction(
      { downloadUrl, fileName, mime },
      (err) => {
        if (isShareAbortError(err)) {
          resolve();
          return;
        }
        reject(err);
      },
      resolve,
    );
  });
}

function ShareActionIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M7 11C6.07003 11 5.60504 11 5.22354 11.1022C4.18827 11.3796 3.37962 12.1883 3.10222 13.2235C3 13.605 3 14.07 3 15V16.2C3 17.8802 3 18.7202 3.32698 19.362C3.6146 19.9265 4.07354 20.3854 4.63803 20.673C5.27976 21 6.11984 21 7.8 21H16.2C17.8802 21 18.7202 21 19.362 20.673C19.9265 20.3854 20.3854 19.9265 20.673 19.362C21 18.7202 21 17.8802 21 16.2V15C21 14.07 21 13.605 20.8978 13.2235C20.6204 12.1883 19.8117 11.3796 18.7765 11.1022C18.395 11 17.93 11 17 11M16 7L12 3M12 3L8 7M12 3V15"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function humanSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  const units = ['Б', 'КБ', 'МБ', 'ГБ'];
  let i = 0, n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  const str = n < 10 && i ? n.toFixed(1) : Math.round(n).toString();
  return str.replace('.', ',') + '\u00a0' + units[i];
}

function extractFilename(keyOrName) {
  try {
    return decodeURIComponent((keyOrName || '').split('/').pop() || 'file');
  } catch { return 'file'; }
}

function inferExt(name) {
  const lower = (name || '').toLowerCase();
  const complex = ['.tar.gz', '.tar.bz2', '.tar.xz', '.tgz', '.tbz2', '.txz'];
  for (const cx of complex) if (lower.endsWith(cx)) return cx.slice(1);
  const idx = lower.lastIndexOf('.');
  return idx >= 0 ? lower.slice(idx + 1) : '';
}

const FILE_ICONS = {
  pdf: '📕', doc: '📝', docx: '📝',
  xls: '📊', xlsx: '📊', xlsm: '📊', csv: '📊',
  pptx: '🖥️', ppsx: '🖥️', txt: '📄', md: '📄',
  zip: '🗂️', '7z': '🗂️', rar: '🗂️', tar: '🗂️',
  gz: '🗂️', bz2: '🗂️', xz: '🗂️', tgz: '🗂️',
};

function fileIcon(att) {
  const name = att.original_filename || extractFilename(att.s3_key);
  return FILE_ICONS[inferExt(name)] || '📎';
}

function formatDate(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

function groupByDate(items) {
  const groups = [];
  let lastDate = null;
  for (const item of items) {
    const d = formatDate(item.timestamp);
    if (d !== lastDate) {
      groups.push({ date: d, items: [] });
      lastDate = d;
    }
    groups[groups.length - 1].items.push(item);
  }
  return groups;
}

function useMediaPage(chatId, kind, enabled) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(false);
  const [cursor, setCursor] = React.useState(null);
  const [error, setError] = React.useState(null);
  const initialLoadDone = React.useRef(false);

  React.useEffect(() => {
    setItems([]);
    setHasMore(false);
    setCursor(null);
    setError(null);
    initialLoadDone.current = false;
  }, [chatId, kind]);

  const load = React.useCallback(async (beforeId = null) => {
    if (!chatId || !enabled) return;
    setLoading(true);
    setError(null);
    try {
      let url = `${API_URL}/chats/${chatId}/media?kind=${kind}&limit=${PAGE_SIZE}`;
      if (beforeId) url += `&before_attachment_id=${beforeId}`;

      const r = await apiFetch(url);
      if (!r.ok) throw new Error(String(r.status));
      const data = await r.json();

      setItems(prev => beforeId ? [...prev, ...(data.items || [])] : (data.items || []));
      setHasMore(Boolean(data.has_more));
      setCursor(data.next_before_attachment_id ?? null);
    } catch (e) {
      setError('Не удалось загрузить медиа');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [chatId, kind, enabled]);

  React.useEffect(() => {
    if (!enabled || initialLoadDone.current) return;
    initialLoadDone.current = true;
    load(null);
  }, [enabled, load]);

  const loadMore = React.useCallback(() => {
    if (!hasMore || loading || !cursor) return;
    load(cursor);
  }, [hasMore, loading, cursor, load]);

  return { items, loading, hasMore, loadMore, error };
}

function getVideoBufferedPercent(video) {
  if (!video) return null;
  const { duration, buffered } = video;
  if (!Number.isFinite(duration) || duration <= 0 || !buffered?.length) return null;
  try {
    const end = buffered.end(buffered.length - 1);
    return Math.min(100, Math.max(0, Math.round((end / duration) * 100)));
  } catch {
    return null;
  }
}

/** Видео во вложении сообщения: оверлей загрузки и % буфера до первого кадра */
function ChatInlineVideo({ src, onLoadedMetadata }) {
  const videoRef = React.useRef(null);
  const [overlay, setOverlay] = React.useState('loading');
  const [loadPercent, setLoadPercent] = React.useState(null);

  const syncBufferedPercent = React.useCallback(() => {
    setLoadPercent(getVideoBufferedPercent(videoRef.current));
  }, []);

  const hideOverlayIfHasFrame = React.useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      setOverlay('hidden');
    }
  }, []);

  React.useEffect(() => {
    setOverlay('loading');
    setLoadPercent(null);
  }, [src]);

  const handleLoadedMetadata = React.useCallback((e) => {
    syncBufferedPercent();
    onLoadedMetadata?.(e);
    hideOverlayIfHasFrame();
  }, [hideOverlayIfHasFrame, onLoadedMetadata, syncBufferedPercent]);

  const handleWaiting = React.useCallback(() => {
    const v = videoRef.current;
    if (!v || v.error) return;
    if (v.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
      setOverlay('buffering');
      syncBufferedPercent();
    }
  }, [syncBufferedPercent]);

  const handlePlaying = React.useCallback(() => {
    setOverlay('hidden');
  }, []);

  const handleCanPlay = React.useCallback(() => {
    hideOverlayIfHasFrame();
    syncBufferedPercent();
  }, [hideOverlayIfHasFrame, syncBufferedPercent]);

  const handleError = React.useCallback(() => {
    setOverlay('error');
    setLoadPercent(null);
  }, []);

  const showOverlay = overlay !== 'hidden';
  const overlayLabel = overlay === 'error'
    ? 'Не удалось загрузить видео'
    : overlay === 'buffering'
      ? 'Буферизация…'
      : 'Загрузка видео…';

  return (
    <div className={s.chatVideoWrap}>
      <video
        ref={videoRef}
        src={src}
        controls
        preload="metadata"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
        }}
        onLoadStart={() => {
          setOverlay('loading');
          setLoadPercent(null);
        }}
        onProgress={syncBufferedPercent}
        onLoadedMetadata={handleLoadedMetadata}
        onLoadedData={hideOverlayIfHasFrame}
        onCanPlay={handleCanPlay}
        onWaiting={handleWaiting}
        onPlaying={handlePlaying}
        onStalled={handleWaiting}
        onError={handleError}
      />
      {showOverlay && (
        <div className={s.chatVideoLoadingOverlay} role="status" aria-live="polite">
          {overlay === 'error' ? (
            <span className={s.chatVideoErrorText}>{overlayLabel}</span>
          ) : (
            <>
              <span className={s.spinner} aria-hidden="true" />
              <span className={s.chatVideoLoadingText}>{overlayLabel}</span>
              {loadPercent != null && loadPercent > 0 && (
                <span className={s.chatVideoLoadingPct}>{loadPercent}%</span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function GridThumb({ item, onClick }) {
  const url = getViewUrl(item.s3_key, item.original_filename);
  const isVideo = item.kind === 'video';
  return (
    <button
      type="button"
      className={s.gridThumb}
      onClick={() => onClick(item)}
      title={item.original_filename || ''}
      aria-label={item.original_filename || 'медиафайл'}
    >
      {isVideo ? (
        <>
          <video src={url} className={s.gridMedia} preload="metadata" muted playsInline />
          <span className={s.videoPlayBadge} aria-hidden="true">▶</span>
        </>
      ) : (
        <img src={url} className={s.gridMedia} alt="" loading="lazy" />
      )}
    </button>
  );
}

function FileRow({ item, onOpenPreview, onOpenMenu }) {
  const shareAvailable = canShareAttachmentOnDevice();
  const previewUrl = getViewUrl(item.s3_key, item.original_filename);
  const downloadUrl = getAttachmentDownloadUrl(item?.chat_id, item?.attachment_id) || previewUrl;
  const name = item.original_filename || extractFilename(item.s3_key);
  const icon = fileIcon(item);
  const size = item.size ? humanSize(item.size) : '';
  const handleOpenPreview = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onOpenPreview?.({ item, downloadUrl, name });
  };
  const handleDownload = (e) => {
    e.preventDefault();
    e.stopPropagation();
    openFileUrl(downloadUrl, name);
  };
  const handleSharePointerDown = () => {
    prefetchShareableFile({ downloadUrl, fileName: name, mime: item?.mime });
  };
  const handleShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    runShareAttachmentAction(
      { downloadUrl, fileName: name, mime: item?.mime },
      (err) => {
        reportShareError(err, { downloadUrl, fileName: name });
      },
    );
  };
  return (
    <div className={s.fileRow}>
      <button
        type="button"
        className={s.fileRowMain}
        onClick={handleOpenPreview}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpenMenu?.({
            x: e.clientX,
            y: e.clientY,
            downloadUrl,
            fileName: name,
            attachmentId: item.attachment_id,
            mime: item?.mime,
          });
        }}
        title={name}
        aria-label={`Предпросмотр ${name}`}
      >
        <span className={s.fileRowIcon} aria-hidden="true">{icon}</span>
        <span className={s.fileRowMeta}>
          <span className={s.fileRowName}>{name}</span>
          <span className={s.fileRowSub}>
            {item.sender_name && <>{item.sender_name} · </>}
            {formatDate(item.timestamp)}
            {size && <> · {size}</>}
          </span>
        </span>
      </button>
      <span className={s.fileRowActions}>
        {shareAvailable && (
          <button
            type="button"
            className={s.fileRowDl}
            title="Поделиться"
            aria-label="Поделиться файлом"
            onPointerDown={handleSharePointerDown}
            onClick={handleShare}
          >
            <ShareActionIcon size={18} />
          </button>
        )}
        <button
          type="button"
          className={s.fileRowDl}
          title="Скачать"
          aria-label="Скачать файл"
          onClick={handleDownload}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M21 15V16.2C21 17.8802 21 18.7202 20.673 19.362C20.3854 19.9265 19.9265 20.3854 19.362 20.673C18.7202 21 17.8802 21 16.2 21H7.8C6.11984 21 5.27976 21 4.63803 20.673C4.07354 20.3854 3.6146 19.9265 3.32698 19.362C3 18.7202 3 17.8802 3 16.2V15M17 10L12 15M12 15L7 10M12 15V3"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </span>
    </div>
  );
}

function AudioRow({ item, onClaimPlayback }) {
  const url = getViewUrl(item.s3_key, item.original_filename);
  const name = item.original_filename || extractFilename(item.s3_key);
  const [playing, setPlaying] = React.useState(false);
  const [waveActive, setWaveActive] = React.useState(false);
  const waveRef = React.useRef(null);
  const pendingPlayRef = React.useRef(false);

  const pauseSelf = React.useCallback(() => {
    waveRef.current?.pause();
    setPlaying(false);
  }, []);

  const togglePlay = (e) => {
    e.stopPropagation();
    onClaimPlayback?.(item.attachment_id, pauseSelf);
    if (!waveActive) {
      pendingPlayRef.current = true;
      setWaveActive(true);
      return;
    }
    waveRef.current?.playPause();
  };

  const handleWaveReady = React.useCallback(() => {
    if (!pendingPlayRef.current) return;
    pendingPlayRef.current = false;
    waveRef.current?.play();
  }, []);

  return (
    <div className={`${s.audioRow} ${waveActive ? s.audioRowWaveActive : ''}`}>
      <button type="button" className={s.audioPlayBtn} onClick={togglePlay} aria-label={playing ? 'Пауза' : 'Воспроизвести'}>
        {playing ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21" /></svg>
        )}
      </button>
      <div className={s.audioMeta}>
        {waveActive ? (
          <AudioWaveform
            ref={waveRef}
            variant="inline"
            src={url}
            filename={name}
            onPlayingChange={setPlaying}
            onReady={handleWaveReady}
          />
        ) : (
          <>
            <span className={s.audioName}>{name}</span>
            <span className={s.audioSub}>
              {item.sender_name && <>{item.sender_name} · </>}
              {formatDate(item.timestamp)}
              {item.size ? ` · ${humanSize(item.size)}` : ''}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function MediaLightbox({ open, items, thumbs = [], index, onClose, onIndexChange, reverseNavigation = false }) {
  const [scale, setScale] = React.useState(1);
  const [pan, setPan] = React.useState({ x: 0, y: 0 });
  const current = items[index];
  const url = current?.url || (current?.s3_key ? getViewUrl(current.s3_key) : null);
  const downloadUrl = getAttachmentDownloadUrl(current?.chat_id, current?.attachment_id) || url;
  const isVideo = current?.kind === 'video';
  const isIOS = React.useMemo(() => (
    typeof navigator !== 'undefined'
    && (/iPad|iPhone|iPod/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))
  ), []);

  const prev = React.useCallback(() => {
    if (reverseNavigation) {
      onIndexChange?.(Math.min(items.length - 1, index + 1));
      return;
    }
    onIndexChange?.(Math.max(0, index - 1));
  }, [index, items.length, onIndexChange, reverseNavigation]);
  const next = React.useCallback(() => {
    if (reverseNavigation) {
      onIndexChange?.(Math.max(0, index - 1));
      return;
    }
    onIndexChange?.(Math.min(items.length - 1, index + 1));
  }, [index, items.length, onIndexChange, reverseNavigation]);
  const canGoPrev = reverseNavigation ? index < items.length - 1 : index > 0;
  const canGoNext = reverseNavigation ? index > 0 : index < items.length - 1;

  const swipeRef = React.useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    dx: 0,
    dy: 0,
  });
  const ignoreCloseClickRef = React.useRef(false);
  const contentRef = React.useRef(null);
  const mediaRef = React.useRef(null);
  const scaleRef = React.useRef(1);
  const panRef = React.useRef({ x: 0, y: 0 });
  const touchPanRef = React.useRef({ active: false, startX: 0, startY: 0, startPanX: 0, startPanY: 0 });
  const mousePanRef = React.useRef({ active: false, pointerId: null, startX: 0, startY: 0, startPanX: 0, startPanY: 0 });
  const pinchRef = React.useRef({ lastDistance: 0 });
  const LB_MIN_SCALE = 1;
  const LB_MAX_SCALE = 15;
  const LB_PINCH_GAIN = 1.7;

  React.useEffect(() => { scaleRef.current = scale; }, [scale]);
  React.useEffect(() => { panRef.current = pan; }, [pan]);
  React.useEffect(() => {
    setScale(1);
    setPan({ x: 0, y: 0 });
    scaleRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    touchPanRef.current.active = false;
    mousePanRef.current.active = false;
    mousePanRef.current.pointerId = null;
    pinchRef.current.lastDistance = 0;
  }, [index, open]);

  const getPanBounds = React.useCallback((scaleValue = scaleRef.current || 1) => {
    const root = contentRef.current;
    const img = mediaRef.current;
    if (!root || !img || scaleValue <= 1) return { x: 0, y: 0 };
    const containerRect = root.getBoundingClientRect();
    const baseW = img.clientWidth || 0;
    const baseH = img.clientHeight || 0;
    if (!baseW || !baseH) return { x: 0, y: 0 };
    const scaledW = baseW * scaleValue;
    const scaledH = baseH * scaleValue;
    return {
      x: Math.max(0, (scaledW - containerRect.width) / 2),
      y: Math.max(0, (scaledH - containerRect.height) / 2),
    };
  }, []);

  const clampPan = React.useCallback((nextX, nextY, scaleValue = scaleRef.current || 1) => {
    const bounds = getPanBounds(scaleValue);
    return {
      x: Math.max(-bounds.x, Math.min(bounds.x, nextX)),
      y: Math.max(-bounds.y, Math.min(bounds.y, nextY)),
    };
  }, [getPanBounds]);

  const applyScaleAtPoint = React.useCallback((targetScale, clientX, clientY) => {
    const baseScale = scaleRef.current || 1;
    const basePan = panRef.current || { x: 0, y: 0 };
    const nextScale = Math.max(LB_MIN_SCALE, Math.min(LB_MAX_SCALE, Number(targetScale.toFixed(3))));
    const root = contentRef.current;
    const rect = root?.getBoundingClientRect?.();
    if (!rect || !rect.width || !rect.height || baseScale <= 0 || !Number.isFinite(clientX) || !Number.isFinite(clientY)) {
      scaleRef.current = nextScale;
      setScale(nextScale);
      if (nextScale <= 1) {
        panRef.current = { x: 0, y: 0 };
        setPan({ x: 0, y: 0 });
      }
      return;
    }
    let nextPan = basePan;
    if (nextScale <= 1) {
      nextPan = { x: 0, y: 0 };
    } else {
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const ratio = nextScale / baseScale;
      nextPan = clampPan(
        basePan.x + (clientX - centerX - basePan.x) * (1 - ratio),
        basePan.y + (clientY - centerY - basePan.y) * (1 - ratio),
        nextScale,
      );
    }
    scaleRef.current = nextScale;
    panRef.current = nextPan;
    setScale(nextScale);
    setPan(nextPan);
  }, [clampPan]);

  React.useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, prev, next]);

  React.useEffect(() => {
    if (isVideo) return undefined;
    const root = contentRef.current;
    if (!root) return undefined;
    const onTouchMove = (e) => {
      if (!root.contains(e.target)) return;
      if (e.touches.length < 2 && scaleRef.current <= 1) return;
      if (e.cancelable) e.preventDefault();
    };
    document.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
    return () => {
      document.removeEventListener('touchmove', onTouchMove, { capture: true });
    };
  }, [isVideo]);

  React.useEffect(() => {
    if (isVideo) return undefined;
    const root = contentRef.current;
    if (!root) return undefined;
    const onWheelDoc = (e) => {
      const inLightbox = root.contains(e.target)
        || e.target?.closest?.(`.${s.lightbox}`);
      if (!inLightbox) return;
      if (e.cancelable) e.preventDefault();
    };
    document.addEventListener('wheel', onWheelDoc, { passive: false, capture: true });
    return () => {
      document.removeEventListener('wheel', onWheelDoc, { capture: true });
    };
  }, [isVideo]);

  if (!open || !current) return null;

  return (
    <div
      className={s.lightbox}
      onClick={() => {
        if (ignoreCloseClickRef.current) {
          ignoreCloseClickRef.current = false;
          return;
        }
        onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <button className={s.lbClose} type="button" onClick={onClose} aria-label="Закрыть">✕</button>

      {canGoPrev && (
        <button className={`${s.lbNav} ${s.lbNavLeft}`} type="button" onClick={e => { e.stopPropagation(); prev(); }} aria-label="Предыдущее">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
      )}

      <div
        className={s.lbContent}
        style={!isVideo ? { touchAction: 'none' } : undefined}
        onClick={e => e.stopPropagation()}
        onWheel={(e) => {
          if (isVideo) return;
          e.preventDefault();
          const factor = e.deltaY < 0 ? 1.2 : 0.85;
          applyScaleAtPoint((scaleRef.current || 1) * factor, e.clientX, e.clientY);
        }}
        onPointerDown={(e) => {
          if (scaleRef.current > 1) return;
          if (e.pointerType !== 'touch') return;
          swipeRef.current.active = true;
          swipeRef.current.pointerId = e.pointerId;
          swipeRef.current.startX = e.clientX;
          swipeRef.current.startY = e.clientY;
          swipeRef.current.dx = 0;
          swipeRef.current.dy = 0;
          ignoreCloseClickRef.current = false;
          try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { }
        }}
        onPointerMove={(e) => {
          if (scaleRef.current > 1) return;
          if (!swipeRef.current.active) return;
          if (swipeRef.current.pointerId !== e.pointerId) return;
          swipeRef.current.dx = e.clientX - swipeRef.current.startX;
          swipeRef.current.dy = e.clientY - swipeRef.current.startY;
        }}
        onPointerUp={(e) => {
          if (scaleRef.current > 1) {
            swipeRef.current.active = false;
            swipeRef.current.pointerId = null;
            return;
          }
          if (!swipeRef.current.active) return;
          if (swipeRef.current.pointerId !== e.pointerId) return;

          swipeRef.current.active = false;
          const dx = swipeRef.current.dx;
          const dy = swipeRef.current.dy;

          const absX = Math.abs(dx);
          const absY = Math.abs(dy);
          const SWIPE_THRESHOLD = 50;

          if (absX > SWIPE_THRESHOLD && absX > absY * 1.2) {
            ignoreCloseClickRef.current = true;
            if (dx < 0) next();
            else prev();
          }
        }}
        onPointerCancel={() => {
          swipeRef.current.active = false;
          swipeRef.current.pointerId = null;
        }}
        ref={contentRef}
      >
        {isVideo ? (
          <video key={url} src={url} className={s.lbMedia} controls autoPlay />
        ) : (
          <img
            ref={mediaRef}
            key={url}
            src={url}
            className={s.lbMedia}
            alt=""
            style={{
              transform: scale > 1 || pan.x || pan.y
                ? (isIOS
                  ? `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${scale})`
                  : `translate(${pan.x}px, ${pan.y}px) scale(${scale})`)
                : (isIOS ? 'translate3d(0, 0, 0)' : 'none'),
              transformOrigin: 'center center',
              transition: (isIOS || touchPanRef.current.active || pinchRef.current.lastDistance) ? 'none' : 'transform .14s ease',
              touchAction: scale > 1 ? 'none' : 'pan-y',
              willChange: isIOS ? 'transform' : undefined,
              backfaceVisibility: isIOS ? 'hidden' : undefined,
              WebkitBackfaceVisibility: isIOS ? 'hidden' : undefined,
            }}
            onTouchStart={(e) => {
              if (e.touches.length > 0) {
                ignoreCloseClickRef.current = false;
              }
              if (e.touches.length === 1) {
                const t = e.touches[0];
                swipeRef.current.active = true;
                swipeRef.current.startX = t.clientX;
                swipeRef.current.startY = t.clientY;
                swipeRef.current.dx = 0;
                swipeRef.current.dy = 0;
                return;
              }
              if (e.touches.length !== 2) {
                pinchRef.current.lastDistance = 0;
                swipeRef.current.active = false;
                return;
              }
              swipeRef.current.active = false;
              const [a, b] = e.touches;
              pinchRef.current.lastDistance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) || 0;
            }}
            onTouchMove={(e) => {
              if (e.touches.length === 1 && scaleRef.current <= 1 && items.length > 1) {
                const t = e.touches[0];
                const dx = t.clientX - swipeRef.current.startX;
                const dy = t.clientY - swipeRef.current.startY;
                swipeRef.current.dx = dx;
                swipeRef.current.dy = dy;
                return;
              }
              if (e.touches.length === 1 && scaleRef.current > 1) {
                const t = e.touches[0];
                const panState = touchPanRef.current;
                if (!panState.active) {
                  panState.active = true;
                  panState.startX = t.clientX;
                  panState.startY = t.clientY;
                  panState.startPanX = panRef.current.x;
                  panState.startPanY = panRef.current.y;
                }
                const dx = t.clientX - panState.startX;
                const dy = t.clientY - panState.startY;
                const clamped = clampPan(panState.startPanX + dx, panState.startPanY + dy, scaleRef.current);
                panRef.current = clamped;
                setPan(clamped);
                ignoreCloseClickRef.current = true;
                if (e.cancelable) e.preventDefault();
                e.stopPropagation();
                return;
              }
              if (e.touches.length !== 2) return;
              const [a, b] = e.touches;
              const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
              const last = pinchRef.current.lastDistance || 0;
              if (!last || !dist) {
                pinchRef.current.lastDistance = dist || 0;
                return;
              }
              const rawFactor = dist / last;
              const factor = 1 + ((rawFactor - 1) * LB_PINCH_GAIN);
              const focalX = (a.clientX + b.clientX) / 2;
              const focalY = (a.clientY + b.clientY) / 2;
              pinchRef.current.lastDistance = dist;
              if (e.cancelable) e.preventDefault();
              e.stopPropagation();
              applyScaleAtPoint((scaleRef.current || 1) * factor, focalX, focalY);
            }}
            onTouchEnd={(e) => {
              if (e.changedTouches.length && scaleRef.current <= 1 && items.length > 1 && swipeRef.current.active) {
                const t = e.changedTouches[0];
                const dx = t.clientX - swipeRef.current.startX;
                const dy = t.clientY - swipeRef.current.startY;
                const absX = Math.abs(dx);
                const absY = Math.abs(dy);
                if (absX >= 40 && absX > absY * 1.3) {
                  ignoreCloseClickRef.current = true;
                  if (dx < 0) next();
                  else prev();
                }
              }
              if (e.touches.length === 0) {
                swipeRef.current.active = false;
                touchPanRef.current.active = false;
              }
              if (e.touches.length < 2) {
                pinchRef.current.lastDistance = 0;
              }
            }}
            onClick={(e) => {
              if (scaleRef.current > 1 || items.length <= 1) return;
              const rect = e.currentTarget.getBoundingClientRect();
              if (!rect.width) return;
              const x = e.clientX - rect.left;
              const ratio = x / rect.width;
              if (ratio <= 0.33) {
                e.stopPropagation();
                prev();
                return;
              }
              if (ratio >= 0.67) {
                e.stopPropagation();
                next();
              }
            }}
            onDoubleClick={(e) => {
              if (isVideo) return;
              const nextScale = scaleRef.current > 1 ? 1 : 2;
              applyScaleAtPoint(nextScale, e.clientX, e.clientY);
            }}
            onPointerDown={(e) => {
              if (isVideo) return;
              if (e.pointerType === 'touch') return;
              if (scaleRef.current <= 1) return;
              const st = mousePanRef.current;
              st.active = true;
              st.pointerId = e.pointerId;
              st.startX = e.clientX;
              st.startY = e.clientY;
              st.startPanX = panRef.current.x;
              st.startPanY = panRef.current.y;
              try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { }
              e.preventDefault();
              e.stopPropagation();
            }}
            onPointerMove={(e) => {
              const st = mousePanRef.current;
              if (!st.active || st.pointerId !== e.pointerId) return;
              const dx = e.clientX - st.startX;
              const dy = e.clientY - st.startY;
              const clamped = clampPan(st.startPanX + dx, st.startPanY + dy, scaleRef.current);
              panRef.current = clamped;
              setPan(clamped);
              ignoreCloseClickRef.current = true;
              e.preventDefault();
              e.stopPropagation();
            }}
            onPointerUp={(e) => {
              const st = mousePanRef.current;
              if (st.pointerId !== e.pointerId) return;
              st.active = false;
              st.pointerId = null;
              try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { }
            }}
            onPointerCancel={() => {
              mousePanRef.current.active = false;
              mousePanRef.current.pointerId = null;
            }}
          />
        )}
        <div className={s.lbInfoCaption}>
          <div className={s.lbCaption}>
            {current.sender_name && <span className={s.lbSender}>{current.sender_name}</span>}
            <span className={s.lbDate}>{formatDate(current.timestamp)}</span>

          </div>
          {current.original_filename && (
            <button
              type="button"
              className={s.lbDl}
              onClick={(e) => {
                e.stopPropagation();
                openFileUrl(downloadUrl, current.original_filename);
              }}
            >
              Скачать
            </button>
          )}
        </div>
      </div>

      {canGoNext && (
        <button className={`${s.lbNav} ${s.lbNavRight}`} type="button" onClick={e => { e.stopPropagation(); next(); }} aria-label="Следующее">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
        </button>
      )}

      {Array.isArray(thumbs) && thumbs.length > 1 && (
        <div
          className={s.imageViewerThumbs}
          onClick={(e) => e.stopPropagation()}
        >
          {thumbs.map((thumbUrl, thumbIdx) => {
            const itemIndex = items.findIndex((it) => (it?.url || getViewUrl(it?.s3_key)) === thumbUrl);
            const active = itemIndex >= 0 ? itemIndex === index : thumbIdx === index;
            return (
              <button
                key={`${thumbUrl}-${thumbIdx}`}
                type="button"
                className={`${s.imageViewerThumbBtn} ${active ? s.imageViewerThumbBtnActive : ''}`}
                onClick={() => {
                  const nextIdx = itemIndex >= 0 ? itemIndex : thumbIdx;
                  onIndexChange?.(nextIdx);
                }}
                aria-label={`Фото ${thumbIdx + 1}`}
              >
                <img src={thumbUrl} alt="" className={s.imageViewerThumbImg} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GridTab({ chatId, kind, enabled }) {
  const { items, loading, hasMore, loadMore, error } = useMediaPage(chatId, kind, enabled);
  const [lightbox, setLightbox] = React.useState(null);

  const loaderRef = React.useRef(null);

  React.useEffect(() => {
    if (!loaderRef.current || !hasMore) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) loadMore();
    }, { threshold: 0.1 });
    obs.observe(loaderRef.current);
    return () => obs.disconnect();
  }, [hasMore, loadMore]);

  if (error) return <div className={s.stateMsg}>{error}</div>;
  if (!loading && items.length === 0) return <div className={s.stateMsg}>Нет медиафайлов</div>;

  const groups = groupByDate(items);

  return (
    <>
      <div className={s.gridTabContent}>
        {groups.map(g => (
          <div key={g.date} className={s.dateGroup}>
            <div className={s.dateLabel}>{g.date}</div>
            <div className={s.grid}>
              {g.items.map((item, localIdx) => {
                const globalIdx = items.indexOf(item);
                return (
                  <GridThumb
                    key={item.attachment_id}
                    item={item}
                    onClick={() => setLightbox(globalIdx)}
                  />
                );
              })}
            </div>
          </div>
        ))}

        {hasMore && (
          <div ref={loaderRef} className={s.loadMoreTrigger}>
            {loading && <span className={s.spinner} />}
          </div>
        )}
        {loading && items.length === 0 && (
          <div className={s.stateMsg}><span className={s.spinner} /></div>
        )}
      </div>

      {lightbox !== null && (
        typeof document !== 'undefined' ? createPortal(
          <MediaLightbox
            open={lightbox !== null}
            items={items}
            index={lightbox ?? 0}
            onIndexChange={setLightbox}
            onClose={() => setLightbox(null)}
          />,
          document.body
        ) : (
          <MediaLightbox
            open={lightbox !== null}
            items={items}
            index={lightbox ?? 0}
            onIndexChange={setLightbox}
            onClose={() => setLightbox(null)}
          />
        )
      )}
    </>
  );
}

function AudioTab({ chatId, enabled }) {
  const { items, loading, hasMore, loadMore, error } = useMediaPage(chatId, 'audio', enabled);
  const loaderRef = React.useRef(null);
  const playbackOwnerRef = React.useRef(null);

  const handleClaimPlayback = React.useCallback((attachmentId, pauseSelf) => {
    const prev = playbackOwnerRef.current;
    if (prev && prev.id !== attachmentId) {
      prev.pause?.();
    }
    playbackOwnerRef.current = { id: attachmentId, pause: pauseSelf };
  }, []);

  React.useEffect(() => {
    if (!loaderRef.current || !hasMore) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) loadMore(); }, { threshold: 0.1 });
    obs.observe(loaderRef.current);
    return () => obs.disconnect();
  }, [hasMore, loadMore]);

  if (error) return <div className={s.stateMsg}>{error}</div>;
  if (!loading && items.length === 0) return <div className={s.stateMsg}>Нет аудиофайлов</div>;

  const groups = groupByDate(items);

  return (
    <div className={s.listTabContent}>
      {groups.map(g => (
        <div key={g.date} className={s.dateGroup}>
          <div className={s.dateLabel}>{g.date}</div>
          {g.items.map(item => (
            <AudioRow
              key={item.attachment_id}
              item={item}
              onClaimPlayback={handleClaimPlayback}
            />
          ))}
        </div>
      ))}
      {hasMore && (
        <div ref={loaderRef} className={s.loadMoreTrigger}>
          {loading && <span className={s.spinner} />}
        </div>
      )}
      {loading && items.length === 0 && <div className={s.stateMsg}><span className={s.spinner} /></div>}
    </div>
  );
}

function FileTab({ chatId, enabled, onOpenAttachmentPdfPreview }) {
  const { items, loading, hasMore, loadMore, error } = useMediaPage(chatId, 'file', enabled);
  const loaderRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const [fileMenu, setFileMenu] = React.useState(null);

  const openFileRowPreview = React.useCallback(({ item, downloadUrl, name }) => {
    setFileMenu(null);
    const aid = item?.attachment_id;
    if (aid == null || !onOpenAttachmentPdfPreview) return;
    onOpenAttachmentPdfPreview({
      chatId: item?.chat_id ?? chatId,
      attachmentId: aid,
      downloadUrl,
      fileName: name || 'file',
    });
  }, [chatId, onOpenAttachmentPdfPreview]);

  React.useEffect(() => {
    if (!fileMenu) return;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setFileMenu(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [fileMenu]);

  React.useEffect(() => {
    if (!loaderRef.current || !hasMore) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) loadMore(); }, { threshold: 0.1 });
    obs.observe(loaderRef.current);
    return () => obs.disconnect();
  }, [hasMore, loadMore]);

  if (error) return <div className={s.stateMsg}>{error}</div>;
  if (!loading && items.length === 0) return <div className={s.stateMsg}>Нет файлов</div>;

  const groups = groupByDate(items);
  const fileMenuPos = fileMenu && typeof window !== 'undefined'
    ? (() => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const safe = 8;
      const estW = 180;
      const estH = canShareAttachmentOnDevice() ? 170 : 124;
      let left = Number(fileMenu.x) || safe;
      let top = Number(fileMenu.y) || safe;
      if (left + estW > vw - safe) left = vw - safe - estW;
      if (top + estH > vh - safe) top = vh - safe - estH;
      left = Math.max(safe, left);
      top = Math.max(safe, top);
      return { left, top };
    })()
    : { left: 8, top: 8 };

  return (
    <>
      <div className={s.listTabContent}>
        {groups.map(g => (
          <div key={g.date} className={s.dateGroup}>
            <div className={s.dateLabel}>{g.date}</div>
            {g.items.map(item => (
              <FileRow
                key={item.attachment_id}
                item={{ ...item, chat_id: item?.chat_id ?? chatId }}
                onOpenPreview={openFileRowPreview}
                onOpenMenu={setFileMenu}
              />
            ))}
          </div>
        ))}
        {hasMore && (
          <div ref={loaderRef} className={s.loadMoreTrigger}>
            {loading && <span className={s.spinner} />}
          </div>
        )}
        {loading && items.length === 0 && <div className={s.stateMsg}><span className={s.spinner} /></div>}
      </div>

      {fileMenu && typeof document !== 'undefined' && createPortal(
        <div
          ref={menuRef}
          className={s.messageContextMenu}
          style={{ left: fileMenuPos.left, top: fileMenuPos.top, width: 'min(180px, calc(100vw - 16px))', position: 'fixed', zIndex: 12000 }}
          role="menu"
          aria-label="Действия с файлом"
          onClick={(e) => e.stopPropagation()}
        >
          <div className={s.contextActionsList}>
            {canShareAttachmentOnDevice() && (
              <button
                type="button"
                className={s.contextActionItem}
                onPointerDown={() => {
                  prefetchShareableFile({
                    downloadUrl: fileMenu.downloadUrl,
                    fileName: fileMenu.fileName,
                    mime: fileMenu.mime,
                  });
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  runShareAttachmentAction(
                    {
                      downloadUrl: fileMenu.downloadUrl,
                      fileName: fileMenu.fileName,
                      mime: fileMenu.mime,
                    },
                    (err) => {
                      reportShareError(err, {
                        downloadUrl: fileMenu.downloadUrl,
                        fileName: fileMenu.fileName,
                      });
                    },
                  );
                  setFileMenu(null);
                }}
              >
                <ShareActionIcon />
                <span>Поделиться</span>
              </button>
            )}
            <button
              type="button"
              className={s.contextActionItem}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openFileUrl(fileMenu.downloadUrl, fileMenu.fileName);
                setFileMenu(null);
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M21 15V16.2C21 17.8802 21 18.7202 20.673 19.362C20.3854 19.9265 19.9265 20.3854 19.362 20.673C18.7202 21 17.8802 21 16.2 21H7.8C6.11984 21 5.27976 21 4.63803 20.673C4.07354 20.3854 3.6146 19.9265 3.32698 19.362C3 18.7202 3 17.8802 3 16.2V15M17 10L12 15M12 15L7 10M12 15V3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Сохранить</span>
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

function ChatMediaPanel({
  isOpen,
  onClose,
  chat,
  resolveImageUrl,
  getPeerEmployeeId,
  employeeTaskStatsMap,
  onOpenAttachmentPdfPreview,
  suppressEscapeClose = false,
}) {
  const [activeTab, setActiveTab] = React.useState('image');
  const panelRef = React.useRef(null);
  const activeTabIndex = React.useMemo(() => (
    Math.max(0, TABS.findIndex((t) => t.key === activeTab))
  ), [activeTab]);

  React.useEffect(() => {
    setActiveTab('image');
  }, [chat?.id]);

  React.useEffect(() => {
    if (!isOpen || suppressEscapeClose) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, suppressEscapeClose]);

  if (!chat) return null;

  return (
    <>
      <div
        className={`${s.backdrop} ${isOpen ? s.backdropVisible : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          const rect = panelRef.current?.getBoundingClientRect?.();
          if (rect) {
            const x = e.clientX, y = e.clientY;
            if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
              return;
            }
          }
          onClose();
        }}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        className={`${s.panel} ${isOpen ? s.panelOpen : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="complementary"
        aria-label="Медиа чата"
      >
        <div className={s.panelHeader}>
          <button
            type="button"
            className={s.closeBtn}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            aria-label="Закрыть"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>

          <div className={s.panelChatInfo}>
            <ChatCounterpartyAvatar
              chat={chat}
              getPeerEmployeeId={getPeerEmployeeId}
              employeeTaskStatsMap={employeeTaskStatsMap}
              resolveImageUrl={resolveImageUrl}
              size={48}
              favoritesClassName={s.favoritesChatAvatarHeader}
              imgClassName={s.panelAvatar}
            />
            <div className={s.panelChatName}>{getChatDisplayTitle(chat)}</div>
          </div>
        </div>

        <div className={s.tabs} role="tablist">
          {TABS.map(tab => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`${s.tab} ${activeTab === tab.key ? s.tabActive : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
          <div
            className={s.tabsIndicator}
            aria-hidden="true"
            style={{
              width: `${100 / TABS.length}%`,
              transform: `translateX(${activeTabIndex * 100}%)`,
            }}
          />
        </div>

        <div className={s.tabContent}>
          <div key={`${chat.id}-${activeTab}`} className={s.tabPanel}>
            {(activeTab === 'image' || activeTab === 'video') && (
              <GridTab
                chatId={chat.id}
                kind={activeTab}
                enabled={isOpen}
              />
            )}
            {activeTab === 'audio' && (
              <AudioTab chatId={chat.id} enabled={isOpen} />
            )}
            {activeTab === 'file' && (
              <FileTab
                chatId={chat.id}
                enabled={isOpen}
                onOpenAttachmentPdfPreview={onOpenAttachmentPdfPreview}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
